<template>
  <div class="container-fluid pm-page">
    <div class="pm-page-header pb-3">
      <div v-if="view !== 'projects'" class="qa-tc-back">
        <button type="button" class="btn btn-link btn-sm p-0 qa-tc-back-btn" @click="goBack">
          <i class="fas fa-arrow-left me-1"></i>
          {{ backLabel }}
        </button>
      </div>
      <h5 class="mb-0 font-weight-bold">Test Case</h5>
      <p class="text-secondary text-sm mb-0">{{ pageSubtitle }}</p>
    </div>

    <div class="row pb-2 pm-page-main">
      <div class="col-12 pm-page-col">
        <div class="card pm-page-card">
          <div class="card-body px-0 pt-0 pb-2 pm-page-tab-body">
            <div class="qa-tc-inset">
              <div v-if="view !== 'cases'" class="qa-tc-toolbar">
                <div class="qa-tc-search">
                  <label class="form-label text-xs text-secondary mb-1">Search</label>
                  <input
                    v-model="searchText"
                    type="search"
                    class="form-control form-control-sm"
                    :placeholder="searchPlaceholder"
                  />
                </div>
                <div v-if="view === 'projects'" class="qa-tc-filter">
                  <label class="form-label text-xs text-secondary mb-1">Project</label>
                  <base-select
                    size="sm"
                    searchable
                    :model-value="projectFilter"
                    :options="projectFilterOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="All Projects"
                    search-placeholder="Search project"
                    @update:model-value="projectFilter = $event"
                  />
                </div>
                <div v-if="view === 'functions'" class="qa-tc-toolbar-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary mb-0"
                    @click="openCreateFunction"
                  >
                    <i class="fas fa-plus me-1"></i> Add Group
                  </button>
                </div>
                <div v-if="view === 'folders'" class="qa-tc-toolbar-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary mb-0"
                    @click="openCreateFolder"
                  >
                    <i class="fas fa-plus me-1"></i> Add Folder
                  </button>
                </div>
              </div>

              <div v-else class="tc-toolbar-wrap">
                <div class="tc-toolbar-main">
                  <div class="tc-toolbar-primary">
                    <div class="tc-filter-field tc-filter-field--search">
                      <label class="text-xs font-weight-bold text-secondary mb-1 d-block tc-filter-label--hidden">Search</label>
                      <div class="input-group input-group-sm tc-search-input-group">
                        <span class="input-group-text bg-white border-end-0">
                          <i class="fas fa-search text-secondary" style="font-size:0.7rem;"></i>
                        </span>
                        <input
                          v-model="searchText"
                          type="text"
                          class="form-control border-start-0 ps-0"
                          placeholder="Search test case..."
                          style="font-size:0.75rem;"
                        />
                      </div>
                    </div>
                    <div class="tc-filter-field tc-filter-field--status">
                      <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Status</label>
                      <base-select
                        size="sm"
                        :model-value="filters.status"
                        :options="statusFilterSelectOptions"
                        option-label="label"
                        option-value="value"
                        @update:model-value="(v) => { filters.status = v ?? ''; }"
                      />
                    </div>
                    <div class="tc-filter-field tc-filter-field--priority">
                      <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Priority</label>
                      <base-select
                        size="sm"
                        :model-value="filters.priority"
                        :options="priorityFilterSelectOptions"
                        option-label="label"
                        option-value="value"
                        @update:model-value="(v) => { filters.priority = v ?? ''; }"
                      />
                    </div>
                    <div class="tc-filter-field tc-filter-field--type">
                      <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Test Type</label>
                      <base-select
                        size="sm"
                        :model-value="filters.testType"
                        :options="testTypeFilterSelectOptions"
                        option-label="label"
                        option-value="value"
                        @update:model-value="(v) => { filters.testType = v ?? ''; }"
                      />
                    </div>
                    <div class="tc-filter-field tc-filter-field--action">
                      <label class="text-xs font-weight-bold text-secondary mb-1 d-block tc-filter-label--hidden">Filters</label>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary mb-0 tc-filters-toggle"
                        :class="{ 'tc-filters-toggle--active': filtersPanelOpen || advancedFilterCount > 0 }"
                        @click="toggleFiltersPanel"
                      >
                        <i class="fas me-1" :class="filtersPanelOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        Filters
                        <span v-if="advancedFilterCount > 0" class="tc-filter-badge">{{ advancedFilterCount }}</span>
                      </button>
                    </div>
                  </div>
                  <div class="tc-toolbar-actions">
                    <div class="tc-action-field">
                      <span class="tc-action-label tc-filter-label--hidden">&nbsp;</span>
                      <button
                        v-if="!selectingMode"
                        type="button"
                        class="btn btn-sm btn-outline-secondary mb-0"
                        style="font-size:0.8rem;"
                        @click="enterSelectMode"
                      >
                        <i class="fas fa-check-square me-1"></i> Select
                      </button>
                      <button
                        v-else
                        type="button"
                        class="btn btn-sm btn-danger mb-0"
                        style="font-size:0.8rem;"
                        @click="exitSelectMode"
                      >
                        Cancel
                      </button>
                    </div>
                    <div class="tc-action-field">
                      <span class="tc-action-label tc-filter-label--hidden">&nbsp;</span>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary mb-0"
                        style="font-size:0.8rem;"
                        @click="viewPdfReport"
                      >
                        <i class="fas fa-eye me-1"></i> View
                      </button>
                    </div>
                    <div class="tc-action-field">
                      <span class="tc-action-label tc-filter-label--hidden">&nbsp;</span>
                      <div class="tc-action-dropdown-wrap tc-import-dropdown-wrap" ref="uploadWrap">
                      <input
                        ref="fileInput"
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        style="display:none;"
                        @change="handleFileUpload"
                      />
                      <button type="button" class="tc-action-trigger" @click.stop="toggleUploadMenu">
                        <span>Import Test Case</span>
                        <i class="fas" :class="showUploadMenu ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </button>
                      <div v-if="showUploadMenu" class="upload-menu tc-import-menu" @click.stop>
                        <button type="button" class="upload-menu-item" @click="triggerFileUpload">
                          <i class="fas fa-file-import me-2 text-secondary"></i> Import Test Case
                        </button>
                        <div class="upload-menu-divider"></div>
                        <button type="button" class="upload-menu-item" @click="downloadTemplate">
                          <i class="fas fa-file-excel me-2 text-success"></i> Download Template
                        </button>
                      </div>
                    </div>
                    </div>
                    <div class="tc-action-field">
                      <span class="tc-action-label tc-filter-label--hidden">&nbsp;</span>
                      <div class="tc-action-dropdown-wrap" ref="downloadWrap">
                      <button type="button" class="tc-action-trigger" @click.stop="toggleDownloadMenu">
                        <span>Download</span>
                        <i class="fas" :class="showDownloadMenu ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </button>
                      <div v-if="showDownloadMenu" class="upload-menu tc-download-menu" @click.stop>
                        <button type="button" class="upload-menu-item" @click="downloadPdfReport">
                          <i class="fas fa-file-pdf me-2 text-danger"></i> PDF
                        </button>
                        <div class="upload-menu-divider"></div>
                        <button type="button" class="upload-menu-item" @click="downloadExcelReport">
                          <i class="fas fa-file-excel me-2 text-success"></i> Excel
                        </button>
                      </div>
                      </div>
                    </div>
                    <div class="tc-action-field tc-action-field--add">
                      <a href="#" class="qa-tc-copy-link tc-action-label" @click.prevent="openCopyFromToolbar">
                        Copy Test Case
                      </a>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary mb-0"
                        style="font-size:0.8rem;"
                        @click="openCreateModal"
                      >
                        <i class="fas fa-plus me-1"></i> Add Test Case
                      </button>
                    </div>
                  </div>
                </div>
                <div v-show="filtersPanelOpen" class="tc-toolbar-advanced">
                  <div class="tc-filter-field">
                    <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Function</label>
                    <base-select
                      size="sm"
                      :model-value="filters.function"
                      :options="functionFilterSelectOptions"
                      option-label="label"
                      option-value="value"
                      @update:model-value="onFunctionFilterChange"
                    />
                  </div>
                  <div class="tc-filter-field">
                    <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Scenario</label>
                    <base-select
                      size="sm"
                      :model-value="filters.scenario"
                      :options="scenarioFilterSelectOptions"
                      option-label="label"
                      option-value="value"
                      @update:model-value="onScenarioFilterChange"
                    />
                  </div>
                  <div class="tc-filter-field">
                    <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Sub-scenario</label>
                    <base-select
                      size="sm"
                      :model-value="filters.subScenario"
                      :options="subScenarioFilterSelectOptions"
                      option-label="label"
                      option-value="value"
                      @update:model-value="(v) => { filters.subScenario = v ?? ''; }"
                    />
                  </div>
                  <button type="button" class="btn btn-sm btn-outline-secondary mb-0 tc-clear-filters-btn" @click="clearFilters">
                    Clear Filters
                  </button>
                </div>
              </div>

              <div v-if="loading" class="qa-tc-empty">
                <span class="text-secondary text-sm">
                  <i class="fas fa-spinner fa-spin me-2"></i>Loading test cases...
                </span>
              </div>
              <div v-else-if="error" class="alert alert-danger text-sm py-2 mb-0" role="alert">
                {{ error }}
              </div>

              <template v-else>
              <div class="qa-tc-total-bar">
                <div v-if="selectingMode" class="qa-tc-total-actions" @click.stop>
                  <button
                    type="button"
                    class="qa-tc-total-action qa-tc-total-action--edit"
                    :disabled="!selectedIds.length"
                    @mousedown.stop
                    @click.stop="openBulkEdit"
                  >
                    Edit ({{ selectedIds.length }})
                  </button>
                  <button
                    type="button"
                    class="qa-tc-total-action qa-tc-total-action--delete"
                    :disabled="!selectedIds.length"
                    @mousedown.stop
                    @click.stop="showDeleteConfirm = true"
                  >
                    Delete ({{ selectedIds.length }})
                  </button>
                </div>
                <span class="qa-tc-total-count">Total: {{ listTotalCount }}</span>
              </div>

              <div v-if="view === 'projects'" class="pm-tab-table-wrap">
                <div class="pm-table pm-tab-table qa-tc-table">
                  <div class="pm-table-header pm-tab-table-header">
                    <span class="col-project">PROJECT</span>
                    <span class="col-count text-center">TOTAL GROUPS</span>
                  </div>
                  <div class="pm-tab-table-body">
                    <div v-if="!filteredProjects.length" class="pm-empty-table-row">
                      <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                      <p class="text-secondary text-sm mt-2 mb-0">{{ projectsEmptyMessage }}</p>
                    </div>
                    <button
                      v-for="project in filteredProjects"
                      :key="project.projectId"
                      type="button"
                      class="pm-row qa-tc-row"
                      @click="openProject(project)"
                    >
                      <div class="col-project">
                        <p class="qa-tc-name mb-0">{{ project.projectName || "Untitled project" }}</p>
                      </div>
                      <div class="col-count text-center">
                        <span class="pm-cell-secondary">{{ project.functionCount || 0 }}</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <div v-else-if="view === 'functions'" class="pm-tab-table-wrap">
                <div class="pm-table pm-tab-table qa-tc-table">
                  <div class="pm-table-header pm-tab-table-header">
                    <span class="col-project">GROUP</span>
                    <span class="col-count text-center">FOLDERS</span>
                    <span class="col-action text-center">ACTION</span>
                  </div>
                  <div class="pm-tab-table-body">
                    <div v-if="!filteredFunctions.length" class="pm-empty-table-row">
                      <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                      <p class="text-secondary text-sm mt-2 mb-0">{{ functionsEmptyMessage }}</p>
                    </div>
                    <div
                      v-for="item in filteredFunctions"
                      :key="item.functionId"
                      class="pm-row qa-tc-row qa-tc-folder-list-row"
                      @click="openFunction(item)"
                    >
                      <div class="col-project">
                        <p class="qa-tc-name mb-0">{{ item.functionName || "Untitled group" }}</p>
                        <p class="qa-tc-sub mb-0">{{ item.projectName || selectedProjectName }}</p>
                      </div>
                      <div class="col-count text-center">
                        <span class="pm-cell-secondary">{{ item.folderCount }}</span>
                      </div>
                      <div class="col-action text-center" @click.stop>
                        <button
                          type="button"
                          class="btn btn-link btn-sm p-0 mb-0 action-edit"
                          title="Edit"
                          :disabled="isVirtualFunction(item)"
                          @click="startEditFunction(item)"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-link btn-sm p-0 mb-0 action-delete"
                          :disabled="!canDeleteFunction(item)"
                          :title="functionDeleteTitle(item)"
                          @click="deleteFunction(item)"
                        >
                          <i class="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="view === 'folders'" class="pm-tab-table-wrap">
                <div class="pm-table pm-tab-table qa-tc-table">
                  <div class="pm-table-header pm-tab-table-header">
                    <span class="col-project">FOLDER</span>
                    <span class="col-count text-center">TEST CASES</span>
                    <span class="col-status text-center">STATUS</span>
                    <span class="col-action text-center">ACTION</span>
                  </div>
                  <div class="pm-tab-table-body">
                    <div v-if="!filteredFolders.length" class="pm-empty-table-row">
                      <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                      <p class="text-secondary text-sm mt-2 mb-0">{{ foldersEmptyMessage }}</p>
                    </div>
                    <div
                      v-for="item in filteredFolders"
                      :key="item.folderId"
                      class="pm-row qa-tc-row qa-tc-folder-list-row"
                      @click="openFolder(item)"
                    >
                      <div class="col-project">
                        <p class="qa-tc-name mb-0">
                          <i class="fas fa-folder me-2 qa-tc-folder-list-icon"></i>
                          {{ item.folderName || "Untitled folder" }}
                        </p>
                        <p class="qa-tc-sub mb-0">{{ selectedFunctionName }}</p>
                      </div>
                      <div class="col-count text-center">
                        <span class="pm-cell-secondary">{{ item.testCaseCount || 0 }}</span>
                      </div>
                      <div class="col-status text-center">
                        <span class="status-pill status-pill--sm" :class="statusPillClass(item.state)">{{ enumLabel(item.state || "Draft") }}</span>
                      </div>
                      <div class="col-action text-center" @click.stop>
                        <button
                          type="button"
                          class="btn btn-link btn-sm p-0 mb-0 action-edit"
                          title="Edit"
                          @click="startEditFolder(item)"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-link btn-sm p-0 mb-0 action-delete"
                          :disabled="!canDeleteFolder(item)"
                          :title="folderDeleteTitle(item)"
                          @click="deleteFolder(item)"
                        >
                          <i class="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="view === 'cases'" class="pm-tab-table-wrap">
                <div class="pm-table pm-tab-table qa-tc-table qa-tc-tree">
                  <div class="pm-table-header pm-tab-table-header qa-tc-tree-header">
                    <span v-if="selectingMode" class="col-check">
                      <label class="bo-checkbox bo-checkbox--sm" @click.stop>
                        <input class="bo-checkbox-input" type="checkbox" :checked="allVisibleSelected" @change="toggleSelectAll" />
                        <span class="bo-checkbox-box"></span>
                      </label>
                    </span>
                    <span class="col-id">ID</span>
                    <span class="col-name">TEST CASE NAME</span>
                    <span class="col-status text-center">STATE</span>
                    <span class="col-priority text-center">PRIORITY</span>
                  </div>
                  <div class="pm-tab-table-body">
                    <div v-if="!filteredCases.length" class="pm-empty-table-row">
                      <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                      <p class="text-secondary text-sm mt-2 mb-0">No test cases match this filter.</p>
                    </div>
                    <template v-for="fnGroup in groupedCases" :key="fnGroup.function">
                      <div
                        class="qa-tc-folder-row qa-tc-folder-row--function"
                        @click="toggleFunction(fnGroup.function)"
                      >
                        <i
                          class="fas qa-tc-folder-chevron me-1"
                          :class="isFunctionCollapsed(fnGroup.function) ? 'fa-chevron-right' : 'fa-chevron-down'"
                        ></i>
                        <i
                          class="fas me-2 qa-tc-folder-icon qa-tc-folder-icon--function"
                          :class="isFunctionCollapsed(fnGroup.function) ? 'fa-folder' : 'fa-folder-open'"
                        ></i>
                        <span class="qa-tc-folder-label">{{ fnGroup.function }}</span>
                        <span class="qa-tc-folder-count">({{ functionCaseCount(fnGroup) }})</span>
                      </div>
                      <template v-if="!isFunctionCollapsed(fnGroup.function)">
                        <template
                          v-for="scGroup in fnGroup.scenarioGroups"
                          :key="`${fnGroup.function}|${scGroup.scenario}`"
                        >
                          <div
                            class="qa-tc-folder-row qa-tc-folder-row--scenario"
                            @click="toggleScenario(fnGroup.function, scGroup.scenario)"
                          >
                            <i
                              class="fas qa-tc-folder-chevron me-1"
                              :class="
                                isScenarioCollapsed(fnGroup.function, scGroup.scenario)
                                  ? 'fa-chevron-right'
                                  : 'fa-chevron-down'
                              "
                            ></i>
                            <i
                              class="fas me-2 qa-tc-folder-icon qa-tc-folder-icon--scenario"
                              :class="
                                isScenarioCollapsed(fnGroup.function, scGroup.scenario)
                                  ? 'fa-folder'
                                  : 'fa-folder-open'
                              "
                            ></i>
                            <span class="qa-tc-folder-label">{{ scGroup.scenario }}</span>
                            <span class="qa-tc-folder-count">({{ scenarioCaseCount(scGroup) }})</span>
                          </div>
                          <template v-if="!isScenarioCollapsed(fnGroup.function, scGroup.scenario)">
                            <template
                              v-for="subGroup in scGroup.subGroups"
                              :key="`${fnGroup.function}|${scGroup.scenario}|${subGroup.subScenario}`"
                            >
                              <div
                                class="qa-tc-folder-row qa-tc-folder-row--sub"
                                @click="toggleSubScenario(fnGroup.function, scGroup.scenario, subGroup.subScenario)"
                              >
                                <i
                                  class="fas qa-tc-folder-chevron me-1"
                                  :class="
                                    isSubScenarioCollapsed(fnGroup.function, scGroup.scenario, subGroup.subScenario)
                                      ? 'fa-angle-right'
                                      : 'fa-angle-down'
                                  "
                                ></i>
                                <span class="qa-tc-folder-label">{{ subGroup.subScenario }}</span>
                                <span class="qa-tc-folder-count">({{ subGroup.cases.length }})</span>
                              </div>
                              <template
                                v-if="!isSubScenarioCollapsed(fnGroup.function, scGroup.scenario, subGroup.subScenario)"
                              >
                                <div
                                  v-for="tc in subGroup.cases"
                                  :key="tc.testCaseId"
                                  class="qa-tc-case-row qa-tc-case-row--nested"
                                  :class="{ 'qa-tc-case-row--selected': selectedIds.includes(tc.testCaseId) }"
                                  @click="onCaseRowClick(tc)"
                                >
                                  <div v-if="selectingMode" class="col-check" @click.stop>
                                    <label class="bo-checkbox bo-checkbox--sm" @click.stop>
                                      <input
                                        class="bo-checkbox-input"
                                        type="checkbox"
                                        :checked="selectedIds.includes(tc.testCaseId)"
                                        @change="toggleSelect(tc.testCaseId)"
                                      />
                                      <span class="bo-checkbox-box"></span>
                                    </label>
                                  </div>
                                  <div class="col-id">
                                    <span class="qa-tc-id">{{ formatTcLabel(tc) }}</span>
                                    <span
                                      v-if="tc.testType"
                                      class="qa-tc-type-pill"
                                      :class="testTypePillClass(tc.testType)"
                                    >
                                      {{ enumLabel(tc.testType) }}
                                    </span>
                                  </div>
                                  <div class="col-name">
                                    <p class="qa-tc-case-title mb-0">{{ tc.title || "Untitled" }}</p>
                                  </div>
                                  <div class="col-status text-center">
                                    <span class="status-pill" :class="statusPillClass(tc.state)">
                                      {{ enumLabel(tc.state || "Active") }}
                                    </span>
                                  </div>
                                  <div class="col-priority text-center">
                                    <span class="status-pill" :class="priorityPillClass(tc.priority)">
                                      {{ enumLabel(tc.priority) }}
                                    </span>
                                  </div>
                                </div>
                              </template>
                            </template>
                          </template>
                        </template>
                      </template>
                    </template>
                  </div>
                </div>
              </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ===== CREATE / EDIT MODAL ===== -->
  <Teleport to="body">
    <div
      v-if="showCreateModal"
      :key="editMode ? `edit-${editingTcId}` : 'create'"
      class="modal-overlay"
    >
      <div class="modal-box tc-create-modal">
        <!-- Header -->
        <div class="tc-create-modal-header d-flex align-items-center justify-content-between p-3 border-bottom">
          <div>
            <h6 class="mb-0 font-weight-bold" style="font-size:1rem;">
              {{ editMode ? 'Edit Test Case' : formCopyMode ? 'Copy Test Case' : 'Create Test Case' }}
            </h6>
            <p v-if="editMode && editingTc" class="mb-0 text-xs text-secondary">
              Test Case ID: <strong>{{ formatTcLabel(editingTc) }}</strong>
            </p>
          </div>
          <div v-if="editMode" class="d-flex align-items-center gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-danger mb-0"
              style="font-size:0.75rem;"
              :disabled="formSaving || formDeleting || editDeleteChecking"
              @click="openEditDeleteConfirm"
            >
              <i v-if="editDeleteChecking" class="fas fa-spinner fa-spin me-1"></i>Delete
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mb-0"
              style="font-size:0.75rem;"
              :disabled="formSaving || formDeleting"
              @click="cancelCreateOrEdit"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary mb-0"
              style="font-size:0.75rem;"
              :disabled="formSaving || formDeleting"
              @click="submitForm(false)"
            >
              <i v-if="formSaving" class="fas fa-spinner fa-spin me-1"></i>Save
            </button>
          </div>
        </div>

        <div class="tc-create-modal-body">
          <div class="d-flex tc-create-modal-columns">
            <!-- Left / Main content -->
            <div class="tc-create-modal-left">
              <div class="tc-create-modal-left-fixed">
                <div v-if="formCopyMode" class="mb-3">
                  <label class="text-xs font-weight-bold mb-1 d-block">
                    Group <span class="text-danger">*</span>
                  </label>
                  <base-select
                    size="sm"
                    searchable
                    :model-value="copyPicker.functionId || null"
                    :options="copyFunctionSelectOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="— Select group —"
                    :disabled="copyFunctionsLoading || copyStepsLoading"
                    @update:model-value="onCopyFunctionSelect"
                  />
                  <label class="text-xs font-weight-bold mb-1 d-block mt-2">
                    Folder <span class="text-danger">*</span>
                  </label>
                  <base-select
                    size="sm"
                    searchable
                    :model-value="copyPicker.folderKey || null"
                    :options="copyFolderSelectOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="— Select folder —"
                    :disabled="!copyPicker.functionId || copyFoldersLoading || copyStepsLoading"
                    @update:model-value="onCopyFolderSelect"
                  />
                  <label class="text-xs font-weight-bold mb-1 d-block mt-2">
                    Copy from Test Case <span class="text-danger">*</span>
                  </label>
                  <base-select
                    size="sm"
                    searchable
                    :model-value="copyPicker.testCaseId || null"
                    :options="copyCaseSelectOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="— Select test case —"
                    :disabled="!copyPicker.folderKey || copyCasesLoading || copyStepsLoading"
                    @update:model-value="onCopySourceSelect"
                  />
                  <p v-if="copyFunctionsLoading || copyFoldersLoading || copyCasesLoading || copyStepsLoading" class="text-xs text-secondary mb-0 mt-1">
                    <i class="fas fa-spinner fa-spin me-1"></i>Loading...
                  </p>
                </div>
                <div :class="{ 'tc-copy-locked': copyFieldsLocked }">
                <!-- Function / Scenario / Sub-Scenario -->
                <div class="mb-3">
                  <label class="text-xs font-weight-bold mb-1" :class="{ 'text-danger': !!formFieldErrors.function }">Function <span class="text-danger">*</span></label>
                  <base-select
                    size="sm"
                    searchable
                    allow-create
                    create-label="Create function"
                    v-model="form.functionId"
                    :options="functionOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="Select or type new function"
                    :has-error="!!formFieldErrors.function"
                    @update:model-value="onFormFunctionChange"
                    @create="form.functionIsNew = true"
                  />
                  <p v-if="formFieldErrors.function" class="text-xs text-danger mb-0 mt-1">{{ formFieldErrors.function }}</p>
                  <p v-else-if="form.functionIsNew && form.functionId" class="text-xs text-warning mb-0 mt-1">
                    <i class="fas fa-info-circle me-1"></i>จะสร้าง Function ใหม่เมื่อบันทึก
                  </p>
                </div>
                <div class="row g-2 mb-3">
                  <div class="col-6">
                    <label class="text-xs font-weight-bold mb-1" :class="{ 'text-danger': !!formFieldErrors.scenario }">Scenario <span class="text-danger">*</span></label>
                    <base-select
                      size="sm"
                      searchable
                      allow-create
                      create-label="Create scenario"
                      v-model="form.scenarioId"
                      :options="scenarioOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="Select or type new scenario"
                      :has-error="!!formFieldErrors.scenario"
                      @update:model-value="onFormScenarioChange"
                      @create="form.scenarioIsNew = true"
                    />
                    <p v-if="formFieldErrors.scenario" class="text-xs text-danger mb-0 mt-1">{{ formFieldErrors.scenario }}</p>
                    <p v-else-if="form.scenarioIsNew && form.scenarioId" class="text-xs text-warning mb-0 mt-1">
                      <i class="fas fa-info-circle me-1"></i>จะสร้าง Scenario ใหม่เมื่อบันทึก
                    </p>
                  </div>
                  <div class="col-6">
                    <label class="text-xs font-weight-bold mb-1" :class="{ 'text-danger': !!formFieldErrors.subScenario }">Sub-Scenario <span class="text-danger">*</span></label>
                    <base-select
                      size="sm"
                      searchable
                      allow-create
                      create-label="Create sub-scenario"
                      v-model="form.subScenarioId"
                      :options="subScenarioOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="Select or type new sub-scenario"
                      :has-error="!!formFieldErrors.subScenario"
                      @update:model-value="onFormSubScenarioChange"
                      @create="form.subScenarioIsNew = true"
                    />
                    <p v-if="formFieldErrors.subScenario" class="text-xs text-danger mb-0 mt-1">{{ formFieldErrors.subScenario }}</p>
                    <p v-else-if="form.subScenarioIsNew && form.subScenarioId" class="text-xs text-warning mb-0 mt-1">
                      <i class="fas fa-info-circle me-1"></i>จะสร้าง Sub-Scenario ใหม่เมื่อบันทึก
                    </p>
                  </div>
                </div>

                <!-- Title -->
                <div class="mb-0">
                  <label class="text-xs font-weight-bold mb-1" :class="{ 'text-danger': !!formFieldErrors.title }">
                    Title <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model="form.title"
                    type="text"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': !!formFieldErrors.title }"
                    placeholder="Enter test case name"
                    style="font-size:0.8rem;"
                    @input="onTitleInput"
                  />
                  <p v-if="formFieldErrors.title" class="text-xs text-danger mb-0 mt-1">{{ formFieldErrors.title }}</p>
                </div>
                </div>
              </div>

              <div class="tc-create-modal-left-scroll" :class="{ 'tc-copy-locked': copyFieldsLocked }">
                <!-- Description toggle -->
                <div class="mb-3">
                  <button
                    v-if="!form.showDescription"
                    type="button"
                    class="btn btn-link p-0 text-xs text-primary"
                    style="font-size:0.75rem;"
                    @click="form.showDescription = true"
                  >
                    <i class="fas fa-plus-circle me-1"></i> Add Description
                  </button>
                  <div v-else>
                    <label class="text-xs font-weight-bold mb-1">Description</label>
                    <textarea
                      v-model="form.description"
                      class="form-control form-control-sm"
                      rows="2"
                      style="font-size:0.8rem;"
                      placeholder="Enter description..."
                    ></textarea>
                  </div>
                </div>

                <!-- Preconditions -->
                <div class="mb-3">
                  <label class="text-xs font-weight-bold mb-1">Preconditions</label>
                  <textarea
                    v-model="form.preconditions"
                    class="form-control form-control-sm"
                    rows="3"
                    style="font-size:0.8rem;"
                    placeholder="Define any preconditions about the test"
                  ></textarea>
                </div>

                <!-- Test Data -->
                <div class="mb-4">
                  <label class="text-xs font-weight-bold mb-1">Test Data</label>
                  <textarea
                    v-model="form.testData"
                    class="form-control form-control-sm"
                    rows="2"
                    style="font-size:0.8rem;"
                    placeholder="e.g. username: admin, password: Test@1234"
                  ></textarea>
                </div>

                <!-- Steps and Results -->
                <div class="mb-4">
                  <div class="d-flex align-items-center justify-content-between mb-3 qa-tc-steps-heading">
                    <h6 class="font-weight-bold mb-0" style="font-size:0.85rem;">Steps and Results</h6>
                    <a
                      href="#"
                      class="qa-tc-copy-link"
                      :class="{ disabled: copyFieldsLocked }"
                      :tabindex="copyFieldsLocked ? -1 : 0"
                      @click.prevent="openCopyStepModal"
                    >Copy Test Step</a>
                  </div>
                  <div class="mb-2 d-flex gap-2 text-xs font-weight-bold text-secondary" style="padding-left:72px;">
                    <span class="tc-step-module-col">Module</span>
                    <span style="flex:1;">Step</span>
                    <span style="flex:1;">Expected Result</span>
                  </div>
                  <div
                    v-for="(step, si) in form.steps"
                    :key="step._id || si"
                    class="d-flex align-items-start gap-2 mb-2 tc-step-row"
                    :class="{ 'is-drag-over': dragOverStepIndex === si, 'is-dragging': dragStepIndex === si }"
                    @dragover.prevent="onStepDragOver($event, si)"
                    @drop.prevent="onStepDrop($event, si)"
                  >
                    <div class="d-flex align-items-center" style="min-width:72px; padding-top:6px; flex-shrink:0;">
                      <span
                        class="text-xs text-secondary font-weight-bold text-nowrap tc-step-handle"
                        draggable="true"
                        title="Drag to reorder"
                        @dragstart="onStepDragStart($event, si)"
                        @dragend="onStepDragEnd"
                      >Step {{ si + 1 }}:</span>
                    </div>
                    <div class="tc-step-module-col">
                      <input
                        v-model="step.platform"
                        type="text"
                        class="form-control form-control-sm"
                        style="font-size:0.75rem;"
                        placeholder="Module"
                      />
                    </div>
                    <BulletTextarea
                      v-model="step.stepDetail"
                      :rows="3"
                      resizable
                      placeholder="Details of the step (- item or 1. item)"
                    />
                    <BulletTextarea
                      v-model="step.expectedResult"
                      :rows="3"
                      resizable
                      placeholder="Expected Result (- item or 1. item)"
                    />
                    <button
                      v-if="form.steps.length > 1"
                      type="button"
                      class="btn btn-link btn-sm p-0 text-danger"
                      style="margin-top:4px;"
                      @click="removeStep(si)"
                    >
                      <i class="fas fa-times" style="font-size:0.7rem;"></i>
                    </button>
                  </div>
                  <div class="d-flex gap-2 mt-2" style="padding-left:72px;">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary mb-0"
                      style="font-size:0.75rem;"
                      @click="addStep"
                    >
                      <i class="fas fa-plus me-1"></i> Add Step
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right sidebar: Test Case Fields -->
            <div class="tc-create-modal-right" :class="{ 'tc-copy-locked': copyFieldsLocked }">
              <div class="mb-3">
                <span class="text-xs font-weight-bold text-uppercase text-secondary">Test Case Fields</span>
              </div>

              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Requirement ID</label>
                <div class="d-flex flex-wrap gap-1 mb-2" v-if="form.requirementIds.length">
                  <span v-for="key in form.requirementIds" :key="key" class="jira-tag">
                    <i class="fab fa-jira me-1"></i>{{ key }}
                    <button type="button" class="jira-tag-remove" @click="removeRequirementId(form, key)">×</button>
                  </span>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary w-100 mb-0 qa-tc-req-add-btn"
                  @click="openRequirementModal"
                >
                  <i class="fas fa-plus me-1"></i> Add Requirement ID
                </button>
              </div>

              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1">Owner</label>
                <base-select
                  v-model="form.ownerId"
                  size="sm"
                  searchable
                  :options="userOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="— Select owner —"
                />
              </div>

              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1">State</label>
                <base-select
                  v-model="form.state"
                  size="sm"
                  :options="stateOptions"
                  option-label="label"
                  option-value="value"
                />
              </div>

              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1">Priority <span class="text-danger">*</span></label>
                <base-select
                  v-model="form.priority"
                  size="sm"
                  :options="priorityOptions"
                  option-label="label"
                  option-value="value"
                />
              </div>

              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1">Category <span class="text-danger">*</span></label>
                <base-select
                  v-model="form.typeOfTestCase"
                  size="sm"
                  :options="categoryOptions"
                  option-label="label"
                  option-value="value"
                />
              </div>

              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1">Automation Status</label>
                <base-select
                  v-model="form.automationStatus"
                  size="sm"
                  :options="automationStatusOptions"
                  option-label="label"
                  option-value="value"
                />
              </div>

              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1">Test Type</label>
                <base-select
                  v-model="form.testType"
                  size="sm"
                  :options="testTypeOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="-- Select --"
                />
              </div>

              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1">Execution Type</label>
                <base-select
                  v-model="form.executionType"
                  size="sm"
                  :options="executionTypeOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="-- Select --"
                />
              </div>

            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="tc-create-modal-footer d-flex align-items-center justify-content-between p-3 border-top">
          <div class="text-xs text-danger">{{ formError }}</div>
          <div v-if="!editMode" class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mb-0"
              style="font-size:0.8rem;"
              :disabled="formSaving"
              @click="cancelCreateOrEdit"
            >Cancel</button>
            <button
              type="button"
              class="btn btn-sm btn-outline-dark mb-0"
              style="font-size:0.8rem;"
              :disabled="formSaving || copyFieldsLocked"
              @click="submitForm(true)"
            >
              <i v-if="formSaving" class="fas fa-spinner fa-spin me-1"></i>Draft
            </button>
            <button
              type="button"
              class="btn btn-sm bg-gradient-primary text-white mb-0"
              style="font-size:0.8rem;"
              :disabled="formSaving || copyFieldsLocked"
              @click="submitForm(false)"
            >
              <i v-if="formSaving" class="fas fa-spinner fa-spin me-1"></i>Create
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showCopyStepModal" class="modal-overlay" style="z-index:3300;">
      <div class="modal-box qa-tc-copy-step-modal" @click.stop>
        <div class="qa-tc-copy-modal-header d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
          <h6 class="mb-0 font-weight-bold">Copy Test Step</h6>
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeCopyStepModal">Close</button>
        </div>
        <div class="qa-tc-copy-step-filters px-4 py-3 border-bottom">
          <div class="row g-2 mb-3">
            <div class="col-md-6">
              <label class="text-xs font-weight-bold mb-1 d-block">Group <span class="text-danger">*</span></label>
              <base-select
                size="sm"
                searchable
                :model-value="copyStepPicker.functionId || null"
                :options="copyStepFunctionSelectOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select group —"
                :disabled="copyStepFunctionsLoading"
                @update:model-value="onCopyStepFunctionChange"
              />
            </div>
            <div class="col-md-6">
              <label class="text-xs font-weight-bold mb-1 d-block">Folder <span class="text-danger">*</span></label>
              <base-select
                size="sm"
                searchable
                :model-value="copyStepPicker.folderKey || null"
                :options="copyStepFolderSelectOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select folder —"
                :disabled="!copyStepPicker.functionId || copyStepFoldersLoading"
                @update:model-value="onCopyStepFolderChange"
              />
            </div>
            <div class="col-md-4">
              <label class="text-xs font-weight-bold mb-1 d-block">Scenario <span class="text-danger">*</span></label>
              <base-select
                size="sm"
                searchable
                :model-value="copyStepPicker.scenarioId || null"
                :options="copyStepScenarioOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select scenario —"
                :disabled="!copyStepPicker.folderKey || copyStepFoldersLoading || copyStepLoading"
                @update:model-value="onCopyStepScenarioChange"
              />
            </div>
            <div class="col-md-4">
              <label class="text-xs font-weight-bold mb-1 d-block">Sub-Scenario <span class="text-danger">*</span></label>
              <base-select
                size="sm"
                searchable
                :model-value="copyStepPicker.subScenarioId || null"
                :options="copyStepSubScenarioOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select sub-scenario —"
                :disabled="!copyStepPicker.scenarioId"
                @update:model-value="onCopyStepSubScenarioChange"
              />
            </div>
            <div class="col-md-4">
              <label class="text-xs font-weight-bold mb-1 d-block">Title <span class="text-danger">*</span></label>
              <base-select
                size="sm"
                searchable
                :model-value="copyStepPicker.testCaseId || null"
                :options="copyStepTitleOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select title —"
                :disabled="!copyStepPicker.scenarioId || !copyStepPicker.subScenarioId"
                @update:model-value="onCopyStepTitleChange"
              />
            </div>
          </div>
          <p v-if="copyStepFunctionsLoading || copyStepFoldersLoading || copyStepLoading" class="text-xs text-secondary mb-2">
            <i class="fas fa-spinner fa-spin me-1"></i>Loading...
          </p>
          <div class="d-flex justify-content-end mb-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mb-0"
              style="font-size:0.75rem;"
              :disabled="!copyStepSourceSteps.length"
              @click="selectAllCopySteps"
            >
              Select all step
            </button>
          </div>
        </div>
        <div class="qa-tc-copy-step-body px-4 py-3">
          <div class="table-responsive qa-tc-copy-step-table-wrap">
            <table class="table align-items-start mb-0">
              <thead>
                <tr>
                  <th style="width:36px;"></th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Step ID</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Module</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Step</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Expected</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!copyStepPicker.testCaseId">
                  <td colspan="5" class="text-center text-secondary text-xs py-4">Select Scenario, Sub-Scenario and Title to view steps.</td>
                </tr>
                <tr v-else-if="copyStepLoading">
                  <td colspan="5" class="text-center text-secondary text-xs py-4">Loading steps...</td>
                </tr>
                <tr v-else-if="!copyStepSourceSteps.length">
                  <td colspan="5" class="text-center text-secondary text-xs py-4">No steps found.</td>
                </tr>
                <tr
                  v-for="(step, idx) in copyStepSourceSteps"
                  :key="idx"
                  class="qa-tc-copy-step-row"
                  :class="{ 'is-checked': copyStepPicker.selectedIndexes.includes(idx) }"
                  @click="toggleCopyStepModalStep(idx)"
                >
                  <td>
                    <input
                      type="checkbox"
                      :checked="copyStepPicker.selectedIndexes.includes(idx)"
                      @click.stop
                      @change="toggleCopyStepModalStep(idx)"
                    />
                  </td>
                  <td class="text-xs font-weight-bold text-nowrap">Step {{ step.stepId }}</td>
                  <td class="text-xs">{{ step.platform || "—" }}</td>
                  <td class="text-xs qa-tc-copy-step-cell">{{ step.stepDetail || "—" }}</td>
                  <td class="text-xs qa-tc-copy-step-cell">{{ step.expectedResult || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="qa-tc-copy-modal-footer d-flex justify-content-end gap-2 px-4 py-3 border-top">
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeCopyStepModal">Cancel</button>
          <button
            type="button"
            class="btn btn-sm bg-gradient-dark text-white mb-0"
            :disabled="!copyStepPicker.selectedIndexes.length"
            @click="applyCopiedStepsFromModal"
          >
            Copy
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showCopyFieldChoiceModal" class="modal-overlay" style="z-index:3300;">
      <div class="modal-box qa-tc-copy-choice-modal" @click.stop>
        <div class="qa-tc-copy-modal-header px-4 py-3 border-bottom">
          <h6 class="mb-0 fw-semibold">Keep original or use new data?</h6>
          <p class="text-sm text-secondary mb-0 mt-2">
            These fields already have data. Choose original or new. Empty fields are filled automatically. Existing steps are kept, and copied steps are added after them.
          </p>
        </div>
        <div class="qa-tc-copy-choice-body px-4 py-3">
          <div
            v-for="item in copyFieldConflicts"
            :key="item.key"
            class="qa-tc-copy-choice-row"
          >
            <p class="text-xs font-weight-bold mb-2">{{ item.label }}</p>
            <div class="qa-tc-copy-compare">
              <label class="qa-tc-copy-compare-card" :class="{ 'is-selected': item.choice === 'original' }">
                <input type="radio" class="mt-1" :name="'copy-choice-' + item.key" value="original" v-model="item.choice" />
                <span>
                  <strong class="d-block mb-1">Original</strong>
                  <span class="qa-tc-copy-compare-value">{{ item.originalLabel || "—" }}</span>
                </span>
              </label>
              <label class="qa-tc-copy-compare-card" :class="{ 'is-selected': item.choice === 'new' }">
                <input type="radio" class="mt-1" :name="'copy-choice-' + item.key" value="new" v-model="item.choice" />
                <span>
                  <strong class="d-block mb-1">New</strong>
                  <span class="qa-tc-copy-compare-value">{{ item.newLabel || "—" }}</span>
                </span>
              </label>
            </div>
          </div>
        </div>
        <div class="qa-tc-copy-modal-footer d-flex justify-content-end gap-2 px-4 py-3 border-top">
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeCopyFieldChoiceModal">Cancel</button>
          <button type="button" class="btn btn-sm bg-gradient-dark text-white mb-0" @click="confirmCopyFieldChoices">Apply</button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showRequirementModal" class="modal-overlay qa-tc-req-overlay" @click.self="closeRequirementModal">
      <div class="modal-box qa-tc-req-modal" @click.stop>
        <div class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
          <h6 class="mb-0 font-weight-bold">Add Requirement ID</h6>
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeRequirementModal">Close</button>
        </div>
        <div class="px-4 py-3 qa-tc-req-modal-body">
          <div class="d-flex flex-wrap gap-1 mb-3" v-if="form.requirementIds.length">
            <span v-for="key in form.requirementIds" :key="key" class="jira-tag">
              <i class="fab fa-jira me-1"></i>{{ key }}
              <button type="button" class="jira-tag-remove" @click="removeRequirementId(form, key)">×</button>
            </span>
          </div>

          <label class="text-xs font-weight-bold mb-1 d-block">Paste Jira link or Requirement ID</label>
          <div class="qa-tc-req-paste">
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="requirementIdInput"
              placeholder="https://jira.../browse/ABC-123 หรือ ABC-123"
              @keydown.enter.prevent="addRequirementIdFromInput(form)"
            />
            <button type="button" class="btn btn-sm bg-gradient-dark text-white mb-0 qa-tc-req-paste-add" @click="addRequirementIdFromInput(form)">
              Add
            </button>
          </div>

          <div class="qa-tc-req-divider text-xs text-secondary my-3">หรือเลือกจาก Jira</div>

          <label class="text-xs font-weight-bold mb-1 d-block">Jira Space</label>
          <base-select
            size="sm"
            searchable
            v-model="jiraProjectKey"
            :options="jiraProjectSelectOptions"
            option-label="label"
            option-value="value"
            placeholder="— Select Space —"
            @update:model-value="onJiraProjectChange"
          />
          <span v-if="jiraLoadingProjects" class="text-xs text-secondary mt-1 d-block">
            <i class="fas fa-spinner fa-spin me-1"></i>Loading spaces...
          </span>

          <label class="text-xs font-weight-bold mb-1 d-block mt-3">Jira Task</label>
          <input
            v-model="jiraSearch"
            type="text"
            class="form-control form-control-sm mb-2"
            :disabled="!jiraProjectKey"
            placeholder="Search task..."
            @input="onJiraSearch"
          />
          <div class="jira-issue-list qa-tc-req-issue-list">
            <div v-if="!jiraProjectKey" class="text-center py-5 text-secondary text-xs">
              เลือก Jira Space เพื่อโหลด Task
            </div>
            <div v-else-if="jiraLoadingIssues" class="text-center py-5 text-secondary text-xs">
              <i class="fas fa-spinner fa-spin me-1"></i>Loading...
            </div>
            <div v-else-if="jiraIssues.length === 0" class="text-center py-5 text-secondary text-xs">No tasks found</div>
            <div
              v-for="i in jiraIssues"
              :key="i.key"
              class="jira-issue-item"
              :class="{ 'jira-issue-selected': form.requirementIds.includes(i.key) }"
              @click="toggleJiraTicket(form, i.key)"
            >
              <i class="fas fa-check-circle me-1 text-success" v-if="form.requirementIds.includes(i.key)"></i>
              <i class="fas fa-circle me-1" style="font-size: 0.5rem; color: transparent;" v-else></i>
              <span class="text-xs font-weight-bold me-1">{{ i.key }}</span>
              <span class="text-xs">{{ i.summary }}</span>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-end px-4 py-3 border-top">
          <button type="button" class="btn btn-sm bg-gradient-dark text-white mb-0" @click="closeRequirementModal">Done</button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ===== DETAIL MODAL ===== -->
  <Teleport to="body">
    <div v-if="showDetailModal" class="modal-overlay" @click.self="closeDetailModal">
      <div class="modal-box tc-detail-modal" @click.stop="showHistoryDropdown = false">
        <!-- Header (sticky) -->
        <div class="d-flex align-items-center justify-content-between p-3 border-bottom" style="flex-shrink:0; position:sticky; top:0; background:#fff; z-index:10;">
          <div class="d-flex align-items-center gap-2 text-xs text-secondary">
            <span class="font-weight-bold">{{ detailTestCase ? formatTcLabel(detailTestCase) : '' }}</span>
            <i class="fas fa-info-circle"></i>
            <span>|</span>
            <div
              class="position-relative d-flex align-items-center"
              :style="{ cursor: history.length ? 'pointer' : 'default' }"
              @click.stop="toggleHistoryDropdown"
            >
              <i class="fas fa-history me-1"></i>
              <span>{{ detailVersionCount }} versions</span>
              <div
                v-if="showHistoryDropdown && history.length"
                class="shadow-sm border rounded bg-white position-absolute start-0 mt-1"
                style="min-width:260px; z-index:20; max-height:240px; overflow-y:auto; top:100%;"
              >
                <button
                  type="button"
                  class="dropdown-item text-xs w-100 text-start border-0 bg-transparent px-3 py-2"
                  :class="{ 'bg-light': viewingHistoryIndex === null }"
                  @click.stop="viewCurrentVersion"
                >
                  Current (latest)
                </button>
                <button
                  v-for="(entry, hi) in history"
                  :key="entry.historyId || hi"
                  type="button"
                  class="dropdown-item text-xs w-100 text-start border-0 bg-transparent px-3 py-2"
                  :class="{ 'bg-light': viewingHistoryIndex === hi }"
                  @click.stop="selectHistoryVersion(hi)"
                >
                  <div class="font-weight-bold">Version {{ entry.versionNo }}</div>
                  <div class="text-secondary">{{ formatHistoryDate(entry.createdAt) }}</div>
                  <div v-if="entry.changedByName" class="text-secondary">{{ entry.changedByName }}</div>
                </button>
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mb-0"
              style="font-size:0.75rem;"
              :disabled="!detailTestCase || isViewingHistory"
              @click="openEditFromDetail"
            >
              <i class="fas fa-pen me-1"></i> Edit
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary mb-0"
              style="font-size:0.75rem;"
              @click="closeDetailModal"
            >
              Close
            </button>
          </div>
        </div>

        <div class="tc-detail-modal-body">
          <div v-if="detailLoading" class="text-center text-secondary text-sm py-5">
            <i class="fas fa-spinner fa-spin me-2"></i>Loading...
          </div>
          <div v-else-if="detailError" class="alert alert-danger text-sm mb-0">{{ detailError }}</div>
          <template v-else-if="detailTestCase">
            <div
              v-if="isViewingHistory && history[viewingHistoryIndex]"
              class="alert alert-warning py-2 px-3 text-xs mb-3 d-flex align-items-center justify-content-between"
              style="flex-shrink:0;"
            >
              <span>
                Viewing version {{ history[viewingHistoryIndex].versionNo }}
                <span v-if="history[viewingHistoryIndex].changedByName"> · {{ history[viewingHistoryIndex].changedByName }}</span>
              </span>
              <button type="button" class="btn btn-link btn-sm p-0 text-xs" @click="viewCurrentVersion">View current</button>
            </div>

            <div class="tab-shell mb-3" role="tablist" aria-label="Test case detail panel" style="flex-shrink:0;">
              <button
                type="button"
                class="tab-item"
                :class="{ active: detailTab === 'details' }"
                @click="detailTab = 'details'"
              >
                Details
              </button>
              <button
                type="button"
                class="tab-item"
                :class="{ active: detailTab === 'remark' }"
                @click="openDetailRemarkTab"
              >
                Remark
              </button>
              <button
                type="button"
                class="tab-item"
                :class="{ active: detailTab === 'history' }"
                @click="openDetailHistoryTab"
              >
                History
              </button>
            </div>

            <div class="tc-detail-modal-panel">
              <template v-if="detailTab === 'remark'">
                <div class="tc-detail-remark">
                  <div class="tc-detail-remark-composer">
                    <MentionTextarea
                      v-model="remarkDraft"
                      :rows="3"
                      placeholder="พิมพ์ Remark... (@ เพื่อแท็กคน)"
                    />
                    <div class="d-flex justify-content-end mt-2">
                      <button
                        type="button"
                        class="btn btn-sm btn-primary mb-0"
                        :disabled="remarkSaving || !remarkDraft.trim()"
                        @click="saveDetailRemark"
                      >
                        <span v-if="remarkSaving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
                        <span v-else>Save Remark</span>
                      </button>
                    </div>
                  </div>
                  <div class="tc-detail-history-scroll">
                    <div v-if="remarkLoading" class="text-secondary text-xs py-2">Loading...</div>
                    <div v-else-if="!remarkEntries.length" class="text-secondary text-xs py-2">
                      No remarks yet.
                    </div>
                    <div v-else class="activity-history-list">
                      <div
                        v-for="entry in remarkEntries"
                        :key="entry.activityId"
                        class="activity-history-item"
                      >
                        <div class="d-flex justify-content-between gap-2 mb-1">
                          <span class="activity-badge" :class="activityBadgeClass(entry)">
                            {{ remarkTabBadgeLabel(entry) }}
                          </span>
                          <span class="text-secondary text-xs text-nowrap">{{ formatActivityTime(entry.createdAt) }}</span>
                        </div>
                        <p class="text-xs text-secondary mb-2">{{ entry.authorName || "System" }}</p>
                        <p class="text-sm mb-0 activity-text"><MentionText :text="remarkTabBody(entry)" /></p>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else-if="detailTab === 'history'">
                <div class="tc-detail-history-scroll">
                  <div v-if="activityLoading" class="text-secondary text-xs py-2">Loading...</div>
                  <div v-else-if="activityHistory.length === 0" class="text-secondary text-xs py-2">
                    No history yet.
                  </div>
                  <div v-else class="activity-history-list">
                    <div
                      v-for="entry in activityHistory"
                      :key="entry.activityId"
                      class="activity-history-item"
                    >
                      <div class="d-flex justify-content-between gap-2 mb-1">
                        <span class="activity-badge" :class="activityBadgeClass(entry)">
                          {{ activityBadgeLabel(entry) }}
                        </span>
                        <span class="text-secondary text-xs text-nowrap">{{ formatActivityTime(entry.createdAt) }}</span>
                      </div>
                      <p class="text-xs text-secondary mb-1">{{ entry.authorName }}</p>

                      <div v-if="entry.fieldName === 'Steps'" class="activity-subtasks-history">
                        <p class="text-sm mb-2"><strong>{{ entry.fieldName }}</strong></p>
                        <div
                          v-for="(ch, ci) in diffStepHistoryChanges(entry.oldValue, entry.newValue)"
                          :key="'step-ch-' + ci"
                          class="activity-step-change"
                        >
                          <div class="activity-step-diff-grid">
                            <div v-if="ch.before" class="activity-step-pane activity-step-pane--before">
                              <span class="activity-step-pane-label">Before</span>
                              <p class="text-xs mb-1 activity-text"><strong>Step:</strong> {{ ch.stepNo }}</p>
                              <p class="text-xs mb-1 activity-text"><strong>Step Detail:</strong> {{ ch.before.action || '—' }}</p>
                              <p class="text-xs mb-0 activity-text"><strong>Expected Result:</strong> {{ ch.before.expected || '—' }}</p>
                            </div>
                            <div v-if="ch.after" class="activity-step-pane activity-step-pane--after">
                              <span class="activity-step-pane-label">After</span>
                              <p class="text-xs mb-1 activity-text"><strong>Step:</strong> {{ ch.stepNo }}</p>
                              <p class="text-xs mb-1 activity-text"><strong>Step Detail:</strong> {{ ch.after.action || '—' }}</p>
                              <p class="text-xs mb-0 activity-text"><strong>Expected Result:</strong> {{ ch.after.expected || '—' }}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-else class="activity-subtasks-history">
                        <p class="text-sm mb-2"><strong>{{ entry.fieldName }}</strong></p>
                        <div class="activity-step-diff-grid">
                          <div class="activity-step-pane activity-step-pane--before">
                            <span class="activity-step-pane-label">Before</span>
                            <p class="text-xs mb-0 activity-text">{{ activityDisplayValue(entry.oldValue) }}</p>
                          </div>
                          <div class="activity-step-pane activity-step-pane--after">
                            <span class="activity-step-pane-label">After</span>
                            <p class="text-xs mb-0 activity-text">{{ activityDisplayValue(entry.newValue) }}</p>
                            <p v-if="entry.remarkText && entry.entryType !== 'sync'" class="text-xs mb-0 mt-2 activity-text">
                              <MentionText :text="entry.remarkText" />
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else-if="detailTab === 'details'">
                <div class="d-flex tc-create-modal-columns">
                  <div class="tc-create-modal-left">
                    <div class="tc-detail-modal-left-fixed">
                      <div class="tc-detail-title">
                        <h6 class="font-weight-bold mb-0" style="font-size:0.95rem; line-height:1.4;">{{ detailDisplayTc.title }}</h6>
                      </div>
                      <div class="mb-0 tc-detail-hierarchy text-xs">
                        <div class="mb-1">
                          <span class="tc-detail-field-label">Function:</span>
                          {{ detailDisplayTc.function || '—' }}
                        </div>
                        <div class="mb-1">
                          <span class="tc-detail-field-label">Scenario:</span>
                          {{ detailDisplayTc.scenario || '—' }}
                        </div>
                        <div>
                          <span class="tc-detail-field-label">Sub-Scenario:</span>
                          {{ detailDisplayTc.subScenario || '—' }}
                        </div>
                      </div>
                    </div>

                    <div class="tc-create-modal-left-scroll">
                      <div v-if="detailDisplayTc.description" class="mb-3">
                        <p class="text-xs font-weight-bold tc-detail-field-label mb-1">Description</p>
                        <div class="text-xs tc-detail-field-value">
                          <BulletText :text="detailDisplayTc.description" list-class="mb-0" fallback="--" />
                        </div>
                      </div>
                      <div class="mb-3">
                        <p class="text-xs font-weight-bold tc-detail-field-label mb-1">Preconditions</p>
                        <div v-if="detailDisplayTc.preconditions" class="text-xs tc-detail-field-value">
                          <BulletText :text="detailDisplayTc.preconditions" list-class="mb-0" fallback="--" />
                        </div>
                        <p v-else class="text-xs tc-detail-field-value mb-0">--</p>
                      </div>
                      <div class="mb-3">
                        <p class="text-xs font-weight-bold tc-detail-field-label mb-1">Test Data</p>
                        <div v-if="detailDisplayTc.testData" class="text-xs tc-detail-field-value">
                          <BulletText :text="detailDisplayTc.testData" list-class="mb-0" fallback="--" />
                        </div>
                        <p v-else class="text-xs tc-detail-field-value mb-0">--</p>
                      </div>
                      <div class="mb-4 tc-detail-steps">
                        <h6 class="tc-detail-section-title mb-3">Steps and Results</h6>
                        <div class="tc-detail-steps-header text-xs">
                          <span></span>
                          <span>Module</span>
                          <span>Step</span>
                          <span>Expected Result</span>
                        </div>
                        <div
                          v-for="(step, si) in detailStepsForDisplay"
                          :key="si"
                          class="tc-detail-steps-row"
                        >
                          <span class="text-xs text-nowrap tc-detail-steps-label">Step {{ si + 1 }}:</span>
                          <div class="tc-detail-step-cell">
                            <div class="form-control form-control-sm tc-detail-step-box">{{ step.platform || '—' }}</div>
                          </div>
                          <div class="tc-detail-step-cell">
                            <div class="form-control form-control-sm tc-detail-step-box">
                              <BulletText :text="step.action || step.stepDetail" list-class="mb-0" fallback="—" />
                            </div>
                          </div>
                          <div class="tc-detail-step-cell">
                            <div class="form-control form-control-sm tc-detail-step-box">
                              <BulletText :text="step.expected || step.expectedResult" list-class="mb-0" fallback="—" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="tc-create-modal-right">
                    <div class="mb-3">
                      <span class="text-xs tc-detail-sidebar-title text-uppercase">Test Case Fields</span>
                    </div>
                    <div class="mb-3">
                      <p class="text-xs tc-detail-field-label mb-1">Requirement ID</p>
                      <p class="text-xs tc-detail-field-value mb-0">{{ detailDisplayTc.requirementId || '--' }}</p>
                    </div>
                    <div class="mb-3">
                      <p class="text-xs tc-detail-field-label mb-1">Owner</p>
                      <p class="text-xs tc-detail-field-value mb-0">{{ detailDisplayTc.ownerFullName || '--' }}</p>
                    </div>
                    <div class="mb-3">
                      <p class="text-xs tc-detail-field-label mb-1">State</p>
                      <p class="text-xs tc-detail-field-value mb-0">
                        <i class="fas fa-pencil-alt me-1 text-warning" style="font-size:0.65rem;"></i>
                        <span>{{ detailDisplayTc.state || 'Active' }}</span>
                      </p>
                    </div>
                    <div class="mb-3">
                      <p class="text-xs tc-detail-field-label mb-1">Priority</p>
                      <p class="text-xs tc-detail-field-value mb-0">
                        <i class="fas fa-arrow-down me-1 text-success" style="font-size:0.65rem;"></i>
                        <span>{{ enumLabel(detailDisplayTc.priority) }}</span>
                      </p>
                    </div>
                    <div class="mb-3">
                      <p class="text-xs tc-detail-field-label mb-1">Category</p>
                      <p class="text-xs tc-detail-field-value mb-0">{{ enumLabel(detailDisplayTc.typeOfTestCase) }}</p>
                    </div>
                    <div class="mb-3">
                      <p class="text-xs tc-detail-field-label mb-1">Automation Status</p>
                      <p class="text-xs tc-detail-field-value mb-0">{{ enumLabel(detailDisplayTc.automationStatus) }}</p>
                    </div>
                    <div class="mb-3">
                      <p class="text-xs tc-detail-field-label mb-1">Test Type</p>
                      <p class="text-xs tc-detail-field-value mb-0">{{ enumLabel(detailDisplayTc.testType) }}</p>
                    </div>
                    <div class="mb-3">
                      <p class="text-xs tc-detail-field-label mb-1">Execution Type</p>
                      <p class="text-xs tc-detail-field-value mb-0">{{ enumLabel(detailDisplayTc.executionType) }}</p>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>
        <div class="tc-detail-modal-footer border-top px-3 py-2">
          <p class="text-xs text-secondary mb-0">{{ detailCreatedByLine }}</p>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="importProgress.show" class="modal-overlay">
      <div class="modal-box" style="width:420px; max-width:92vw; padding:2rem;">
        <div class="d-flex align-items-center gap-3 mb-4">
          <div v-if="!importProgress.done" class="spinner-border spinner-border-sm text-primary flex-shrink-0" role="status"></div>
          <span v-else-if="importProgress.cancelled" style="font-size:1.2rem;">⛔</span>
          <span v-else style="font-size:1.2rem;">✅</span>
          <h6 class="mb-0 fw-semibold">
            <template v-if="!importProgress.done">Importing Test Cases...</template>
            <template v-else-if="importProgress.cancelled">Import Cancelled</template>
            <template v-else>Import Complete</template>
          </h6>
        </div>
        <div style="height:6px; border-radius:3px; background:#e9ecef; overflow:hidden; margin-bottom:0.75rem;">
          <div
            :style="{
              height: '100%',
              borderRadius: '3px',
              transition: 'width 0.2s ease',
              background: importProgress.cancelled ? '#ffc107' : '#cb0c9f',
              width: importProgress.total
                ? (importProgress.done && !importProgress.cancelled ? 100 : importProgress.current / importProgress.total * 100) + '%'
                : '0%',
            }"
          ></div>
        </div>
        <p class="text-xs mb-4" :class="importProgress.cancelled ? 'text-warning' : 'text-secondary'" style="min-height:1.2em; word-break:break-all;">
          {{ importProgress.currentTitle || "..." }}
        </p>
        <div class="d-flex gap-3 mb-4">
          <div class="text-center flex-fill p-2 rounded" style="background:#f8f9fa;">
            <div class="fw-bold" style="font-size:1.3rem;">{{ importProgress.total }}</div>
            <div class="text-xs text-secondary">Total</div>
          </div>
          <div class="text-center flex-fill p-2 rounded" style="background:#f8f9fa;">
            <div class="fw-bold" style="font-size:1.3rem;">{{ importProgress.current }}</div>
            <div class="text-xs text-secondary">Processing</div>
          </div>
          <div class="text-center flex-fill p-2 rounded" style="background:#d4edda;">
            <div class="fw-bold text-success" style="font-size:1.3rem;">{{ importProgress.success }}</div>
            <div class="text-xs text-secondary">Success</div>
          </div>
          <div class="text-center flex-fill p-2 rounded" style="background:#f8d7da;">
            <div class="fw-bold text-danger" style="font-size:1.3rem;">{{ importProgress.failed }}</div>
            <div class="text-xs text-secondary">Failed</div>
          </div>
        </div>
        <div class="d-flex justify-content-end">
          <button v-if="!importProgress.done" class="btn btn-sm btn-outline-danger mb-0" @click="importProgress.cancelled = true">
            Cancel
          </button>
          <button v-else class="btn btn-sm btn-primary mb-0" @click="importProgress.show = false">
            Done
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showFolderModal" class="modal-overlay" @click.self="closeFolderModal">
      <div
        class="modal-box"
        :class="{ 'folder-modal-box--edit': editingFolderId }"
        :style="editingFolderId ? { width: '880px', maxWidth: '96vw' } : { width: '440px', maxWidth: '96vw' }"
      >
        <div class="d-flex align-items-center p-3 border-bottom">
          <h6 class="mb-0 font-weight-bold">{{ editingFolderId ? "Edit Folder" : "Add Folder" }}</h6>
        </div>
        <div v-if="editingFolderId" class="folder-modal-tabs">
          <div class="folder-tab-shell" role="tablist" aria-label="Folder tabs">
            <button
              type="button"
              class="folder-tab-item"
              :class="{ active: folderTab === 'details' }"
              role="tab"
              :aria-selected="folderTab === 'details' ? 'true' : 'false'"
              @click="folderTab = 'details'"
            >
              Current
            </button>
            <button
              type="button"
              class="folder-tab-item"
              :class="{ active: folderTab === 'history' }"
              role="tab"
              :aria-selected="folderTab === 'history' ? 'true' : 'false'"
              @click="openFolderHistoryTab"
            >
              History
            </button>
          </div>
        </div>
        <div v-if="editingFolderId" class="folder-modal-body">
          <div v-show="folderTab === 'details'" class="folder-modal-split">
            <div class="folder-modal-pane-form">
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Folder Name <span class="text-danger">*</span></label>
                <input
                  ref="folderNameInput"
                  v-model="folderFormName"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Enter folder name"
                />
              </div>
              <div class="mb-0">
                <label class="text-xs font-weight-bold mb-1 d-block">Status</label>
                <base-select
                  size="sm"
                  :model-value="folderFormState"
                  :options="stateOptions"
                  option-label="label"
                  option-value="value"
                  @update:model-value="(v) => { folderFormState = v || 'Draft'; }"
                />
              </div>
            </div>
            <div class="folder-modal-pane-comment">
              <div class="folder-modal-comment-compose">
                <label class="text-xs font-weight-bold mb-1 d-block">
                  {{ folderFormState === 'FeedBack' ? 'Feedback' : 'Comment' }}
                  <span v-if="folderFormState === 'FeedBack'" class="text-danger">*</span>
                </label>
                <MentionTextarea
                  v-model="folderFormComment"
                  input-class="form-control form-control-sm folder-modal-comment-input"
                  :placeholder="folderFormState === 'FeedBack' ? 'Feedback is required... (@ to tag)' : 'Folder comment... (@ to tag)'"
                />
              </div>
              <div class="folder-modal-comment-history">
                <p class="text-xs font-weight-bold mb-2">Comment History</p>
                <div v-if="folderHistoryLoading" class="text-secondary text-xs py-1">Loading...</div>
                <div v-else-if="!folderCommentHistory.length" class="text-secondary text-xs py-1">No comments yet.</div>
                <div v-else class="activity-history-list">
                  <div
                    v-for="entry in folderCommentHistory"
                    :key="entry.activityId"
                    class="activity-history-item"
                  >
                    <div class="d-flex justify-content-between gap-2 mb-1">
                      <span class="activity-badge" :class="activityBadgeClass(entry)">
                        {{ activityBadgeLabel(entry) }}
                      </span>
                      <span class="text-secondary text-xs text-nowrap">{{ formatActivityTime(entry.createdAt) }}</span>
                    </div>
                    <p class="text-xs text-secondary mb-1">{{ entry.authorName || "System" }}</p>
                    <p class="text-sm mb-0 activity-text"><MentionText :text="folderHistoryBody(entry)" /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-show="folderTab === 'history'" class="folder-modal-history">
            <div v-if="folderHistoryLoading" class="text-secondary text-xs py-2">Loading...</div>
            <div v-else-if="!folderHistory.length" class="folder-modal-history-empty text-secondary text-xs">No history yet.</div>
            <div v-else class="activity-history-list">
              <div
                v-for="entry in folderHistory"
                :key="entry.activityId"
                class="activity-history-item"
              >
                <div class="d-flex justify-content-between gap-2 mb-1">
                  <span class="activity-badge" :class="activityBadgeClass(entry)">
                    {{ activityBadgeLabel(entry) }}
                  </span>
                  <span class="text-secondary text-xs text-nowrap">{{ formatActivityTime(entry.createdAt) }}</span>
                </div>
                <p class="text-xs text-secondary mb-1">{{ entry.authorName || "System" }}</p>
                <p class="text-sm mb-0 activity-text"><MentionText :text="folderHistoryBody(entry)" /></p>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="p-3">
          <div class="mb-0">
            <label class="text-xs font-weight-bold mb-1 d-block">Folder Name <span class="text-danger">*</span></label>
            <input
              ref="folderNameInput"
              v-model="folderFormName"
              type="text"
              class="form-control form-control-sm"
              placeholder="Enter folder name"
              @keyup.enter="saveFolderModal()"
            />
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 p-3 border-top" :class="{ 'folder-modal-footer': editingFolderId }">
          <div v-if="folderSaveError" class="me-auto text-danger text-xs align-self-center">{{ folderSaveError }}</div>
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeFolderModal">Cancel</button>
          <button
            v-if="!editingFolderId || folderTab === 'details'"
            type="button"
            class="btn btn-sm mb-0"
            :class="folderFormState === 'FeedBack' ? 'btn-danger' : 'bg-gradient-dark text-white'"
            :disabled="folderSaving"
            @click="saveFolderModal"
          >
            <span v-if="folderSaving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
            <span v-else-if="folderFormState === 'FeedBack'">Save FeedBack</span>
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showFunctionModal" class="modal-overlay" @click.self="closeFunctionModal">
      <div class="modal-box" style="width: 440px; max-width: 96vw;">
        <div class="d-flex align-items-center p-3 border-bottom">
          <h6 class="mb-0 font-weight-bold">{{ editingFunctionId ? "Edit Group" : "Add Group" }}</h6>
        </div>
        <div class="p-3">
          <label class="text-xs font-weight-bold mb-1 d-block">Group Name <span class="text-danger">*</span></label>
          <input
            ref="functionNameInput"
            v-model="functionFormName"
            type="text"
            class="form-control form-control-sm"
            placeholder="Enter group name"
            @keyup.enter="saveFunctionModal()"
          />
        </div>
        <div class="d-flex justify-content-end gap-2 p-3 border-top">
          <div v-if="functionSaveError" class="me-auto text-danger text-xs align-self-center">{{ functionSaveError }}</div>
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeFunctionModal">Cancel</button>
          <button
            type="button"
            class="btn btn-sm mb-0 bg-gradient-dark text-white"
            :disabled="functionSaving"
            @click="saveFunctionModal"
          >
            <span v-if="functionSaving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div
      v-if="showReviewView"
      class="qa-tc-review-overlay"
      :class="reviewViewMode === 'pdf' ? 'is-pdf' : 'is-standard'"
    >
      <div class="qa-tc-review-shell">
        <div class="qa-tc-review-header">
          <div class="qa-tc-review-header-title">
            <h6 class="mb-0 font-weight-bold">{{ selectedFolderName || "Folder" }}</h6>
            <p class="text-xs text-secondary mb-0">{{ selectedFunctionName || "—" }}</p>
          </div>
          <div class="d-flex align-items-center gap-2">
            <div class="qa-tc-review-mode" role="tablist" aria-label="View mode">
              <button
                type="button"
                class="qa-tc-review-mode-btn"
                :class="{ active: reviewViewMode === 'standard' }"
                @click="reviewViewMode = 'standard'"
              >
                Standard
              </button>
              <button
                type="button"
                class="qa-tc-review-mode-btn"
                :class="{ active: reviewViewMode === 'pdf' }"
                @click="reviewViewMode = 'pdf'"
              >
                Table
              </button>
            </div>
            <button type="button" class="btn btn-sm btn-secondary mb-0" @click="closeReviewView">Close</button>
          </div>
        </div>
        <div class="qa-tc-review-filters">
          <div class="qa-tc-review-filter-field qa-tc-review-filter-field--search">
            <label>Search</label>
            <div class="input-group input-group-sm">
              <span class="input-group-text bg-white border-end-0">
                <i class="fas fa-search text-secondary" style="font-size:0.7rem;"></i>
              </span>
              <input
                v-model="reviewFilters.search"
                type="search"
                class="form-control border-start-0 ps-0"
                placeholder="Search test case..."
                style="font-size:0.75rem;"
              />
            </div>
          </div>
          <div class="qa-tc-review-filter-field">
            <label>Type</label>
            <base-select
              size="sm"
              :model-value="reviewFilters.testType"
              :options="testTypeFilterSelectOptions"
              option-label="label"
              option-value="value"
              @update:model-value="(v) => { reviewFilters.testType = v ?? ''; }"
            />
          </div>
          <div class="qa-tc-review-filter-field">
            <label>Priority</label>
            <base-select
              size="sm"
              :model-value="reviewFilters.priority"
              :options="priorityFilterSelectOptions"
              option-label="label"
              option-value="value"
              @update:model-value="(v) => { reviewFilters.priority = v ?? ''; }"
            />
          </div>
          <div class="qa-tc-review-filter-field">
            <label>Scenario</label>
            <base-select
              size="sm"
              :model-value="reviewFilters.scenario"
              :options="reviewScenarioFilterOptions"
              option-label="label"
              option-value="value"
              @update:model-value="onReviewScenarioFilterChange"
            />
          </div>
          <div class="qa-tc-review-filter-field">
            <label>Sub-Scenario</label>
            <base-select
              size="sm"
              :model-value="reviewFilters.subScenario"
              :options="reviewSubScenarioFilterOptions"
              option-label="label"
              option-value="value"
              @update:model-value="(v) => { reviewFilters.subScenario = v ?? ''; }"
            />
          </div>
          <div class="qa-tc-review-filter-field">
            <label>Status</label>
            <base-select
              size="sm"
              :model-value="reviewFilters.status"
              :options="reviewStatusFilterOptions"
              option-label="label"
              option-value="value"
              @update:model-value="(v) => { reviewFilters.status = v ?? ''; }"
            />
          </div>
        </div>
        <div class="qa-tc-review-body">
          <div class="qa-tc-review-doc">
            <div v-if="reviewLoading" class="qa-tc-review-status">
              <i class="fas fa-spinner fa-spin me-2"></i> Loading test cases...
            </div>
            <div v-else-if="reviewError" class="qa-tc-review-status text-danger">{{ reviewError }}</div>
            <div v-else class="qa-tc-review-content" :class="{ 'qa-tc-review-content--pdf': reviewViewMode === 'pdf' }">
              <div class="qa-tc-review-title-block">
                <p class="qa-tc-review-doc-title mb-2">TEST CASE : {{ reviewDocTitle }}</p>
                <div class="qa-tc-review-info-grid">
                  <div>
                    <span>Project</span>
                    <strong>{{ selectedProjectName || "—" }}</strong>
                  </div>
                  <div>
                    <span>Sub Project</span>
                    <strong>{{ selectedFunctionName || "—" }}</strong>
                  </div>
                  <div>
                    <span>Test Case total</span>
                    <strong>{{ filteredReviewCases.length }}</strong>
                  </div>
                </div>
              </div>
              <div v-if="reviewViewMode === 'pdf'" class="qa-tc-pdf-wrap">
                <p v-if="!filteredReviewCases.length" class="qa-tc-review-status">No test cases match this filter.</p>
                <table v-if="filteredReviewCases.length" class="qa-tc-pdf-table">
                  <colgroup>
                    <col class="qa-tc-pdf-col-id" />
                    <col class="qa-tc-pdf-col-name" />
                    <col class="qa-tc-pdf-col-type" />
                    <col class="qa-tc-pdf-col-priority" />
                    <col class="qa-tc-pdf-col-pre" />
                    <col class="qa-tc-pdf-col-data" />
                    <col class="qa-tc-pdf-col-module" />
                    <col class="qa-tc-pdf-col-step" />
                    <col class="qa-tc-pdf-col-expected" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Test Case Name</th>
                      <th>Test Type</th>
                      <th>Priority</th>
                      <th>Pre-Condition</th>
                      <th>Test Data</th>
                      <th>Module</th>
                      <th>Test Step</th>
                      <th>Expected Result</th>
                    </tr>
                  </thead>
                    <tbody v-for="scGroup in reviewPdfGroupedCases" :key="scGroup.scenario">
                      <tr class="qa-tc-pdf-section qa-tc-pdf-section--sc">
                        <td colspan="9">Scenario: {{ scGroup.scenario || "(No Scenario)" }}</td>
                      </tr>
                      <template v-for="subGroup in scGroup.subGroups" :key="`${scGroup.scenario}|${subGroup.subScenario}`">
                        <tr class="qa-tc-pdf-section qa-tc-pdf-section--sub">
                          <td colspan="9">Sub-Scenario: {{ subGroup.subScenario || "(No Sub-Scenario)" }}</td>
                        </tr>
                        <template v-for="tc in subGroup.cases" :key="tc.testCaseId">
                          <tr
                            class="qa-tc-pdf-case"
                            :class="{ 'qa-tc-pdf-case--active': reviewFocusId === tc.testCaseId }"
                            @click="focusReviewCase(tc)"
                          >
                            <td
                              class="qa-tc-pdf-id"
                              :rowspan="reviewStepCount(tc)"
                            >
                              <div>{{ formatTcLabel(tc) }}</div>
                              <span class="status-pill status-pill--sm" :class="statusPillClass(tc.state)">{{ enumLabel(tc.state || "Active") }}</span>
                            </td>
                            <td
                              class="qa-tc-pdf-name"
                              :rowspan="reviewStepCount(tc)"
                            >{{ tc.title || "Untitled" }}</td>
                            <td
                              class="text-center"
                              :rowspan="reviewStepCount(tc)"
                            >
                              <span class="qa-tc-review-type-val" :class="reviewTypeToneClass(tc.testType)">{{ enumLabel(tc.testType) }}</span>
                            </td>
                            <td
                              class="text-center"
                              :rowspan="reviewStepCount(tc)"
                            >
                              <span class="qa-tc-review-priority-val" :class="reviewPriorityToneClass(tc.priority)">{{ enumLabel(tc.priority) }}</span>
                            </td>
                            <td :rowspan="reviewStepCount(tc)">{{ tc.preconditions || "—" }}</td>
                            <td :rowspan="reviewStepCount(tc)">{{ tc.testData || "—" }}</td>
                            <td class="qa-tc-pdf-pre">{{ reviewModuleText(tc, 0) }}</td>
                            <td class="qa-tc-pdf-pre">{{ reviewStepText(tc, 0) }}</td>
                            <td class="qa-tc-pdf-pre">{{ reviewExpectedText(tc, 0) }}</td>
                          </tr>
                          <tr
                            v-for="(step, idx) in reviewExtraSteps(tc)"
                            :key="`${tc.testCaseId}-step-${idx + 1}`"
                            class="qa-tc-pdf-case qa-tc-pdf-case--cont"
                            :class="{ 'qa-tc-pdf-case--active': reviewFocusId === tc.testCaseId }"
                            @click="focusReviewCase(tc)"
                          >
                            <td class="qa-tc-pdf-pre">{{ reviewModuleText(tc, idx + 1) }}</td>
                            <td class="qa-tc-pdf-pre">{{ reviewStepText(tc, idx + 1) }}</td>
                            <td class="qa-tc-pdf-pre">{{ reviewExpectedText(tc, idx + 1) }}</td>
                          </tr>
                        </template>
                      </template>
                    </tbody>
                </table>
              </div>
              <template v-else>
                <p v-if="!filteredReviewCases.length" class="qa-tc-review-status">No test cases match this filter.</p>
                <article
                  v-for="tc in filteredReviewCases"
                  :key="tc.testCaseId"
                  :id="`review-tc-${tc.testCaseId}`"
                  class="qa-tc-review-case"
                  :class="{ 'qa-tc-review-case--active': reviewFocusId === tc.testCaseId }"
                  @click="focusReviewCase(tc)"
                >
                  <div class="qa-tc-review-case-top">
                    <div>
                      <p class="qa-tc-review-case-id mb-0">{{ formatTcLabel(tc) }}</p>
                      <h6 class="qa-tc-review-case-name mb-1">{{ tc.title || "Untitled" }}</h6>
                      <div class="qa-tc-review-type-row">
                        <span class="qa-tc-review-type-item">
                          <strong class="qa-tc-review-k">Type:</strong>
                          <span class="qa-tc-review-type-val" :class="reviewTypeToneClass(tc.testType)">{{ enumLabel(tc.testType) }}</span>
                        </span>
                        <span class="qa-tc-review-type-item">
                          <strong class="qa-tc-review-k">Priority:</strong>
                          <span class="qa-tc-review-priority-val" :class="reviewPriorityToneClass(tc.priority)">{{ enumLabel(tc.priority) }}</span>
                        </span>
                      </div>
                      <div class="qa-tc-review-hierarchy">
                        <div><strong class="qa-tc-review-k">Function:</strong> {{ reviewCaseFunction(tc) }}</div>
                        <div><strong class="qa-tc-review-k">Scenario:</strong> {{ reviewCaseScenario(tc) }}</div>
                        <div><strong class="qa-tc-review-k">Sub-Scenario:</strong> {{ reviewCaseSubScenario(tc) }}</div>
                      </div>
                    </div>
                    <span class="status-pill status-pill--sm" :class="statusPillClass(tc.state)">{{ enumLabel(tc.state || "Active") }}</span>
                  </div>
                  <div class="qa-tc-review-fields">
                    <div>
                      <p class="qa-tc-review-label">Pre-Condition</p>
                      <div class="qa-tc-review-value">
                        <BulletText :text="tc.preconditions" list-class="mb-0" fallback="—" />
                      </div>
                    </div>
                    <div>
                      <p class="qa-tc-review-label">Test Data</p>
                      <div class="qa-tc-review-value">
                        <BulletText :text="tc.testData" list-class="mb-0" fallback="—" />
                      </div>
                    </div>
                  </div>
                  <table class="qa-tc-review-steps">
                    <colgroup>
                      <col class="qa-tc-review-col-no" />
                      <col class="qa-tc-review-col-module" />
                      <col class="qa-tc-review-col-step" />
                      <col class="qa-tc-review-col-expected" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Step</th>
                        <th>Module</th>
                        <th>Test Step</th>
                        <th>Expected Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!(tc.steps || []).length">
                        <td colspan="4" class="text-secondary">—</td>
                      </tr>
                      <tr v-for="(step, idx) in tc.steps" :key="`${tc.testCaseId}-step-${idx}`">
                        <td>{{ idx + 1 }}</td>
                        <td><BulletText :text="step.platform" list-class="mb-0" fallback="—" /></td>
                        <td><BulletText :text="step.stepDetail || step.action" list-class="mb-0" fallback="—" /></td>
                        <td><BulletText :text="step.expectedResult || step.expected" list-class="mb-0" fallback="—" /></td>
                      </tr>
                    </tbody>
                  </table>
                </article>
              </template>
            </div>
          </div>
          <aside class="qa-tc-review-side">
            <section class="qa-tc-review-side-status">
              <p class="qa-tc-review-side-title mb-1">Status</p>
              <p class="text-xs text-secondary mb-2">{{ reviewFocusLabel }}</p>
              <base-select
                v-if="reviewFocusCase"
                size="sm"
                :model-value="reviewStatusSelectValue"
                :options="stateOptions"
                option-label="label"
                option-value="value"
                :disabled="reviewStateSaving === reviewFocusCase.testCaseId"
                @update:model-value="onReviewStatusChange"
              />
            </section>
            <section class="qa-tc-review-side-comments">
              <p class="qa-tc-review-side-title mb-2">{{ reviewFeedbackId ? "Feedback" : "Comments" }}</p>
              <div class="qa-tc-review-side-compose">
                <template v-if="reviewFeedbackId">
                  <BulletTextarea
                    v-model="reviewFeedbackText"
                    class="mb-2"
                    :rows="4"
                    resizable
                    mentions
                    placeholder="Feedback is required (- item or 1. item, @ to tag)"
                  />
                  <div class="d-flex justify-content-end gap-2 mb-2">
                    <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="cancelReviewFeedback">Cancel</button>
                    <button
                      type="button"
                      class="btn btn-sm btn-danger mb-0"
                      :disabled="reviewStateSaving === reviewFeedbackId"
                      @click="submitReviewFeedback(reviewFeedbackCase)"
                    >
                      <span v-if="reviewStateSaving === reviewFeedbackId"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
                      <span v-else>Save FeedBack</span>
                    </button>
                  </div>
                </template>
                <template v-else>
                  <BulletTextarea
                    v-model="reviewCommentText"
                    class="mb-2"
                    :rows="4"
                    resizable
                    mentions
                    placeholder="Add a comment... (- item or 1. item, @ to tag)"
                  />
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-dark w-100 mb-2"
                    :disabled="reviewCommentSaving || !reviewCommentText.trim() || !reviewFocusId"
                    @click="saveReviewComment"
                  >
                    <span v-if="reviewCommentSaving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
                    <span v-else>Save Comment</span>
                  </button>
                </template>
              </div>
              <div class="qa-tc-review-side-history">
                <div v-if="reviewCommentsLoading" class="text-xs text-secondary">Loading comments...</div>
                <div v-else-if="!reviewComments.length" class="text-xs text-secondary">No comments yet.</div>
                <div v-else class="qa-tc-review-comments">
                  <div v-for="entry in reviewComments" :key="entry.activityId" class="qa-tc-review-comment">
                    <div class="d-flex justify-content-between gap-2">
                      <strong>{{ entry.authorName || "System" }}</strong>
                      <span>{{ formatHistoryDate(entry.createdAt) }}</span>
                    </div>
                    <span class="activity-badge" :class="activityBadgeClass(entry)">{{ activityBadgeLabel(entry) }}</span>
                    <div class="qa-tc-review-comment-body">
                      <BulletText :text="reviewCommentBody(entry)" list-class="mb-0" fallback="—" />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showPendingFeedbackModal" class="modal-overlay" style="z-index:3300;">
      <div class="modal-box" style="width:480px; max-width:92vw; padding:2rem;">
        <h6 class="mb-0 fw-semibold mb-3">Specify FeedBack</h6>
        <p class="text-sm text-secondary mb-4">Please specify FeedBack before switching test cases.</p>
        <div class="d-flex flex-wrap justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="stayOnPendingFeedback">Cancel</button>
          <button
            type="button"
            class="btn btn-sm btn-outline-dark mb-0"
            :disabled="!!reviewStateSaving"
            @click="savePendingFeedbackWithoutMessage"
          >
            Save without feedback
          </button>
          <button type="button" class="btn btn-sm btn-secondary mb-0" @click="skipPendingFeedbackStatusChange">Not change status</button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showEditDeleteConfirm" class="modal-overlay" style="z-index:3200;">
      <div class="modal-box" style="width:420px; max-width:92vw; padding:2rem;">
        <div class="d-flex align-items-center gap-3 mb-3">
          <span style="font-size:1.4rem;">🗑️</span>
          <h6 class="mb-0 fw-semibold">Delete Test Case</h6>
        </div>
        <p class="text-sm text-secondary mb-4">
          {{ editDeleteBlocked ? editDeleteBlockedMessage : "Are you sure you want to delete?" }}
        </p>
        <div class="d-flex justify-content-end gap-2">
          <template v-if="editDeleteBlocked">
            <button type="button" class="btn btn-sm btn-secondary mb-0" @click="closeEditDeleteConfirm">Close</button>
          </template>
          <template v-else>
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="formDeleting" @click="closeEditDeleteConfirm">Cancel</button>
            <button type="button" class="btn btn-sm btn-danger mb-0" :disabled="formDeleting" @click="deleteEditingTestCase">
              <i v-if="formDeleting" class="fas fa-spinner fa-spin me-1"></i>Delete
            </button>
          </template>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showBulkEdit" class="modal-overlay" @click.self="closeBulkEdit">
      <div class="modal-box" style="width: 520px; max-width: 96vw;">
        <div class="d-flex align-items-center p-3 border-bottom">
          <h6 class="mb-0 font-weight-bold">Edit Test Cases</h6>
        </div>
        <div class="p-3">
          <p class="text-sm text-secondary mb-3">
            Update Function, Scenario and Sub-Scenario for
            <strong>{{ selectedIds.length }}</strong>
            selected test case{{ selectedIds.length !== 1 ? "s" : "" }}.
            Leave a field empty to keep the current value.
          </p>
          <div class="mb-3">
            <label class="text-xs font-weight-bold mb-1 d-block">Function</label>
            <base-select
              size="sm"
              searchable
              allow-create
              create-label="Create function"
              v-model="bulkForm.functionId"
              :options="bulkFunctionSelectOptions"
              option-label="label"
              option-value="value"
              placeholder="Select or type new function"
              :has-error="!!bulkFormFieldErrors.function"
              @update:model-value="onBulkFunctionChange"
              @create="bulkForm.functionIsNew = true"
            />
            <p v-if="bulkFormFieldErrors.function" class="text-xs text-danger mb-0 mt-1">{{ bulkFormFieldErrors.function }}</p>
            <p v-else-if="bulkForm.functionIsNew && bulkForm.functionId" class="text-xs text-warning mb-0 mt-1">
              <i class="fas fa-info-circle me-1"></i>จะสร้าง Function ใหม่เมื่อบันทึก
            </p>
          </div>
          <div class="mb-3">
            <label class="text-xs font-weight-bold mb-1 d-block">Scenario</label>
            <base-select
              size="sm"
              searchable
              allow-create
              create-label="Create scenario"
              v-model="bulkForm.scenarioId"
              :options="bulkScenarioSelectOptions"
              option-label="label"
              option-value="value"
              placeholder="Select or type new scenario"
              :has-error="!!bulkFormFieldErrors.scenario"
              @update:model-value="onBulkScenarioChange"
              @create="bulkForm.scenarioIsNew = true"
            />
            <p v-if="bulkFormFieldErrors.scenario" class="text-xs text-danger mb-0 mt-1">{{ bulkFormFieldErrors.scenario }}</p>
            <p v-else-if="bulkForm.scenarioIsNew && bulkForm.scenarioId" class="text-xs text-warning mb-0 mt-1">
              <i class="fas fa-info-circle me-1"></i>จะสร้าง Scenario ใหม่เมื่อบันทึก
            </p>
          </div>
          <div class="mb-0">
            <label class="text-xs font-weight-bold mb-1 d-block">Sub-Scenario</label>
            <base-select
              size="sm"
              searchable
              allow-create
              create-label="Create sub-scenario"
              v-model="bulkForm.subScenarioId"
              :options="bulkSubScenarioSelectOptions"
              option-label="label"
              option-value="value"
              placeholder="Select or type new sub-scenario"
              :has-error="!!bulkFormFieldErrors.subScenario"
              @update:model-value="onBulkSubScenarioChange"
              @create="bulkForm.subScenarioIsNew = true"
            />
            <p v-if="bulkFormFieldErrors.subScenario" class="text-xs text-danger mb-0 mt-1">{{ bulkFormFieldErrors.subScenario }}</p>
            <p v-else-if="bulkForm.subScenarioIsNew && bulkForm.subScenarioId" class="text-xs text-warning mb-0 mt-1">
              <i class="fas fa-info-circle me-1"></i>จะสร้าง Sub-Scenario ใหม่เมื่อบันทึก
            </p>
          </div>
          <p v-if="bulkEditError" class="text-xs text-danger mb-0 mt-2">{{ bulkEditError }}</p>
        </div>
        <div class="d-flex justify-content-end gap-2 p-3 border-top">
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="bulkEditSaving" @click="closeBulkEdit">Cancel</button>
          <button type="button" class="btn btn-sm mb-0 bg-gradient-dark text-white" :disabled="bulkEditSaving" @click="saveBulkEdit">
            <span v-if="bulkEditSaving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showFunctionDeleteConfirm" class="modal-overlay">
      <div class="modal-box" style="width:380px; max-width:92vw; padding:2rem;">
        <div class="d-flex align-items-center gap-3 mb-3">
          <span style="font-size:1.4rem;">🗑️</span>
          <h6 class="mb-0 fw-semibold">Delete Group</h6>
        </div>
        <p class="text-sm text-secondary mb-4">
          Are you sure you want to delete
          <strong>{{ functionDeleteTarget?.functionName || "this group" }}</strong>?
          This action cannot be undone.
        </p>
        <p v-if="functionDeleteError" class="text-xs text-danger mb-3">{{ functionDeleteError }}</p>
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="functionDeleting" @click="closeFunctionDeleteConfirm">Cancel</button>
          <button type="button" class="btn btn-sm btn-danger mb-0" :disabled="functionDeleting" @click="confirmDeleteFunction">
            <span v-if="functionDeleting"><i class="fas fa-spinner fa-spin me-1"></i>Deleting...</span>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showDeleteConfirm" class="modal-overlay">
      <div class="modal-box" style="width:380px; max-width:92vw; padding:2rem;">
        <div class="d-flex align-items-center gap-3 mb-3">
          <span style="font-size:1.4rem;">🗑️</span>
          <h6 class="mb-0 fw-semibold">Delete Test Cases</h6>
        </div>
        <p class="text-sm text-secondary mb-4">
          Are you sure you want to delete <strong>{{ selectedIds.length }}</strong>
          test case{{ selectedIds.length !== 1 ? "s" : "" }}?
          This action cannot be undone.
        </p>
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="showDeleteConfirm = false">Cancel</button>
          <button type="button" class="btn btn-sm btn-danger mb-0" @click="deleteSelected">Delete</button>
        </div>
      </div>
    </div>
  </Teleport>

  <div v-if="uploadToast.show" class="upload-toast" :class="uploadToast.type">
    <i :class="uploadToast.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
    {{ uploadToast.message }}
  </div>
</template>

<script>
import {
  realTestCasesApi,
  testFunctionsApi,
  scenariosApi,
  subScenariosApi,
  stepsAndResultApi,
  usersApi,
  reportsApi,
} from "@/services/backendApi";
import { API_BASE_URL, fetchWithTimeout } from "@/config/apiBase";
import { normalizeTypeOfTestCase } from "@/utils/testCaseCategory";
import BulletText from "@/components/BulletText.vue";
import BulletTextarea from "@/components/BulletTextarea.vue";
import MentionText from "@/components/MentionText.vue";
import MentionTextarea from "@/components/MentionTextarea.vue";
import "@/assets/css/pm-tab-scroll.css";

function extractJiraKey(text) {
  const trimmed = String(text || "").trim();
  const match = trimmed.match(/([A-Za-z][A-Za-z0-9_]*-\d+)/);
  return match ? match[1].toUpperCase() : trimmed;
}

const STATE_OPTIONS = ["Draft", "Waiting Review", "In Review", "FeedBack", "Active"].map((v) => ({
  value: v,
  label: v,
}));
const PRIORITY_OPTIONS = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];
const CATEGORY_OPTIONS = [
  { value: "Functional", label: "Functional" },
  { value: "E2E", label: "E2E" },
];
const AUTOMATION_STATUS_OPTIONS = [
  { value: "not_automated", label: "Not Automated" },
  { value: "automated", label: "Automated" },
  { value: "in_progress", label: "In Progress" },
];
const TEST_TYPE_OPTIONS = [
  { value: "Positive", label: "Positive" },
  { value: "Negative", label: "Negative" },
];
const EXECUTION_TYPE_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "automated", label: "Automated" },
];

function emptyCopyPicker() {
  return {
    functionId: "",
    folderKey: "",
    testCaseId: "",
    stepMode: "all",
    selectedStepIndexes: [],
  };
}

function emptyCopyStepPicker() {
  return {
    functionId: "",
    folderKey: "",
    scenarioId: "",
    subScenarioId: "",
    testCaseId: "",
    selectedIndexes: [],
  };
}

function blankStep(extra = {}) {
  return {
    _id: Date.now() + Math.random(),
    platform: "",
    stepDetail: "",
    expectedResult: "",
    ...extra,
  };
}

function normalizeFormStep(s) {
  return blankStep({
    _id: s?._id || Date.now() + Math.random(),
    platform: s?.platform || "",
    stepDetail: s?.stepDetail || s?.action || "",
    expectedResult: s?.expectedResult || s?.expected || "",
  });
}

function emptyBulkForm() {
  return {
    functionId: "",
    functionIsNew: false,
    scenarioId: "",
    scenarioIsNew: false,
    subScenarioId: "",
    subScenarioIsNew: false,
  };
}

function emptyCreateForm() {
  return {
    functionId: "",
    functionIsNew: false,
    scenarioId: "",
    scenarioIsNew: false,
    subScenarioId: "",
    subScenarioIsNew: false,
    title: "",
    showDescription: false,
    description: "",
    preconditions: "",
    testData: "",
    steps: [blankStep()],
    requirementIds: [],
    state: "Waiting Review",
    priority: "medium",
    typeOfTestCase: "Functional",
    automationStatus: "not_automated",
    testType: "Positive",
    executionType: "manual",
    ownerId: "",
  };
}

export default {
  name: "qa-test-case-board",
  components: { BulletText, BulletTextarea, MentionText, MentionTextarea },
  data() {
    return {
      projects: [],
      subProjects: [],
      functions: [],
      sets: [],
      folders: [],
      cases: [],
      loading: false,
      loadToken: 0,
      error: "",
      searchText: "",
      projectFilter: "all",
      filtersPanelOpen: false,
      filters: {
        function: "",
        scenario: "",
        subScenario: "",
        testType: "",
        status: "",
        priority: "",
      },
      loadedProjectId: "",
      loadedSubProjectId: "",
      loadedFunctionId: "",
      showFunctionModal: false,
      editingFunctionId: null,
      functionFormName: "",
      functionSaving: false,
      functionSaveError: "",
      showFolderModal: false,
      folderFormName: "",
      folderFormState: "Draft",
      folderFormInitialState: "Draft",
      folderFormComment: "",
      folderTab: "details",
      folderHistory: [],
      folderHistoryLoading: false,
      folderSaving: false,
      folderSaveError: "",
      editingFolderId: null,
      editingFolderVirtual: false,
      collapsedFunctions: [],
      collapsedScenarios: [],
      collapsedSubScenarios: [],
      showDownloadMenu: false,
      showUploadMenu: false,
      selectingMode: false,
      selectedIds: [],
      showDeleteConfirm: false,
      showFunctionDeleteConfirm: false,
      functionDeleteTarget: null,
      functionDeleting: false,
      functionDeleteError: "",
      showBulkEdit: false,
      bulkForm: emptyBulkForm(),
      bulkFormFieldErrors: { function: "", scenario: "", subScenario: "" },
      bulkEditSaving: false,
      bulkEditError: "",
      showReviewView: false,
      reviewViewMode: "standard",
      reviewCommentsOpen: true,
      reviewFilters: { search: "", scenario: "", subScenario: "", status: "", testType: "", priority: "" },
      reviewLoading: false,
      reviewError: "",
      reviewCases: [],
      reviewFocusId: null,
      reviewComments: [],
      reviewCommentsLoading: false,
      reviewCommentText: "",
      reviewCommentSaving: false,
      reviewStateSaving: null,
      reviewFeedbackId: null,
      reviewFeedbackText: "",
      showPendingFeedbackModal: false,
      pendingReviewFocusCase: null,
      uploadToast: { show: false, type: "success", message: "" },
      importProgress: {
        show: false,
        total: 0,
        current: 0,
        success: 0,
        failed: 0,
        currentTitle: "",
        done: false,
        cancelled: false,
      },
      showCreateModal: false,
      formCopyMode: false,
      copyHydrating: false,
      copyPicker: emptyCopyPicker(),
      copyFunctions: [],
      copyFunctionsLoading: false,
      copyFolders: [],
      copyFoldersLoading: false,
      copyCases: [],
      copyCasesLoading: false,
      copyStepFunctions: [],
      copyStepFolders: [],
      copyStepCases: [],
      copyStepFunctionsLoading: false,
      copyStepFoldersLoading: false,
      copySourceCase: null,
      copyStepsLoading: false,
      copyApplyMode: "case",
      dragStepIndex: null,
      dragOverStepIndex: null,
      showCopyFieldChoiceModal: false,
      copyFieldConflicts: [],
      pendingCopySteps: [],
      pendingCopyIncoming: null,
      showCopyStepModal: false,
      copyStepPicker: emptyCopyStepPicker(),
      copyStepSource: null,
      copyStepLoading: false,
      showRequirementModal: false,
      editMode: false,
      editingFromDetail: false,
      editingTc: null,
      editInitialScenarioId: "",
      editInitialSubScenarioId: "",
      editInitialScenarioName: "",
      editInitialSubScenarioName: "",
      formSaving: false,
      formDeleting: false,
      showEditDeleteConfirm: false,
      editDeleteChecking: false,
      editDeleteBlocked: false,
      editDeleteBlockedMessage: "",
      formError: "",
      formFieldErrors: { function: "", scenario: "", subScenario: "", title: "" },
      form: emptyCreateForm(),
      functionOptions: [],
      scenarioOptions: [],
      allScenarios: [],
      subScenarioOptions: [],
      requirementIdInput: "",
      jiraProjectKey: "",
      jiraProjects: [],
      jiraIssues: [],
      jiraLoadingProjects: false,
      jiraLoadingIssues: false,
      jiraSearch: "",
      jiraSearchTimer: null,
      stateOptions: STATE_OPTIONS,
      priorityOptions: PRIORITY_OPTIONS,
      categoryOptions: CATEGORY_OPTIONS,
      automationStatusOptions: AUTOMATION_STATUS_OPTIONS,
      testTypeOptions: TEST_TYPE_OPTIONS,
      executionTypeOptions: EXECUTION_TYPE_OPTIONS,
      showDetailModal: false,
      detailTab: "details",
      detailLoading: false,
      detailError: "",
      detailTestCase: null,
      userOptions: [],
      history: [],
      viewingHistoryIndex: null,
      showHistoryDropdown: false,
      activityHistory: [],
      activityLoading: false,
      remarkDraft: "",
      remarkSaving: false,
      remarkEntries: [],
      remarkLoading: false,
    };
  },
  computed: {
    selectedProjectId() {
      const raw = String(this.$route.query.project || "").trim();
      return raw || "";
    },
    selectedFunctionId() {
      const raw = String(this.$route.query.function || "").trim();
      return raw || "";
    },
    selectedSubProjectId() {
      return String(this.selectedFunction?.subProjectId || this.$route.query.subProject || "").trim();
    },
    selectedFolderId() {
      const raw = String(this.$route.query.folder || this.$route.query.set || "").trim();
      return raw || "";
    },
    view() {
      if (this.selectedProjectId && this.selectedFunctionId && this.selectedFolderId) return "cases";
      if (this.selectedProjectId && this.selectedFunctionId) return "folders";
      if (this.selectedProjectId) return "functions";
      return "projects";
    },
    selectedProject() {
      return this.projects.find((p) => String(p.projectId) === this.selectedProjectId) || null;
    },
    selectedFunction() {
      return this.functions.find((f) => String(f.functionId) === this.selectedFunctionId) || null;
    },
    selectedSubProject() {
      return this.subProjects.find((s) => String(s.subProjectId) === this.selectedSubProjectId) || null;
    },
    selectedFunctionName() {
      return this.selectedFunction?.functionName
        || this.cases[0]?.functionName
        || this.cases[0]?.function
        || "Group";
    },
    selectedProjectName() {
      return this.selectedProject?.projectName
        || this.selectedSubProject?.projectName
        || this.selectedFolder?.projectName
        || this.cases[0]?.projectName
        || "Project";
    },
    selectedSubProjectName() {
      return this.selectedSubProject?.subProjectName
        || this.selectedFolder?.subProjectName
        || this.cases[0]?.subProjectName
        || "Sub Project";
    },
    selectedFolder() {
      return this.folders.find((f) => String(f.folderId) === this.selectedFolderId) || null;
    },
    selectedFolderName() {
      return this.selectedFolder?.folderName || "Folder";
    },
    bulkFunctionSelectOptions() {
      const seen = new Set();
      const opts = [];
      for (const tc of this.cases || []) {
        const label = this.caseFunctionName(tc);
        if (!label || this.isUncategorizedLabel(label)) continue;
        const key = label.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        opts.push({ value: label, label });
      }
      return opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    },
    bulkScenarioSelectOptions() {
      const fn = String(this.bulkForm.functionId || "").trim();
      const seen = new Set();
      const opts = [];
      for (const tc of this.cases || []) {
        if (fn && this.caseFunctionName(tc) !== fn) continue;
        const label = this.caseScenarioName(tc);
        if (!label || this.isUncategorizedLabel(label)) continue;
        const key = label.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const value = tc.scenarioId ? String(tc.scenarioId) : label;
        opts.push({ value, label });
      }
      return opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    },
    bulkSubScenarioSelectOptions() {
      const fn = String(this.bulkForm.functionId || "").trim();
      const sc = String(this.bulkForm.scenarioId || "").trim();
      const seen = new Set();
      const opts = [];
      for (const tc of this.cases || []) {
        if (fn && this.caseFunctionName(tc) !== fn) continue;
        if (sc) {
          const scMatch = /^\d+$/.test(sc)
            ? String(tc.scenarioId) === sc
            : this.caseScenarioName(tc) === sc;
          if (!scMatch) continue;
        }
        const label = this.caseSubScenarioName(tc);
        if (!label || this.isUncategorizedLabel(label)) continue;
        const key = label.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const value = tc.subScenarioId ? String(tc.subScenarioId) : label;
        opts.push({ value, label });
      }
      return opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    },
    copyFunctionSelectOptions() {
      return this.uniqueGroupsByName(this.copyFunctions).map((fn) => ({
        value: String(fn.functionId),
        label: fn.functionName || `Group ${fn.functionId}`,
      }));
    },
    copyFolderSelectOptions() {
      return (this.copyFolders || []).map((folder) => ({
        value: folder.folderKey,
        label: folder.label,
      }));
    },
    copyCaseSelectOptions() {
      return (this.copyCases || []).map((tc) => ({
        value: String(tc.testCaseId),
        label: `${this.formatTcLabel(tc)} · ${tc.title || "Untitled"}`,
      }));
    },
    copyFieldsLocked() {
      return this.formCopyMode && !this.copySourceCase;
    },
    copySourceSteps() {
      const steps = this.copySourceCase?.steps;
      if (!Array.isArray(steps)) return [];
      return steps.map((s) => normalizeFormStep(s));
    },
    copyStepFunctionSelectOptions() {
      return this.uniqueGroupsByName(this.copyStepFunctions).map((fn) => ({
        value: String(fn.functionId),
        label: fn.functionName || `Group ${fn.functionId}`,
      }));
    },
    copyStepFolderSelectOptions() {
      return (this.copyStepFolders || []).map((folder) => ({
        value: folder.folderKey,
        label: folder.label,
      }));
    },
    copyStepScenarioOptions() {
      const seen = new Set();
      return (this.copyStepCases || []).reduce((list, tc) => {
        const value = String(tc.scenarioId || "");
        if (!value || seen.has(value)) return list;
        seen.add(value);
        list.push({ value, label: tc.scenario || `Scenario ${value}` });
        return list;
      }, []);
    },
    copyStepSubScenarioOptions() {
      const scenarioId = String(this.copyStepPicker.scenarioId || "");
      if (!scenarioId) return [];
      const seen = new Set();
      return (this.copyStepCases || []).reduce((list, tc) => {
        if (String(tc.scenarioId || "") !== scenarioId) return list;
        const value = tc.subScenarioId ? String(tc.subScenarioId) : "__none__";
        if (seen.has(value)) return list;
        seen.add(value);
        list.push({
          value,
          label: tc.subScenario || (value === "__none__" ? "—" : `Sub-Scenario ${value}`),
        });
        return list;
      }, []);
    },
    copyStepTitleOptions() {
      const scenarioId = String(this.copyStepPicker.scenarioId || "");
      const subId = String(this.copyStepPicker.subScenarioId || "");
      if (!scenarioId || !subId) return [];
      return (this.copyStepCases || [])
        .filter((tc) => {
          if (String(tc.scenarioId || "") !== scenarioId) return false;
          const tcSub = tc.subScenarioId ? String(tc.subScenarioId) : "__none__";
          return tcSub === subId;
        })
        .map((tc) => ({
          value: String(tc.testCaseId),
          label: `${this.formatTcLabel(tc)} · ${tc.title || "Untitled"}`,
        }));
    },
    copyStepSourceSteps() {
      const steps = this.copyStepSource?.steps;
      if (!Array.isArray(steps)) return [];
      return steps.map((s, idx) => ({
        ...normalizeFormStep(s),
        stepId: s.stepNo || s.testStepId || idx + 1,
      }));
    },
    searchPlaceholder() {
      if (this.view === "cases") return "Title, ID, scenario…";
      if (this.view === "folders") return "Folder name";
      if (this.view === "functions") return "Group name";
      return "Project name";
    },
    backLabel() {
      if (this.view === "cases") return "All Folder";
      if (this.view === "folders") return "All Group";
      return "All projects";
    },
    pageSubtitle() {
      if (this.view === "projects") return "เลือก Project";
      if (this.view === "functions") return this.selectedProjectName;
      if (this.view === "folders") return `${this.selectedProjectName} / ${this.selectedFunctionName}`;
      return `${this.selectedProjectName} / ${this.selectedFunctionName} / ${this.selectedFolderName}`;
    },
    listTotalCount() {
      if (this.view === "projects") return this.filteredProjects.length;
      if (this.view === "functions") return this.filteredFunctions.length;
      if (this.view === "folders") return this.filteredFolders.length;
      return this.filteredCases.length;
    },
    filteredProjects() {
      const q = this.searchText.trim().toLowerCase();
      const selected = String(this.projectFilter || "all");
      return this.projects.filter((p) => {
        if (selected !== "all" && String(p.projectId) !== selected) return false;
        if (!q) return true;
        return String(p.projectName || "").toLowerCase().includes(q);
      });
    },
    projectFilterOptions() {
      return [
        { value: "all", label: "All Projects" },
        ...this.projects.map((p) => ({
          value: String(p.projectId),
          label: p.projectName || "Untitled project",
        })),
      ];
    },
    projectsEmptyMessage() {
      if (!this.projects.length) return "No projects yet.";
      return "No projects match this filter.";
    },
    foldersEmptyMessage() {
      return this.folders.length
        ? "No folders match this search."
        : "This sub-project has no folders yet. Click Add Folder to create one.";
    },
    filteredFolders() {
      const q = this.searchText.trim().toLowerCase();
      if (!q) return this.folders;
      return this.folders.filter((f) => String(f.folderName || "").toLowerCase().includes(q));
    },
    folderCommentHistory() {
      return (this.folderHistory || []).filter(
        (entry) => entry.entryType === "remark" || entry.entryType === "feedback",
      );
    },
    functionsEmptyMessage() {
      return this.functions.length
        ? "No groups match this search."
        : "This project has no groups yet.";
    },
    filteredFunctions() {
      const q = this.searchText.trim().toLowerCase();
      const rows = this.uniqueGroupsByName(this.functions);
      const filtered = q
        ? rows.filter((f) => String(f.functionName || "").toLowerCase().includes(q))
        : rows;
      return filtered.sort((a, b) =>
        String(a.functionName || "").localeCompare(String(b.functionName || ""), undefined, { sensitivity: "base" }),
      );
    },
    advancedFilterCount() {
      let count = 0;
      if (this.filters.function) count += 1;
      if (this.filters.scenario) count += 1;
      if (this.filters.subScenario) count += 1;
      if (this.filters.testType) count += 1;
      return count;
    },
    statusFilterSelectOptions() {
      return [
        { label: "All Status", value: "" },
        { label: "Active", value: "Active" },
        { label: "FeedBack", value: "FeedBack" },
        { label: "Cancel", value: "Cancel" },
        { label: "Waiting Review", value: "Waiting Review" },
        { label: "Draft", value: "Draft" },
      ];
    },
    priorityFilterSelectOptions() {
      return [
        { label: "All Priority", value: "" },
        { label: "High", value: "high" },
        { label: "Medium", value: "medium" },
        { label: "Low", value: "low" },
      ];
    },
    functionFilterSelectOptions() {
      return [
        { label: "All Function", value: "" },
        ...this.uniqueCaseValues((tc) => this.caseFunctionName(tc)).map((value) => ({
          label: value,
          value,
        })),
      ];
    },
    scenarioFilterSelectOptions() {
      const fn = String(this.filters.function || "");
      const values = this.uniqueCaseValues((tc) => {
        if (fn && this.caseFunctionName(tc) !== fn) return "";
        return this.caseScenarioName(tc);
      });
      return [
        { label: "All Scenario", value: "" },
        ...values.map((value) => ({ label: value, value })),
      ];
    },
    subScenarioFilterSelectOptions() {
      const fn = String(this.filters.function || "");
      const sc = String(this.filters.scenario || "");
      const values = this.uniqueCaseValues((tc) => {
        if (fn && this.caseFunctionName(tc) !== fn) return "";
        if (sc && this.caseScenarioName(tc) !== sc) return "";
        return this.caseSubScenarioName(tc);
      });
      return [
        { label: "All Sub-scenario", value: "" },
        ...values.map((value) => ({ label: value, value })),
      ];
    },
    testTypeFilterSelectOptions() {
      return [
        { label: "All Type", value: "" },
        { label: "Positive", value: "Positive" },
        { label: "Negative", value: "Negative" },
      ];
    },
    allVisibleSelected() {
      const ids = this.filteredCases.map((tc) => tc.testCaseId);
      return ids.length > 0 && ids.every((id) => this.selectedIds.includes(id));
    },
    filteredCases() {
      const q = this.searchText.trim().toLowerCase();
      const { status, priority, function: fn, scenario, subScenario, testType } = this.filters;
      return this.cases.filter((tc) => {
        if (this.isDeletedState(tc.state)) return false;
        if (status && String(tc.state || "").trim().toLowerCase() !== status.toLowerCase()) return false;
        if (priority && String(tc.priority || "").trim().toLowerCase() !== priority.toLowerCase()) return false;
        if (fn && this.caseFunctionName(tc) !== fn) return false;
        if (scenario && this.caseScenarioName(tc) !== scenario) return false;
        if (subScenario && this.caseSubScenarioName(tc) !== subScenario) return false;
        if (testType && String(tc.testType || "").trim().toLowerCase() !== testType.toLowerCase()) return false;
        if (!q) return true;
        const hay = [
          this.formatTcLabel(tc),
          tc.title,
          this.caseFunctionName(tc),
          this.caseScenarioName(tc),
          this.caseSubScenarioName(tc),
          tc.subProjectName,
          tc.state,
          tc.priority,
          tc.testType,
        ].join(" ").toLowerCase();
        return hay.includes(q);
      });
    },
    groupedCases() {
      const functionMap = new Map();
      for (const tc of this.filteredCases) {
        const functionKey = this.caseFunctionName(tc) || "Uncategorized";
        if (!functionMap.has(functionKey)) {
          functionMap.set(functionKey, { function: functionKey, scenarioGroups: new Map() });
        }
        const fnGroup = functionMap.get(functionKey);

        const scenarioKey = this.caseScenarioName(tc) || "Uncategorized";
        if (!fnGroup.scenarioGroups.has(scenarioKey)) {
          fnGroup.scenarioGroups.set(scenarioKey, { scenario: scenarioKey, subGroups: new Map() });
        }
        const scGroup = fnGroup.scenarioGroups.get(scenarioKey);

        const subKey = this.caseSubScenarioName(tc) || "Uncategorized";
        if (!scGroup.subGroups.has(subKey)) {
          scGroup.subGroups.set(subKey, { subScenario: subKey, cases: [] });
        }
        scGroup.subGroups.get(subKey).cases.push(tc);
      }
      return [...functionMap.values()].map((fnGroup) => ({
        function: fnGroup.function,
        scenarioGroups: [...fnGroup.scenarioGroups.values()].map((scGroup) => ({
          scenario: scGroup.scenario,
          subGroups: [...scGroup.subGroups.values()],
        })),
      }));
    },
    routeKey() {
      return `${this.selectedProjectId}:${this.selectedFunctionId}:${this.selectedFolderId}`;
    },
    jiraProjectSelectOptions() {
      return (this.jiraProjects || []).map((p) => ({
        label: `${p.name} (${p.key})`,
        value: p.key,
      }));
    },
    currentUserId() {
      return this.$store.state.user?.userId ?? this.$store.state.user?.user_id ?? null;
    },
    editingTcId() {
      return this.editingTc?.testCaseId || "";
    },
    isViewingHistory() {
      return this.viewingHistoryIndex !== null;
    },
    detailVersionCount() {
      return 1 + (this.history?.length || 0);
    },
    detailDisplayTc() {
      const current = this.detailTestCase || {};
      if (this.viewingHistoryIndex === null) return current;
      const snapshot = this.history?.[this.viewingHistoryIndex]?.snapshot;
      if (!snapshot) return current;
      return {
        ...snapshot,
        function: snapshot.function || snapshot.functionName || "",
        scenario: snapshot.scenario || snapshot.scenarioName || "",
        subScenario: snapshot.subScenario || snapshot.subScenarioName || "",
        ownerFullName: snapshot.ownerFullName || "",
        steps: (snapshot.steps || []).map((s) => ({
          platform: s.platform || "",
          action: s.action || s.stepDetail || "",
          expected: s.expected || s.expectedResult || "",
        })),
      };
    },
    detailStepsForDisplay() {
      const steps = this.detailDisplayTc?.steps;
      if (Array.isArray(steps) && steps.length) return steps;
      return [{ platform: "", action: "", expected: "" }];
    },
    detailCreatedByLine() {
      const tc = this.detailTestCase || {};
      const name = tc.ownerFullName || "";
      const at = tc.createdAt ? this.formatDetailCreatedAt(tc.createdAt) : "";
      if (name && at) return `Created by ${name} ${at}`;
      if (name) return `Created by ${name}`;
      if (at) return `Created ${at}`;
      return "Created by —";
    },
    reviewDocTitle() {
      return String(this.selectedFunctionName || "GROUP").toUpperCase();
    },
    reviewSubtitle() {
      const count = this.reviewCases.length;
      return `${this.selectedFolderName || "Folder"} · ${count} test case${count === 1 ? "" : "s"}`;
    },
    reviewFocusCase() {
      return this.reviewCases.find((tc) => tc.testCaseId === this.reviewFocusId) || null;
    },
    reviewStatusSelectValue() {
      const tc = this.reviewFocusCase;
      if (!tc) return "";
      if (this.reviewFeedbackId === tc.testCaseId) return "FeedBack";
      return tc.state || "";
    },
    filteredReviewCases() {
      const scenario = String(this.reviewFilters.scenario || "");
      const subScenario = String(this.reviewFilters.subScenario || "");
      const status = String(this.reviewFilters.status || "").trim().toLowerCase();
      const testType = String(this.reviewFilters.testType || "").trim().toLowerCase();
      const priority = String(this.reviewFilters.priority || "").trim().toLowerCase();
      const q = String(this.reviewFilters.search || "").trim().toLowerCase();
      return this.reviewCases.filter((tc) => {
        if (scenario && String(tc.scenarioName || tc.scenario || "") !== scenario) return false;
        if (subScenario && String(tc.subScenarioName || tc.subScenario || "") !== subScenario) return false;
        if (status && String(tc.state || "").trim().toLowerCase() !== status) return false;
        if (testType && String(tc.testType || "").trim().toLowerCase() !== testType) return false;
        if (priority && String(tc.priority || "").trim().toLowerCase() !== priority) return false;
        if (!q) return true;
        const hay = [
          this.formatTcLabel(tc),
          tc.title,
          tc.functionName || tc.function,
          tc.scenarioName || tc.scenario,
          tc.subScenarioName || tc.subScenario,
          tc.state,
          tc.priority,
          tc.testType,
          tc.preconditions,
          tc.testData,
        ].join(" ").toLowerCase();
        return hay.includes(q);
      });
    },
    reviewScenarioFilterOptions() {
      const values = [...new Set(
        this.reviewCases.map((tc) => String(tc.scenarioName || tc.scenario || "").trim()).filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));
      return [{ label: "All Scenario", value: "" }, ...values.map((value) => ({ label: value, value }))];
    },
    reviewSubScenarioFilterOptions() {
      const scenario = String(this.reviewFilters.scenario || "");
      const values = [...new Set(
        this.reviewCases
          .filter((tc) => !scenario || String(tc.scenarioName || tc.scenario || "") === scenario)
          .map((tc) => String(tc.subScenarioName || tc.subScenario || "").trim())
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));
      return [{ label: "All Sub-Scenario", value: "" }, ...values.map((value) => ({ label: value, value }))];
    },
    reviewStatusFilterOptions() {
      return [
        { label: "All Status", value: "" },
        ...["Draft", "Waiting Review", "In Review", "FeedBack", "Active"].map((value) => ({ label: value, value })),
      ];
    },
    reviewFocusLabel() {
      const tc = this.reviewFocusCase;
      if (!tc) return "Select a test case to comment";
      return `${this.formatTcLabel(tc)} · ${tc.title || "Untitled"}`;
    },
    reviewFeedbackCase() {
      return this.reviewCases.find((tc) => tc.testCaseId === this.reviewFeedbackId) || this.reviewFocusCase;
    },
    reviewGroupedCases() {
      const functionMap = new Map();
      for (const tc of this.reviewCases) {
        const functionKey = this.caseFunctionName(tc) || "Uncategorized";
        if (!functionMap.has(functionKey)) {
          functionMap.set(functionKey, { function: functionKey, scenarioGroups: new Map() });
        }
        const fnGroup = functionMap.get(functionKey);
        const scenarioKey = this.caseScenarioName(tc) || "Uncategorized";
        if (!fnGroup.scenarioGroups.has(scenarioKey)) {
          fnGroup.scenarioGroups.set(scenarioKey, { scenario: scenarioKey, subGroups: new Map() });
        }
        const scGroup = fnGroup.scenarioGroups.get(scenarioKey);
        const subKey = this.caseSubScenarioName(tc) || "Uncategorized";
        if (!scGroup.subGroups.has(subKey)) {
          scGroup.subGroups.set(subKey, { subScenario: subKey, cases: [] });
        }
        scGroup.subGroups.get(subKey).cases.push(tc);
      }
      return [...functionMap.values()].map((fnGroup) => ({
        function: fnGroup.function,
        scenarioGroups: [...fnGroup.scenarioGroups.values()].map((scGroup) => ({
          scenario: scGroup.scenario,
          subGroups: [...scGroup.subGroups.values()],
        })),
      }));
    },
    reviewPdfGroupedCases() {
      const scenarioMap = new Map();
      for (const tc of this.filteredReviewCases) {
        const scenarioKey = this.caseScenarioName(tc);
        if (!scenarioMap.has(scenarioKey)) {
          scenarioMap.set(scenarioKey, { scenario: scenarioKey, subGroups: new Map() });
        }
        const scGroup = scenarioMap.get(scenarioKey);
        const subKey = this.caseSubScenarioName(tc);
        if (!scGroup.subGroups.has(subKey)) {
          scGroup.subGroups.set(subKey, { subScenario: subKey, cases: [] });
        }
        scGroup.subGroups.get(subKey).cases.push(tc);
      }
      return [...scenarioMap.values()].map((scGroup) => ({
        scenario: scGroup.scenario,
        subGroups: [...scGroup.subGroups.values()],
      }));
    },
  },
  watch: {
    routeKey() {
      this.searchText = "";
      this.projectFilter = "all";
      this.resetCaseFilters();
      this.collapsedFunctions = [];
      this.collapsedScenarios = [];
      this.collapsedSubScenarios = [];
      this.closeFolderModal();
      this.exitSelectMode();
      this.closeReviewView();
      this.load();
    },
    showReviewView() {
      this.syncPdfStickyHeaders();
    },
    reviewViewMode() {
      this.syncPdfStickyHeaders();
    },
    reviewPdfGroupedCases() {
      this.syncPdfStickyHeaders();
    },
    "form.scenarioId"(scenarioId) {
      if (this.copyHydrating) return;
      // Keep the saved sub-scenario when the edit form is (re)loaded on its original scenario.
      const isOriginal = this.editMode && String(scenarioId || "") === this.editInitialScenarioId;
      this.form.subScenarioId = isOriginal ? this.editInitialSubScenarioId : "";
      this.form.subScenarioIsNew = false;
      if (/^\d+$/.test(String(scenarioId || ""))) {
        this.loadSubScenarios(scenarioId);
      } else {
        this.subScenarioOptions = [];
      }
    },
  },
  created() {
    this.load();
  },
  mounted() {
    document.addEventListener("click", this.closeActionMenus);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeActionMenus);
    this.unbindPdfStickyHeaders();
    this.closeReviewView();
  },
  methods: {
    async load() {
      const token = ++this.loadToken;
      const stillCurrent = () => token === this.loadToken;
      this.loading = true;
      this.error = "";
      try {
        if (this.view === "projects" || !this.projects.length) {
          const projects = await realTestCasesApi.listProjects();
          if (!stillCurrent()) return;
          this.projects = projects;
        }
        if (!stillCurrent()) return;
        if (this.view === "functions") {
          const functions = await realTestCasesApi.listFunctions(this.selectedProjectId);
          if (!stillCurrent()) return;
          this.functions = Array.isArray(functions) ? functions : [];
          this.loadedProjectId = this.selectedProjectId;
          this.folders = [];
          this.cases = [];
        } else if (this.view === "folders") {
          await this.ensureFunctionsLoaded();
          if (!stillCurrent()) return;
          const folders = await this.loadFoldersForSelection();
          if (!stillCurrent()) return;
          this.folders = folders;
          this.loadedSubProjectId = this.selectedSubProjectId;
          this.loadedFunctionId = this.selectedFunctionId;
          this.cases = [];
        } else if (this.view === "cases") {
          await this.ensureFunctionsLoaded();
          if (!stillCurrent()) return;
          if (
            this.loadedSubProjectId !== this.selectedSubProjectId
            || this.loadedFunctionId !== this.selectedFunctionId
          ) {
            const folders = await this.loadFoldersForSelection();
            if (!stillCurrent()) return;
            this.folders = folders;
            this.loadedSubProjectId = this.selectedSubProjectId;
            this.loadedFunctionId = this.selectedFunctionId;
          }
          const rows = await this.loadCasesForSelection();
          if (!stillCurrent()) return;
          this.cases = (Array.isArray(rows) ? rows : []).map((tc) => this.normalizeCaseHierarchy(tc));
        } else {
          this.functions = [];
          this.folders = [];
          this.cases = [];
          this.loadedProjectId = "";
          this.loadedSubProjectId = "";
          this.loadedFunctionId = "";
        }
      } catch (e) {
        if (!stillCurrent()) return;
        console.error("load QA test cases error:", e);
        this.error = e.message || "Failed to load test cases";
        if (this.view === "projects") this.projects = [];
        if (this.view === "functions") this.functions = [];
        if (this.view === "folders") this.folders = [];
        this.cases = [];
      } finally {
        if (stillCurrent()) this.loading = false;
      }
    },
    openProject(project) {
      this.$router.push({
        name: "QA Test Case",
        query: { project: String(project.projectId) },
      });
    },
    backToProjects() {
      this.$router.push({ name: "QA Test Case" });
    },
    openFunction(item) {
      this.$router.push({
        name: "QA Test Case",
        query: {
          project: String(item.projectId || this.selectedProjectId),
          function: String(item.functionId),
        },
      });
    },
    openFolder(item) {
      this.$router.push({
        name: "QA Test Case",
        query: {
          project: String(item.projectId || this.selectedProjectId),
          function: this.selectedFunctionId,
          folder: String(item.folderId),
        },
      });
    },
    async ensureFunctionsLoaded() {
      if (this.loadedProjectId === this.selectedProjectId && this.functions.length) return;
      this.functions = await realTestCasesApi.listFunctions(this.selectedProjectId);
      this.loadedProjectId = this.selectedProjectId;
    },
    uniqueGroupsByName(list) {
      const byId = new Map();
      for (const fn of list || []) {
        const id = String(fn.functionId ?? "");
        if (id && byId.has(id)) continue;
        byId.set(id || `row-${byId.size}`, {
          ...fn,
          folderCount: Number(fn.folderCount || 0),
          testCaseCount: Number(fn.testCaseCount || 0),
        });
      }
      const grouped = new Map();
      for (const fn of byId.values()) {
        const virtual = this.isVirtualFunction(fn);
        const nameKey = String(fn.functionName || "").trim().toLowerCase() || "(blank)";
        const key = `${virtual ? "virtual" : "real"}:${nameKey}`;
        const prev = grouped.get(key);
        if (!prev) {
          grouped.set(key, { ...fn });
          continue;
        }
        prev.folderCount += Number(fn.folderCount || 0);
        prev.testCaseCount += Number(fn.testCaseCount || 0);
        if (!virtual && /^\d+$/.test(String(fn.functionId || "")) && !/^\d+$/.test(String(prev.functionId || ""))) {
          prev.functionId = fn.functionId;
          if (fn.subProjectId) prev.subProjectId = fn.subProjectId;
        }
      }
      return [...grouped.values()].sort((a, b) =>
        String(a.functionName || "").localeCompare(String(b.functionName || ""), undefined, { sensitivity: "base" }),
      );
    },
    sameNamedFunctionsFromList(functionId, list) {
      const rows = list || [];
      const current = rows.find((f) => String(f.functionId) === String(functionId || ""));
      const key = String(current?.functionName || "").trim().toLowerCase();
      if (!key) return current ? [current] : [];
      const virtual = this.isVirtualFunction(current);
      const matches = rows.filter((f) => (
        this.isVirtualFunction(f) === virtual
        && String(f.functionName || "").trim().toLowerCase() === key
      ));
      return matches.length ? matches : [current];
    },
    sameNamedFunctions(functionId = this.selectedFunctionId) {
      return this.sameNamedFunctionsFromList(functionId, this.functions);
    },
    async loadFoldersForSelection() {
      const targets = this.sameNamedFunctions();
      if (!targets.length && this.selectedSubProjectId) {
        const rows = await realTestCasesApi.listFolders(this.selectedSubProjectId, this.selectedFunctionId);
        return Array.isArray(rows) ? rows : [];
      }
      const lists = await Promise.all(targets.map((fn) => (
        fn.subProjectId
          ? realTestCasesApi.listFolders(fn.subProjectId, fn.functionId).catch(() => [])
          : Promise.resolve([])
      )));
      const seen = new Set();
      const folders = [];
      for (const rows of lists) {
        for (const row of (Array.isArray(rows) ? rows : [])) {
          const id = `${row.folderId}::${row.subProjectId || ""}`;
          if (seen.has(id)) continue;
          seen.add(id);
          folders.push(row);
        }
      }
      return folders;
    },
    async loadCasesForSelection() {
      const targets = this.sameNamedFunctions();
      const requests = (targets.length ? targets : [{
        subProjectId: this.selectedSubProjectId,
        functionId: this.selectedFunctionId,
      }]).map((fn) => realTestCasesApi.getByProject(this.selectedProjectId, {
        subProjectId: fn.subProjectId || undefined,
        functionId: fn.functionId || this.selectedFunctionId || undefined,
        folderId: this.selectedFolderId,
      }).catch(() => []));
      const lists = await Promise.all(requests);
      const seen = new Set();
      const rows = [];
      for (const list of lists) {
        for (const tc of (Array.isArray(list) ? list : [])) {
          const id = tc.testCaseId;
          if (id == null || seen.has(id)) continue;
          seen.add(id);
          rows.push(tc);
        }
      }
      return rows;
    },
    canDeleteFolder(item) {
      return !item.virtual && Number(item.testCaseCount || 0) === 0;
    },
    folderDeleteTitle(item) {
      if (item.virtual) return "Cannot delete this folder";
      if (Number(item.testCaseCount || 0) > 0) return "Cannot delete a folder that still has test cases";
      return "Delete";
    },
    startEditFolder(item) {
      this.editingFolderId = item.folderId;
      this.editingFolderVirtual = !!item.virtual;
      this.folderFormName = item.folderName || "";
      this.folderFormState = item.state || "Draft";
      this.folderFormInitialState = item.state || "Draft";
      this.folderFormComment = "";
      this.folderTab = "details";
      this.folderHistory = [];
      this.folderSaveError = "";
      this.showFolderModal = true;
      this.loadFolderHistory();
      this.$nextTick(() => {
        this.$refs.folderNameInput?.focus();
        this.$refs.folderNameInput?.select();
      });
    },
    isVirtualFunction(item) {
      return !!item?.virtual || /^uncategorized(?::|$)/i.test(String(item?.functionId || "").trim());
    },
    canDeleteFunction(item) {
      if (this.isVirtualFunction(item)) return false;
      return Number(item.testCaseCount || 0) === 0;
    },
    functionDeleteTitle(item) {
      if (this.isVirtualFunction(item)) return "Cannot delete Uncategorized";
      if (Number(item.testCaseCount || 0) > 0) return "Cannot delete a group that still has test cases";
      return "Delete";
    },
    openCreateFunction() {
      this.editingFunctionId = null;
      this.functionFormName = "";
      this.functionSaveError = "";
      this.showFunctionModal = true;
      this.$nextTick(() => {
        this.$refs.functionNameInput?.focus();
      });
    },
    startEditFunction(item) {
      if (this.isVirtualFunction(item)) return;
      this.editingFunctionId = item.functionId;
      this.functionFormName = item.functionName || "";
      this.functionSaveError = "";
      this.showFunctionModal = true;
      this.$nextTick(() => {
        this.$refs.functionNameInput?.focus();
        this.$refs.functionNameInput?.select();
      });
    },
    closeFunctionModal() {
      this.showFunctionModal = false;
      this.editingFunctionId = null;
      this.functionFormName = "";
      this.functionSaveError = "";
    },
    async saveFunctionModal() {
      const name = String(this.functionFormName || "").trim();
      if (!name || this.functionSaving) {
        if (!name) this.functionSaveError = "Group name is required";
        return;
      }
      this.functionSaving = true;
      this.functionSaveError = "";
      try {
        if (this.editingFunctionId) {
          if (this.isVirtualFunction({ functionId: this.editingFunctionId })) {
            this.functionSaveError = "Cannot rename Uncategorized";
            return;
          }
          const targets = this.sameNamedFunctions(this.editingFunctionId)
            .filter((fn) => /^\d+$/.test(String(fn.functionId || "")));
          if (!targets.length) {
            this.functionSaveError = "Group not found";
            return;
          }
          for (const fn of targets) {
            await testFunctionsApi.update(fn.functionId, { functionName: name });
          }
        } else {
          await testFunctionsApi.insert({
            projectId: this.selectedProjectId,
            functionName: name,
          });
        }
        this.functions = await realTestCasesApi.listFunctions(this.selectedProjectId);
        this.closeFunctionModal();
      } catch (e) {
        this.functionSaveError = e.message || (this.editingFunctionId ? "Failed to update group" : "Failed to create group");
      } finally {
        this.functionSaving = false;
      }
    },
    deleteFunction(item) {
      if (!this.canDeleteFunction(item)) return;
      this.functionDeleteTarget = item;
      this.functionDeleteError = "";
      this.showFunctionDeleteConfirm = true;
    },
    closeFunctionDeleteConfirm() {
      this.showFunctionDeleteConfirm = false;
      this.functionDeleteTarget = null;
      this.functionDeleting = false;
      this.functionDeleteError = "";
    },
    async confirmDeleteFunction() {
      const item = this.functionDeleteTarget;
      if (!item || this.functionDeleting) return;
      this.functionDeleting = true;
      this.functionDeleteError = "";
      try {
        await testFunctionsApi.delete(item.functionId);
        this.functions = await realTestCasesApi.listFunctions(this.selectedProjectId);
        this.closeFunctionDeleteConfirm();
      } catch (e) {
        this.functionDeleteError = e.message || "Failed to delete group";
        this.functionDeleting = false;
      }
    },
    openCreateFolder() {
      this.editingFolderId = null;
      this.editingFolderVirtual = false;
      this.folderFormName = "";
      this.folderFormState = "Draft";
      this.folderFormInitialState = "Draft";
      this.folderFormComment = "";
      this.folderTab = "details";
      this.folderHistory = [];
      this.folderSaveError = "";
      this.showFolderModal = true;
      this.$nextTick(() => {
        this.$refs.folderNameInput?.focus();
      });
    },
    closeFolderModal() {
      this.showFolderModal = false;
      this.folderFormName = "";
      this.folderFormState = "Draft";
      this.folderFormInitialState = "Draft";
      this.folderFormComment = "";
      this.folderSaveError = "";
      this.editingFolderId = null;
      this.editingFolderVirtual = false;
      this.folderTab = "details";
      this.folderHistory = [];
    },
    async loadFolderHistory() {
      if (!this.editingFolderId) return;
      this.folderHistoryLoading = true;
      try {
        this.folderHistory = await realTestCasesApi.getFolderActivity(
          this.editingFolderId,
          this.selectedSubProjectId,
        );
      } catch (e) {
        this.folderHistory = [];
        this.folderSaveError = e.message || "Failed to load folder history";
      } finally {
        this.folderHistoryLoading = false;
      }
    },
    openFolderHistoryTab() {
      this.folderTab = "history";
      if (!this.folderHistory.length) this.loadFolderHistory();
    },
    folderHistoryBody(entry) {
      if (entry.entryType === "remark" || entry.entryType === "feedback") {
        return entry.remarkText || entry.newValue || "—";
      }
      if (entry.fieldName) return `${entry.fieldName}: ${entry.oldValue || "—"} → ${entry.newValue || "—"}`;
      return entry.remarkText || "—";
    },
    async saveFolderModal() {
      const name = String(this.folderFormName || "").trim();
      const comment = String(this.folderFormComment || "").trim();
      if (!name || this.folderSaving) {
        if (!name) this.folderSaveError = "Folder name is required";
        return;
      }
      if (
        this.editingFolderId
        && this.folderFormState === "FeedBack"
        && this.folderFormInitialState !== "FeedBack"
        && !comment
      ) {
        this.folderSaveError = "Comment is required when setting folder status to FeedBack";
        return;
      }
      this.folderSaving = true;
      this.folderSaveError = "";
      try {
        if (this.editingFolderId) {
          await realTestCasesApi.updateFolder(this.editingFolderId, {
            folderName: name,
            state: this.folderFormState,
            comment,
            subProjectId: this.selectedSubProjectId,
            modifyBy: this.currentUserId,
          });
        } else {
          await realTestCasesApi.createFolder({
            subProjectId: this.selectedSubProjectId,
            functionId: this.selectedFunctionId || undefined,
            folderName: name,
          });
        }
        this.closeFolderModal();
        this.folders = await this.loadFoldersForSelection();
      } catch (e) {
        this.folderSaveError = e.message || (this.editingFolderId ? "Failed to update folder" : "Failed to create folder");
      } finally {
        this.folderSaving = false;
      }
    },
    async deleteFolder(item) {
      if (!this.canDeleteFolder(item)) return;
      if (!window.confirm(`Delete folder "${item.folderName}"?`)) return;
      try {
        await realTestCasesApi.deleteFolder(item.folderId);
        this.folders = await this.loadFoldersForSelection();
      } catch (e) {
        window.alert(e.message || "Failed to delete folder");
      }
    },
    goBack() {
      if (this.view === "cases") {
        this.$router.push({
          name: "QA Test Case",
          query: {
            project: this.selectedProjectId,
            function: this.selectedFunctionId,
          },
        });
        return;
      }
      if (this.view === "folders") {
        this.$router.push({
          name: "QA Test Case",
          query: { project: this.selectedProjectId },
        });
        return;
      }
      this.backToProjects();
    },
    formatTcLabel(tc) {
      const n = tc?.testCaseNumber || tc?.testCaseId || 0;
      return `TC-${String(n).padStart(3, "0")}`;
    },
    functionCaseCount(fnGroup) {
      return fnGroup.scenarioGroups.reduce((sum, sc) => sum + this.scenarioCaseCount(sc), 0);
    },
    isFunctionCollapsed(fn) {
      return this.collapsedFunctions.includes(fn);
    },
    toggleFunction(fn) {
      const idx = this.collapsedFunctions.indexOf(fn);
      if (idx === -1) this.collapsedFunctions.push(fn);
      else this.collapsedFunctions.splice(idx, 1);
    },
    scenarioCaseCount(scGroup) {
      return scGroup.subGroups.reduce((sum, sub) => sum + sub.cases.length, 0);
    },
    scenarioCollapseKey(fn, scenario) {
      return `${fn}|${scenario}`;
    },
    isScenarioCollapsed(fn, scenario) {
      return this.collapsedScenarios.includes(this.scenarioCollapseKey(fn, scenario));
    },
    toggleScenario(fn, scenario) {
      const key = this.scenarioCollapseKey(fn, scenario);
      const idx = this.collapsedScenarios.indexOf(key);
      if (idx === -1) this.collapsedScenarios.push(key);
      else this.collapsedScenarios.splice(idx, 1);
    },
    subScenarioCollapseKey(fn, scenario, subScenario) {
      return `${fn}|${scenario}|${subScenario}`;
    },
    isSubScenarioCollapsed(fn, scenario, subScenario) {
      return this.collapsedSubScenarios.includes(this.subScenarioCollapseKey(fn, scenario, subScenario));
    },
    toggleSubScenario(fn, scenario, subScenario) {
      const key = this.subScenarioCollapseKey(fn, scenario, subScenario);
      const idx = this.collapsedSubScenarios.indexOf(key);
      if (idx === -1) this.collapsedSubScenarios.push(key);
      else this.collapsedSubScenarios.splice(idx, 1);
    },
    resetFormPickers() {
      this.formError = "";
      this.formFieldErrors = { function: "", scenario: "", subScenario: "", title: "" };
      this.functionOptions = [];
      this.scenarioOptions = [];
      this.allScenarios = [];
      this.subScenarioOptions = [];
      this.requirementIdInput = "";
      this.jiraProjectKey = "";
      this.jiraIssues = [];
      this.jiraSearch = "";
    },
    async openCreateModal() {
      this.editMode = false;
      this.formCopyMode = false;
      this.copyHydrating = false;
      this.copyPicker = emptyCopyPicker();
      this.copySourceCase = null;
      this.editingFromDetail = false;
      this.editingTc = null;
      this.editInitialScenarioId = "";
      this.editInitialSubScenarioId = "";
      this.editInitialScenarioName = "";
      this.editInitialSubScenarioName = "";
      this.form = emptyCreateForm();
      this.form.functionId = this.selectedFunctionId || "";
      this.form.ownerId = this.currentUserId != null ? String(this.currentUserId) : "";
      this.resetFormPickers();
      this.showDetailModal = false;
      this.showCreateModal = true;
      this.loadUsers();
      await this.loadFunctionOptions();
      this.loadScenarios();
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.showRequirementModal = false;
      this.formCopyMode = false;
      this.copySourceCase = null;
      this.copyPicker = emptyCopyPicker();
      this.showCopyFieldChoiceModal = false;
      this.copyFieldConflicts = [];
      this.pendingCopySteps = [];
      this.pendingCopyIncoming = null;
      this.closeCopyStepModal();
      this.closeEditDeleteConfirm();
      this.editMode = false;
      this.editingFromDetail = false;
      this.editingTc = null;
    },
    async openCopyFromToolbar() {
      await this.openCreateModal();
      this.formCopyMode = true;
      this.copyApplyMode = "case";
      this.copyPicker = emptyCopyPicker();
      this.copySourceCase = null;
      await this.loadCopyFunctionsForProject();
    },
    async openCopyStepModal() {
      if (this.copyFieldsLocked) return;
      this.copyStepPicker = emptyCopyStepPicker();
      this.copyStepSource = null;
      this.copyStepLoading = false;
      this.copyStepFolders = [];
      this.copyStepCases = [];
      this.showCopyStepModal = true;
      await this.loadCopyStepFunctionsForProject();
    },
    closeCopyStepModal() {
      this.showCopyStepModal = false;
      this.copyStepPicker = emptyCopyStepPicker();
      this.copyStepSource = null;
      this.copyStepLoading = false;
      this.copyStepFolders = [];
      this.copyStepCases = [];
    },
    async loadCopyStepFunctionsForProject() {
      this.copyStepFunctions = [];
      if (!this.selectedProjectId) return;
      this.copyStepFunctionsLoading = true;
      try {
        const rows = await realTestCasesApi.listFunctions(this.selectedProjectId);
        this.copyStepFunctions = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error("load copy step functions error:", e);
        this.copyStepFunctions = [];
      } finally {
        this.copyStepFunctionsLoading = false;
      }
    },
    async onCopyStepFunctionChange(value) {
      this.copyStepPicker.functionId = value ? String(value) : "";
      this.copyStepPicker.folderKey = "";
      this.copyStepPicker.scenarioId = "";
      this.copyStepPicker.subScenarioId = "";
      this.copyStepPicker.testCaseId = "";
      this.copyStepPicker.selectedIndexes = [];
      this.copyStepSource = null;
      this.copyStepFolders = [];
      this.copyStepCases = [];
      if (!this.copyStepPicker.functionId) return;
      this.copyStepFoldersLoading = true;
      try {
        this.copyStepFolders = await this.loadCopyFoldersForFunction(
          this.copyStepPicker.functionId,
          this.copyStepFunctions,
        );
      } catch (e) {
        console.error("load copy step folders error:", e);
        this.copyStepFolders = [];
      } finally {
        this.copyStepFoldersLoading = false;
      }
    },
    async onCopyStepFolderChange(value) {
      this.copyStepPicker.folderKey = value ? String(value) : "";
      this.copyStepPicker.scenarioId = "";
      this.copyStepPicker.subScenarioId = "";
      this.copyStepPicker.testCaseId = "";
      this.copyStepPicker.selectedIndexes = [];
      this.copyStepSource = null;
      this.copyStepCases = [];
      const selected = (this.copyStepFolders || []).find(
        (folder) => folder.folderKey === this.copyStepPicker.folderKey,
      );
      if (!this.selectedProjectId || !selected) return;
      this.copyStepFoldersLoading = true;
      try {
        const rows = await realTestCasesApi.getByProject(this.selectedProjectId, {
          subProjectId: selected.subProjectId || undefined,
          folderId: selected.folderId,
        });
        this.copyStepCases = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error("load copy step cases error:", e);
        this.copyStepCases = [];
      } finally {
        this.copyStepFoldersLoading = false;
      }
    },
    onCopyStepScenarioChange(value) {
      this.copyStepPicker.scenarioId = value ? String(value) : "";
      this.copyStepPicker.subScenarioId = "";
      this.copyStepPicker.testCaseId = "";
      this.copyStepPicker.selectedIndexes = [];
      this.copyStepSource = null;
    },
    onCopyStepSubScenarioChange(value) {
      this.copyStepPicker.subScenarioId = value ? String(value) : "";
      this.copyStepPicker.testCaseId = "";
      this.copyStepPicker.selectedIndexes = [];
      this.copyStepSource = null;
    },
    async onCopyStepTitleChange(value) {
      this.copyStepPicker.testCaseId = value ? String(value) : "";
      this.copyStepPicker.selectedIndexes = [];
      this.copyStepSource = null;
      if (!this.copyStepPicker.testCaseId) return;
      this.copyStepLoading = true;
      try {
        this.copyStepSource = await realTestCasesApi.getById(this.copyStepPicker.testCaseId);
      } catch (e) {
        console.error("load copy steps error:", e);
        this.copyStepSource = null;
      } finally {
        this.copyStepLoading = false;
      }
    },
    toggleCopyStepModalStep(idx) {
      const selected = [...this.copyStepPicker.selectedIndexes];
      const pos = selected.indexOf(idx);
      if (pos === -1) selected.push(idx);
      else selected.splice(pos, 1);
      selected.sort((a, b) => a - b);
      this.copyStepPicker.selectedIndexes = selected;
    },
    selectAllCopySteps() {
      this.copyStepPicker.selectedIndexes = this.copyStepSourceSteps.map((_, idx) => idx);
    },
    applyCopiedStepsFromModal() {
      const selected = this.copyStepSourceSteps
        .filter((_, idx) => this.copyStepPicker.selectedIndexes.includes(idx))
        .map((s) => normalizeFormStep(s));
      if (!selected.length) return;
      this.appendCopiedSteps(selected);
      this.closeCopyStepModal();
    },
    async loadCopyFunctionsForProject() {
      this.copyFunctions = [];
      this.copyFolders = [];
      this.copyCases = [];
      if (!this.selectedProjectId) return;
      this.copyFunctionsLoading = true;
      try {
        const rows = await realTestCasesApi.listFunctions(this.selectedProjectId);
        this.copyFunctions = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error("load copy functions error:", e);
        this.copyFunctions = [];
        this.formError = e.message || "Failed to load functions.";
      } finally {
        this.copyFunctionsLoading = false;
      }
    },
    folderOptionsFromLists(lists, fallbackSubProjectId = "") {
      const folders = [];
      const seen = new Set();
      lists.forEach((rows) => {
        (Array.isArray(rows) ? rows : []).forEach((folder) => {
          const folderId = String(folder.folderId || "uncategorized");
          const subProjectId = String(folder.subProjectId || fallbackSubProjectId || "");
          const folderKey = `${subProjectId}:${folderId}`;
          if (seen.has(folderKey)) return;
          seen.add(folderKey);
          const folderName = folder.folderName || "Folder";
          const context = folder.subProjectName || "";
          folders.push({
            folderKey,
            folderId,
            subProjectId,
            folderName,
            label: context && context !== folderName ? `${folderName} · ${context}` : folderName,
          });
        });
      });
      folders.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
      return folders;
    },
    async loadCopyFoldersForFunction(functionId, functionList) {
      const targets = this.sameNamedFunctionsFromList(functionId, functionList);
      if (!targets.length) return [];
      const lists = await Promise.all(targets.map((fn) => (
        fn.subProjectId
          ? realTestCasesApi.listFolders(fn.subProjectId, fn.functionId).catch(() => [])
          : Promise.resolve([])
      )));
      return this.folderOptionsFromLists(lists, targets[0]?.subProjectId);
    },
    async onCopyFunctionSelect(value) {
      this.copyPicker.functionId = value ? String(value) : "";
      this.copyPicker.folderKey = "";
      this.copyPicker.testCaseId = "";
      this.copyPicker.stepMode = "all";
      this.copyPicker.selectedStepIndexes = [];
      this.copySourceCase = null;
      this.copyFolders = [];
      this.copyCases = [];
      this.formError = "";
      if (!this.copyPicker.functionId) return;
      this.copyFoldersLoading = true;
      try {
        this.copyFolders = await this.loadCopyFoldersForFunction(
          this.copyPicker.functionId,
          this.copyFunctions,
        );
      } catch (e) {
        console.error("load copy folders error:", e);
        this.copyFolders = [];
        this.formError = e.message || "Failed to load folders.";
      } finally {
        this.copyFoldersLoading = false;
      }
    },
    async loadCopyCasesForFolder(folderKey) {
      this.copyCases = [];
      const selected = (this.copyFolders || []).find((folder) => folder.folderKey === String(folderKey || ""));
      if (!this.selectedProjectId || !selected) return;
      this.copyCasesLoading = true;
      try {
        const rows = await realTestCasesApi.getByProject(this.selectedProjectId, {
          subProjectId: selected.subProjectId || undefined,
          folderId: selected.folderId,
        });
        this.copyCases = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error("load copy cases error:", e);
        this.copyCases = [];
        this.formError = e.message || "Failed to load test cases.";
      } finally {
        this.copyCasesLoading = false;
      }
    },
    async onCopyFolderSelect(value) {
      this.copyPicker.folderKey = value ? String(value) : "";
      this.copyPicker.testCaseId = "";
      this.copyPicker.stepMode = "all";
      this.copyPicker.selectedStepIndexes = [];
      this.copySourceCase = null;
      this.formError = "";
      await this.loadCopyCasesForFolder(this.copyPicker.folderKey);
    },
    async onCopySourceSelect(value) {
      this.copyPicker.testCaseId = value ? String(value) : "";
      this.copySourceCase = null;
      this.copyApplyMode = "case";
      this.copyPicker.stepMode = "all";
      this.copyPicker.selectedStepIndexes = [];
      this.formError = "";
      if (!this.copyPicker.testCaseId) return;
      this.copyStepsLoading = true;
      try {
        const tc = await realTestCasesApi.getById(this.copyPicker.testCaseId);
        this.copySourceCase = tc || null;
        const count = Array.isArray(tc?.steps) ? tc.steps.length : 0;
        this.copyPicker.selectedStepIndexes = Array.from({ length: count }, (_, i) => i);
        if (this.copySourceCase) await this.applyCopyIntoForm();
      } catch (e) {
        console.error("load copy source error:", e);
        this.formError = e.message || "Failed to load test case.";
        this.copySourceCase = null;
      } finally {
        this.copyStepsLoading = false;
      }
    },
    stepPreview(text) {
      const value = String(text || "").replace(/\s+/g, " ").trim();
      if (!value) return "";
      return value.length > 120 ? `${value.slice(0, 117)}…` : value;
    },
    toggleCopyStep(idx) {
      const selected = [...this.copyPicker.selectedStepIndexes];
      const pos = selected.indexOf(idx);
      if (pos === -1) selected.push(idx);
      else selected.splice(pos, 1);
      selected.sort((a, b) => a - b);
      this.copyPicker.selectedStepIndexes = selected;
    },
    formHasFilledSteps() {
      return (this.form.steps || []).some((s) => String(s.platform || "").trim() || String(s.stepDetail || "").trim() || String(s.expectedResult || "").trim());
    },
    optionLabel(options, value) {
      const found = (options || []).find((o) => String(o.value) === String(value || ""));
      return found?.label || "";
    },
    incomingCopyFields(tc) {
      return {
        functionId: tc.functionId ? String(tc.functionId) : "",
        scenarioId: tc.scenarioId ? String(tc.scenarioId) : "",
        subScenarioId: tc.subScenarioId ? String(tc.subScenarioId) : "",
        preconditions: tc.preconditions || "",
        testData: tc.testData || "",
        title: tc.title || "",
        description: tc.description || "",
        requirementIds: tc.requirementId
          ? String(tc.requirementId).split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        priority: tc.priority || "",
        typeOfTestCase: tc.typeOfTestCase || "",
        automationStatus: tc.automationStatus || "",
        testType: tc.testType || "",
        executionType: tc.executionType || "",
        functionLabel: tc.function || tc.functionName || this.optionLabel(this.functionOptions, tc.functionId),
        scenarioLabel: tc.scenario || tc.scenarioName || this.optionLabel(this.scenarioOptions, tc.scenarioId),
        subScenarioLabel: tc.subScenario || tc.subScenarioName || this.optionLabel(this.subScenarioOptions, tc.subScenarioId),
      };
    },
    selectedCopySteps() {
      const all = this.copySourceSteps;
      if (this.copyPicker.stepMode === "selected") {
        return all.filter((_, idx) => this.copyPicker.selectedStepIndexes.includes(idx)).map((s) => normalizeFormStep(s));
      }
      return all.map((s) => normalizeFormStep(s));
    },
    appendCopiedSteps(steps) {
      const incoming = (steps || []).map((s) => normalizeFormStep(s));
      if (!incoming.length) return;
      if (!this.formHasFilledSteps()) {
        this.form.steps = incoming;
        return;
      }
      this.form.steps = [...this.form.steps, ...incoming];
    },
    async applyCopyIntoForm() {
      const tc = this.copySourceCase;
      if (!tc) return;
      const steps = this.selectedCopySteps();
      if (this.copyPicker.stepMode === "selected" && !steps.length) {
        this.formError = "Select at least one step.";
        return;
      }
      this.formError = "";
      if (this.copyApplyMode === "steps") {
        this.appendCopiedSteps(steps);
        return;
      }
      const incoming = this.incomingCopyFields(tc);
      await this.loadFunctionOptions();
      if (this.form.functionId) await this.loadScenarios();
      if (this.form.scenarioId) await this.loadSubScenarios(this.form.scenarioId);
      const conflicts = [];
      const specs = [
        {
          key: "function",
          label: "Function",
          originalValue: this.form.functionId,
          newValue: incoming.functionId,
          originalLabel: this.optionLabel(this.functionOptions, this.form.functionId) || this.form.functionId,
          newLabel: incoming.functionLabel || incoming.functionId,
        },
        {
          key: "scenario",
          label: "Scenario",
          originalValue: this.form.scenarioId,
          newValue: incoming.scenarioId,
          originalLabel: this.optionLabel(this.scenarioOptions, this.form.scenarioId) || this.form.scenarioId,
          newLabel: incoming.scenarioLabel || incoming.scenarioId,
        },
        {
          key: "subScenario",
          label: "Sub-Scenario",
          originalValue: this.form.subScenarioId,
          newValue: incoming.subScenarioId,
          originalLabel: this.optionLabel(this.subScenarioOptions, this.form.subScenarioId) || this.form.subScenarioId,
          newLabel: incoming.subScenarioLabel || incoming.subScenarioId,
        },
        {
          key: "preconditions",
          label: "Preconditions",
          originalValue: this.form.preconditions,
          newValue: incoming.preconditions,
          originalLabel: this.form.preconditions,
          newLabel: incoming.preconditions,
        },
        {
          key: "testData",
          label: "Test Data",
          originalValue: this.form.testData,
          newValue: incoming.testData,
          originalLabel: this.form.testData,
          newLabel: incoming.testData,
        },
      ];
      specs.forEach((spec) => {
        const originalFilled = String(spec.originalValue || "").trim();
        if (originalFilled) {
          conflicts.push({
            ...spec,
            originalLabel: String(spec.originalLabel || "").trim() || "—",
            newLabel: String(spec.newLabel || "").trim() || "—",
            choice: "original",
          });
        }
      });
      this.pendingCopyIncoming = incoming;
      this.pendingCopySteps = steps;
      if (conflicts.length) {
        this.copyFieldConflicts = conflicts;
        this.showCopyFieldChoiceModal = true;
        return;
      }
      await this.commitCopyIntoForm(incoming, steps, {});
    },
    closeCopyFieldChoiceModal() {
      this.showCopyFieldChoiceModal = false;
    },
    async confirmCopyFieldChoices() {
      const choices = {};
      (this.copyFieldConflicts || []).forEach((item) => {
        choices[item.key] = item.choice;
      });
      this.showCopyFieldChoiceModal = false;
      await this.commitCopyIntoForm(this.pendingCopyIncoming, this.pendingCopySteps, choices);
    },
    async commitCopyIntoForm(incoming, steps, choices = {}) {
      if (!incoming) {
        this.appendCopiedSteps(steps);
        return;
      }
      const useNew = (key) => {
        const originalMap = {
          function: this.form.functionId,
          scenario: this.form.scenarioId,
          subScenario: this.form.subScenarioId,
          preconditions: this.form.preconditions,
          testData: this.form.testData,
        };
        if (choices[key] === "new") return true;
        if (choices[key] === "original") return false;
        return !String(originalMap[key] || "").trim();
      };
      this.copyHydrating = true;
      if (useNew("function") && incoming.functionId) {
        this.form.functionId = incoming.functionId;
        this.form.functionIsNew = false;
        this.clearFormFieldError("function");
      }
      await this.loadScenarios();
      if (useNew("scenario") && incoming.scenarioId) {
        this.form.scenarioId = incoming.scenarioId;
        this.form.scenarioIsNew = false;
        this.editInitialScenarioId = incoming.scenarioId;
        this.editInitialScenarioName = incoming.scenarioLabel || "";
        this.clearFormFieldError("scenario");
        await this.loadSubScenarios(incoming.scenarioId);
      }
      if (useNew("subScenario") && incoming.subScenarioId) {
        this.form.subScenarioId = incoming.subScenarioId;
        this.form.subScenarioIsNew = false;
        this.editInitialSubScenarioId = incoming.subScenarioId;
        this.editInitialSubScenarioName = incoming.subScenarioLabel || "";
        this.clearFormFieldError("subScenario");
      }
      if (useNew("preconditions")) this.form.preconditions = incoming.preconditions || "";
      if (useNew("testData")) this.form.testData = incoming.testData || "";
      if (!String(this.form.title || "").trim() && incoming.title) this.form.title = incoming.title;
      if (!String(this.form.description || "").trim() && incoming.description) {
        this.form.description = incoming.description;
        this.form.showDescription = true;
      }
      if (!(this.form.requirementIds || []).length && incoming.requirementIds?.length) {
        this.form.requirementIds = [...incoming.requirementIds];
      }
      if (incoming.priority) this.form.priority = incoming.priority;
      if (incoming.typeOfTestCase) this.form.typeOfTestCase = incoming.typeOfTestCase;
      if (incoming.automationStatus) this.form.automationStatus = incoming.automationStatus;
      if (incoming.testType) this.form.testType = incoming.testType;
      if (incoming.executionType) this.form.executionType = incoming.executionType;
      this.copyHydrating = false;
      this.appendCopiedSteps(steps);
      this.checkTitleDuplicate();
      this.pendingCopyIncoming = null;
      this.pendingCopySteps = [];
    },
    onStepDragStart(event, idx) {
      this.dragStepIndex = idx;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", String(idx));
    },
    onStepDragOver(event, idx) {
      event.preventDefault();
      this.dragOverStepIndex = idx;
    },
    onStepDrop(event, idx) {
      event.preventDefault();
      const from = this.dragStepIndex;
      this.dragStepIndex = null;
      this.dragOverStepIndex = null;
      if (from == null || from === idx) return;
      const steps = [...this.form.steps];
      const [moved] = steps.splice(from, 1);
      steps.splice(idx, 0, moved);
      this.form.steps = steps;
    },
    onStepDragEnd() {
      this.dragStepIndex = null;
      this.dragOverStepIndex = null;
    },
    isTitleTakenInFolder(title, excludeTestCaseId = null) {
      const wanted = String(title || "").trim().toLowerCase();
      if (!wanted) return false;
      return (this.cases || []).some((tc) => {
        if (excludeTestCaseId && String(tc.testCaseId) === String(excludeTestCaseId)) return false;
        return String(tc.title || "").trim().toLowerCase() === wanted;
      });
    },
    checkTitleDuplicate() {
      if (this.editMode) return;
      if (this.isTitleTakenInFolder(this.form.title)) {
        this.formFieldErrors = { ...this.formFieldErrors, title: "This title already exists in this folder." };
      } else if (this.formFieldErrors.title === "This title already exists in this folder.") {
        this.clearFormFieldError("title");
      }
    },
    isDeletedState(state) {
      const v = String(state || "").trim().toLowerCase();
      return v === "delete" || v === "deleted";
    },
    closeEditDeleteConfirm() {
      this.showEditDeleteConfirm = false;
      this.editDeleteChecking = false;
      this.editDeleteBlocked = false;
      this.editDeleteBlockedMessage = "";
    },
    async openEditDeleteConfirm() {
      const tc = this.editingTc;
      if (!tc?.testCaseId || this.editDeleteChecking) return;
      this.editDeleteBlocked = false;
      this.editDeleteBlockedMessage = "";
      this.editDeleteChecking = true;
      const blockedMsg = "This test case cannot be deleted because it is linked to a Test Run.";
      try {
        const info = await realTestCasesApi.getTestRunLink(tc.testCaseId);
        this.editDeleteBlocked = !!info?.linked;
        this.editDeleteBlockedMessage = info?.message || blockedMsg;
      } catch (_) {
        this.editDeleteBlocked = false;
      } finally {
        this.editDeleteChecking = false;
        this.showEditDeleteConfirm = true;
      }
    },
    async deleteEditingTestCase() {
      const tc = this.editingTc;
      if (!tc?.testCaseId || this.formDeleting || this.editDeleteBlocked) return;
      this.formDeleting = true;
      try {
        await realTestCasesApi.delete(tc.testCaseId, { modifyBy: this.currentUserId });
        this.closeEditDeleteConfirm();
        this.closeCreateModal();
        this.showDetailModal = false;
        await this.load();
        this.showToast("success", "Test case deleted.");
      } catch (e) {
        this.editDeleteBlocked = true;
        this.editDeleteBlockedMessage =
          e.message || "This test case cannot be deleted because it is linked to a Test Run.";
      } finally {
        this.formDeleting = false;
      }
    },
    cancelCreateOrEdit() {
      const returnTo = this.editMode && this.editingFromDetail ? this.editingTc : null;
      this.closeCreateModal();
      if (returnTo) this.openDetailModal(returnTo);
    },
    openRequirementModal() {
      this.requirementIdInput = "";
      this.showRequirementModal = true;
      this.loadJiraProjects();
    },
    closeRequirementModal() {
      this.showRequirementModal = false;
    },
    clearFormFieldError(field) {
      if (this.formFieldErrors[field]) this.formFieldErrors[field] = "";
    },
    onFormFunctionChange() {
      this.form.functionIsNew = false;
      this.clearFormFieldError("function");
    },
    onFormScenarioChange() {
      this.form.scenarioIsNew = false;
      this.clearFormFieldError("scenario");
    },
    onFormSubScenarioChange(value) {
      const raw = String(value ?? "").trim();
      const exists = this.subScenarioOptions.some((o) => String(o.value) === raw);
      this.form.subScenarioIsNew = !!raw && !exists && !/^\d+$/.test(raw);
      this.clearFormFieldError("subScenario");
    },
    hasRequiredSubScenario(value = this.form.subScenarioId) {
      return !!String(value ?? "").trim();
    },
    onTitleInput() {
      this.clearFormFieldError("title");
      this.checkTitleDuplicate();
    },
    validateCreateForm() {
      const errors = { function: "", scenario: "", subScenario: "", title: "" };
      if (this.formCopyMode && !this.copySourceCase) {
        this.formError = "Select a test case to copy.";
        this.formFieldErrors = errors;
        return false;
      }
      if (!String(this.form.functionId || "").trim()) errors.function = "Function is required";
      if (!String(this.form.scenarioId || "").trim()) errors.scenario = "Scenario is required";
      if (!this.hasRequiredSubScenario()) errors.subScenario = "Sub-Scenario is required";
      if (!String(this.form.title || "").trim()) errors.title = "Title is required";
      else if (!this.editMode && this.isTitleTakenInFolder(this.form.title)) {
        errors.title = "This title already exists in this folder.";
      }
      this.formFieldErrors = errors;
      return !errors.function && !errors.scenario && !errors.subScenario && !errors.title;
    },
    addRequirementIdFromInput(form) {
      const key = extractJiraKey(this.requirementIdInput);
      if (!key) return;
      if (!form.requirementIds.includes(key)) {
        form.requirementIds.push(key);
      }
      this.requirementIdInput = "";
    },
    removeRequirementId(form, key) {
      form.requirementIds = form.requirementIds.filter((k) => k !== key);
    },
    async loadJiraProjects() {
      if (this.jiraProjects.length > 0) return;
      this.jiraLoadingProjects = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/projects`);
        this.jiraProjects = await res.json();
      } catch (e) {
        console.error("loadJiraProjects error:", e);
      } finally {
        this.jiraLoadingProjects = false;
      }
    },
    async loadJiraIssues(projectKey, search = "") {
      this.jiraLoadingIssues = true;
      try {
        const q = search ? `&search=${encodeURIComponent(search)}` : "";
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issues?projectKey=${projectKey}${q}`);
        this.jiraIssues = await res.json();
      } catch (e) {
        console.error("loadJiraIssues error:", e);
      } finally {
        this.jiraLoadingIssues = false;
      }
    },
    onJiraProjectChange() {
      this.jiraIssues = [];
      this.jiraSearch = "";
      if (this.jiraProjectKey) this.loadJiraIssues(this.jiraProjectKey);
    },
    onJiraSearch() {
      clearTimeout(this.jiraSearchTimer);
      this.jiraSearchTimer = setTimeout(() => {
        if (this.jiraProjectKey) this.loadJiraIssues(this.jiraProjectKey, this.jiraSearch);
      }, 300);
    },
    toggleJiraTicket(form, key) {
      const idx = form.requirementIds.indexOf(key);
      if (idx === -1) form.requirementIds.push(key);
      else form.requirementIds.splice(idx, 1);
    },
    async loadFunctionOptions() {
      try {
        const rows = await testFunctionsApi.getBySubProject(this.selectedSubProjectId);
        this.functionOptions = (Array.isArray(rows) ? rows : [])
          .filter((f) => !this.isUncategorizedLabel(f.functionName))
          .map((f) => ({
            value: String(f.functionId),
            label: f.functionName,
          }));
      } catch (e) {
        console.error("loadFunctionOptions error:", e);
        this.functionOptions = [];
      }
    },
    async loadScenarios() {
      try {
        const rows = await scenariosApi.getBySubProject(this.selectedSubProjectId);
        this.allScenarios = Array.isArray(rows) ? rows : [];
        this.scenarioOptions = this.allScenarios.map((s) => ({
          value: String(s.scenarioId),
          label: s.scenarioName,
        }));
      } catch (e) {
        console.error("loadScenarios error:", e);
        this.allScenarios = [];
        this.scenarioOptions = [];
      }
    },
    async loadSubScenarios(scenarioId) {
      if (!scenarioId) {
        this.subScenarioOptions = [];
        return;
      }
      try {
        const rows = await subScenariosApi.getByScenario(scenarioId);
        this.subScenarioOptions = (Array.isArray(rows) ? rows : []).map((s) => ({
          value: String(s.subScenarioId),
          label: s.subScenarioName,
        }));
      } catch (e) {
        console.error("loadSubScenarios error:", e);
        this.subScenarioOptions = [];
      }
    },
    addStep() {
      this.form.steps.push(blankStep());
    },
    removeStep(idx) {
      if (this.form.steps.length <= 1) return;
      this.form.steps.splice(idx, 1);
    },
    async resolveFunctionScenarioSubScenario(form) {
      await this.loadFunctionOptions();
      await this.loadScenarios();

      let functionId = form.functionId || null;
      if (form.functionIsNew && functionId) {
        const createdFn = await testFunctionsApi.insert({
          subProjectId: this.selectedSubProjectId,
          functionName: functionId,
        });
        functionId = createdFn?.functionId ?? null;
      }
      const numericFunctionId = /^\d+$/.test(String(functionId || "")) ? Number(functionId) : null;

      let scenarioName = "";
      let requestedScenarioId = null;
      if (form.scenarioIsNew && form.scenarioId) {
        scenarioName = String(form.scenarioId).trim();
      } else if (/^\d+$/.test(String(form.scenarioId || ""))) {
        requestedScenarioId = Number(form.scenarioId);
        const existing = this.allScenarios.find((s) => Number(s.scenarioId) === requestedScenarioId);
        scenarioName = String(existing?.scenarioName || this.editInitialScenarioName || "").trim();
      }

      let scenarioId = requestedScenarioId;
      const scenarioRow = requestedScenarioId
        ? this.allScenarios.find((s) => Number(s.scenarioId) === requestedScenarioId)
        : null;
      const scenarioMatchesFunction =
        scenarioRow &&
        numericFunctionId &&
        Number(scenarioRow.functionId) === numericFunctionId;

      if (form.scenarioIsNew && scenarioName) {
        const createdSc = await scenariosApi.insert({
          subProjectId: this.selectedSubProjectId,
          scenarioName,
          functionId: numericFunctionId,
        });
        scenarioId = createdSc?.scenarioId ?? null;
        if (createdSc) this.allScenarios.push(createdSc);
      } else if (!scenarioMatchesFunction && scenarioName && numericFunctionId) {
        let target = this.allScenarios.find(
          (s) =>
            String(s.scenarioName || "").trim().toLowerCase() === scenarioName.toLowerCase() &&
            Number(s.functionId) === numericFunctionId,
        );
        if (!target) {
          target = await scenariosApi.insert({
            subProjectId: this.selectedSubProjectId,
            scenarioName,
            functionId: numericFunctionId,
          });
          if (target) this.allScenarios.push(target);
        }
        scenarioId = target?.scenarioId ?? null;
      }

      let subScenarioId = form.subScenarioId || null;
      const keptOriginalScenarioPicker =
        String(form.scenarioId || "") === String(this.editInitialScenarioId || "");
      if (form.subScenarioIsNew && subScenarioId && scenarioId) {
        const createdSub = await subScenariosApi.insert(scenarioId, subScenarioId);
        subScenarioId = createdSub?.subScenarioId ?? null;
      } else if (/^\d+$/.test(String(subScenarioId || "")) && scenarioId) {
        const wantedId = Number(subScenarioId);
        const list = await subScenariosApi.getByScenario(scenarioId);
        const matchById = (Array.isArray(list) ? list : []).find(
          (s) => Number(s.subScenarioId) === wantedId,
        );
        if (!matchById) {
          const wantedName = String(
            this.subScenarioOptions.find((o) => String(o.value) === String(form.subScenarioId))?.label ||
              this.editInitialSubScenarioName ||
              "",
          ).trim();
          if (wantedName && keptOriginalScenarioPicker) {
            const matchByName = (Array.isArray(list) ? list : []).find(
              (s) => String(s.subScenarioName || "").trim().toLowerCase() === wantedName.toLowerCase(),
            );
            if (matchByName) {
              subScenarioId = matchByName.subScenarioId;
            } else {
              const createdSub = await subScenariosApi.insert(scenarioId, wantedName);
              subScenarioId = createdSub?.subScenarioId ?? null;
            }
          } else {
            subScenarioId = null;
          }
        }
      } else if (!subScenarioId) {
        subScenarioId = null;
      }

      return { functionId: numericFunctionId, scenarioId, subScenarioId };
    },
    buildPayload() {
      const f = this.form;
      return {
        title: f.title.trim(),
        description: f.showDescription ? f.description.trim() || null : null,
        preconditions: f.preconditions.trim() || null,
        testData: f.testData.trim() || null,
        state: f.state,
        priority: f.priority,
        typeOfTestCase: f.typeOfTestCase,
        automationStatus: f.automationStatus,
        testType: f.testType,
        executionType: f.executionType,
        createdBy: f.ownerId || this.currentUserId || null,
        requirementId: f.requirementIds.join(", ") || null,
        folderId: this.folderIdForSave(),
      };
    },
    folderIdForSave() {
      return /^\d+$/.test(this.selectedFolderId) ? Number(this.selectedFolderId) : null;
    },
    async submitForm(asDraft = false) {
      this.formError = "";
      if (!this.validateCreateForm()) return;
      this.formSaving = true;
      try {
        const { functionId, scenarioId, subScenarioId } = await this.resolveFunctionScenarioSubScenario(this.form);
        if (!scenarioId) {
          this.formFieldErrors = { ...this.formFieldErrors, scenario: "Scenario is required" };
          return;
        }
        if (!this.hasRequiredSubScenario(subScenarioId)) {
          this.formFieldErrors = { ...this.formFieldErrors, subScenario: "Sub-Scenario is required" };
          return;
        }
        const steps = this.form.steps.filter((s) => s.stepDetail.trim() || s.expectedResult.trim());
        const payload = this.buildPayload();
        if (!this.editMode && asDraft) payload.state = "Draft";
        const returnToDetail = this.editMode && this.editingFromDetail;
        const editingId = this.editingTc?.testCaseId;
        if (this.editMode) {
          const testCaseId = this.editingTc.testCaseId;
          await realTestCasesApi.update(testCaseId, {
            ...payload,
            scenarioId,
            functionId,
            subScenarioId,
            modifyBy: this.currentUserId,
          });
          await stepsAndResultApi.save(testCaseId, steps, this.currentUserId);
        } else {
          const created = await realTestCasesApi.insert({ ...payload, scenarioId, subScenarioId });
          if (steps.length && created?.testCaseId) {
            await stepsAndResultApi.save(created.testCaseId, steps, this.currentUserId);
          }
        }
        this.closeCreateModal();
        await this.load();
        if (returnToDetail && editingId) {
          const refreshed = (this.cases || []).find((tc) => Number(tc.testCaseId) === Number(editingId));
          await this.openDetailModal(refreshed || { testCaseId: editingId });
        }
      } catch (e) {
        console.error("submitForm error:", e);
        this.formError = e.message || "Failed to save test case";
      } finally {
        this.formSaving = false;
      }
    },
    async openDetailModal(tc) {
      this.showDetailModal = true;
      this.detailTab = "details";
      this.detailTestCase = null;
      this.detailError = "";
      this.history = [];
      this.viewingHistoryIndex = null;
      this.showHistoryDropdown = false;
      this.activityHistory = [];
      this.remarkDraft = "";
      this.remarkEntries = [];
      this.detailLoading = true;
      try {
        const [snapshot, historyRows] = await Promise.all([
          realTestCasesApi.getById(tc.testCaseId),
          realTestCasesApi.getHistory(tc.testCaseId).catch(() => []),
        ]);
        this.detailTestCase = snapshot;
        this.history = Array.isArray(historyRows) ? historyRows : [];
      } catch (e) {
        console.error("openDetailModal error:", e);
        this.detailError = e.message || "Failed to load test case";
      } finally {
        this.detailLoading = false;
      }
    },
    closeDetailModal() {
      this.showDetailModal = false;
      this.showHistoryDropdown = false;
    },
    toggleHistoryDropdown() {
      if (!this.history.length) return;
      this.showHistoryDropdown = !this.showHistoryDropdown;
    },
    viewCurrentVersion() {
      this.viewingHistoryIndex = null;
      this.showHistoryDropdown = false;
    },
    selectHistoryVersion(index) {
      this.viewingHistoryIndex = index;
      this.showHistoryDropdown = false;
    },
    async openDetailHistoryTab() {
      this.detailTab = "history";
      const testCaseId = this.detailTestCase?.testCaseId;
      if (!testCaseId) return;
      this.activityLoading = true;
      try {
        const apiRows = await realTestCasesApi.getActivity(testCaseId).catch(() => []);
        const merged = [...(Array.isArray(apiRows) ? apiRows : []), ...this.buildVersionActivityEntries()];
        this.activityHistory = this.dedupeActivityHistory(merged)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      } finally {
        this.activityLoading = false;
      }
    },
    activityMinuteBucket(entry) {
      const t = entry?.createdAt ? new Date(entry.createdAt) : null;
      if (!t || Number.isNaN(t.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
    },
    activityGroupKey(entry) {
      if (!entry || entry.entryType === "sync") return "";
      const bucket = this.activityMinuteBucket(entry);
      const field = String(entry.fieldName || "");
      const type = String(entry.entryType || "change");
      return bucket && field ? `${bucket}|${type}|${field}` : "";
    },
    activityCompletenessScore(entry) {
      let score = 0;
      if (entry?.activityId && !String(entry.activityId).startsWith("ver-")) score += 3;
      if (entry?.remarkText) score += 1;
      if (entry.fieldName === "Steps") {
        score += this.diffStepHistoryChanges(entry.oldValue, entry.newValue).length;
      } else {
        const oldV = String(entry.oldValue ?? "").trim();
        const newV = String(entry.newValue ?? "").trim();
        if (oldV && oldV !== "—") score += 1;
        if (newV && newV !== "—") score += 1;
      }
      return score;
    },
    dedupeActivityHistory(entries) {
      const list = Array.isArray(entries) ? entries : [];
      const bestByKey = new Map();
      for (const entry of list) {
        if (entry.fieldName === "Steps" && !this.diffStepHistoryChanges(entry.oldValue, entry.newValue).length) {
          continue;
        }
        const key = this.activityGroupKey(entry);
        if (!key) continue;
        const existing = bestByKey.get(key);
        if (!existing || this.activityCompletenessScore(entry) > this.activityCompletenessScore(existing)) {
          bestByKey.set(key, entry);
        }
      }
      const used = new Set();
      return list.reduce((acc, entry) => {
        if (entry.fieldName === "Steps" && !this.diffStepHistoryChanges(entry.oldValue, entry.newValue).length) {
          return acc;
        }
        const key = this.activityGroupKey(entry);
        if (!key) {
          acc.push(entry);
          return acc;
        }
        if (used.has(key)) return acc;
        used.add(key);
        acc.push(bestByKey.get(key) || entry);
        return acc;
      }, []);
    },
    isRemarkTabEntry(entry) {
      const type = String(entry?.entryType || "").toLowerCase();
      if (type === "remark" || type === "feedback") return true;
      const field = String(entry?.fieldName || "").toLowerCase();
      return field === "remark" || field === "feedback";
    },
    remarkTabBadgeLabel(entry) {
      const type = String(entry?.entryType || "").toLowerCase();
      const field = String(entry?.fieldName || "").toLowerCase();
      if (type === "feedback" || field === "feedback") return "Feedback";
      return "Remark";
    },
    remarkTabBody(entry) {
      const type = String(entry?.entryType || "").toLowerCase();
      if (type === "remark") return String(entry.remarkText || entry.newValue || "").trim() || "—";
      return String(entry.newValue || entry.remarkText || "").trim() || "—";
    },
    async openDetailRemarkTab() {
      this.detailTab = "remark";
      await this.loadRemarkEntries();
    },
    async loadRemarkEntries() {
      const testCaseId = this.detailTestCase?.testCaseId;
      if (!testCaseId) {
        this.remarkEntries = [];
        return;
      }
      this.remarkLoading = true;
      try {
        const rows = await realTestCasesApi.getActivity(testCaseId).catch(() => []);
        this.remarkEntries = (Array.isArray(rows) ? rows : [])
          .filter((e) => this.isRemarkTabEntry(e))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      } catch (e) {
        this.remarkEntries = [];
      } finally {
        this.remarkLoading = false;
      }
    },
    async saveDetailRemark() {
      const text = String(this.remarkDraft || "").trim();
      const testCaseId = this.detailTestCase?.testCaseId;
      if (!text || !testCaseId || this.remarkSaving) return;
      this.remarkSaving = true;
      try {
        const rows = await realTestCasesApi.addRemark(testCaseId, {
          remarkText: text,
          actorUserId: this.currentUserId,
        });
        this.remarkDraft = "";
        this.remarkEntries = (Array.isArray(rows) ? rows : [])
          .filter((e) => this.isRemarkTabEntry(e))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      } catch (e) {
        window.alert(e.message || "Failed to save remark");
      } finally {
        this.remarkSaving = false;
      }
    },
    snapshotToActivityState(snap) {
      const t = (v) => String(v ?? "").replace(/\r\n/g, "\n").trim();
      if (!snap) return {};
      return {
        Function: t(snap.function || snap.functionName),
        Scenario: t(snap.scenario || snap.scenarioName),
        "Sub Scenario": t(snap.subScenario || snap.subScenarioName),
        Title: t(snap.title),
        Description: t(snap.description),
        Preconditions: t(snap.preconditions),
        "Test Data": t(snap.testData),
        State: t(snap.state || "Active"),
        Priority: t(snap.priority || "medium"),
        Category: t(snap.typeOfTestCase),
        "Automation Status": t(snap.automationStatus),
        "Test Type": t(snap.testType),
        "Execution Type": t(snap.executionType),
        "Requirement ID": t(snap.requirementId),
        Owner: t(snap.ownerFullName),
        Steps: JSON.stringify(
          (snap.steps || []).map((s, i) => ({
            stepNo: s.stepNo || i + 1,
            platform: t(s.platform),
            action: t(s.action || s.stepDetail),
            expected: t(s.expected || s.expectedResult),
          })),
        ),
      };
    },
    buildVersionActivityEntries() {
      const history = [...(this.history || [])].sort((a, b) => (b.versionNo || 0) - (a.versionNo || 0));
      const entries = [];
      history.forEach((h, i) => {
        const before = this.snapshotToActivityState(h.snapshot || {});
        const after = this.snapshotToActivityState(i === 0 ? this.detailTestCase : history[i - 1].snapshot || {});
        for (const fieldName of Object.keys(after)) {
          if ((before[fieldName] ?? "") === (after[fieldName] ?? "")) continue;
          entries.push({
            activityId: `ver-${h.historyId || i}-${fieldName}`,
            entryType: "change",
            fieldName,
            oldValue: before[fieldName] || (fieldName === "Steps" ? "[]" : "—"),
            newValue: after[fieldName] || (fieldName === "Steps" ? "[]" : "—"),
            createdAt: h.createdAt,
            authorName: h.changedByName || "System",
          });
        }
      });
      return entries;
    },
    parseStepsHistoryJson(raw) {
      if (!raw) return [];
      try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    },
    diffStepHistoryChanges(oldRaw, newRaw) {
      const before = this.parseStepsHistoryJson(oldRaw);
      const after = this.parseStepsHistoryJson(newRaw);
      const changes = [];
      for (let i = 0; i < Math.max(before.length, after.length); i++) {
        const b = before[i];
        const a = after[i];
        const stepNo = a?.stepNo || b?.stepNo || i + 1;
        const norm = (st) => ({
          action: String(st?.action || st?.stepDetail || "").trim(),
          expected: String(st?.expected || st?.expectedResult || "").trim(),
        });
        if (!b && a) changes.push({ stepNo, after: norm(a) });
        else if (b && !a) changes.push({ stepNo, before: norm(b) });
        else if (b && a) {
          const nb = norm(b);
          const na = norm(a);
          if (nb.action !== na.action || nb.expected !== na.expected) changes.push({ stepNo, before: nb, after: na });
        }
      }
      return changes;
    },
    activityBadgeLabel(entry) {
      if (entry.entryType === "sync") return "Sync";
      if (entry.entryType === "feedback") return "Feedback";
      if (entry.entryType === "remark") return "Comment";
      return "Change";
    },
    activityBadgeClass(entry) {
      if (entry.entryType === "sync") return "badge-sync";
      if (entry.entryType === "feedback") return "badge-feedback";
      if (entry.entryType === "remark") return "badge-remark";
      return "badge-change";
    },
    activityDisplayValue(val) {
      const text = String(val ?? "").trim();
      return text || "—";
    },
    formatActivityTime(val) {
      if (!val) return "";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatDetailCreatedAt(iso) {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      const datePart = d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
      const timePart = d.toLocaleString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZoneName: "short",
      });
      return `${datePart} | ${timePart}`;
    },
    async openEditFromDetail() {
      const tc = this.detailTestCase;
      if (!tc) return;
      this.editMode = true;
      this.editingTc = tc;
      this.editInitialScenarioId = tc.scenarioId ? String(tc.scenarioId) : "";
      this.editInitialSubScenarioId = tc.subScenarioId ? String(tc.subScenarioId) : "";
      this.editInitialScenarioName = tc.scenario || tc.scenarioName || "";
      this.editInitialSubScenarioName = tc.subScenario || tc.subScenarioName || "";
      this.form = {
        ...emptyCreateForm(),
        functionId: tc.functionId ? String(tc.functionId) : "",
        scenarioId: this.editInitialScenarioId,
        subScenarioId: this.editInitialSubScenarioId,
        title: tc.title || "",
        showDescription: !!(tc.description && tc.description.trim()),
        description: tc.description || "",
        preconditions: tc.preconditions || "",
        testData: tc.testData || "",
        steps:
          tc.steps && tc.steps.length
            ? tc.steps.map((s) => normalizeFormStep(s))
            : [blankStep()],
        requirementIds: tc.requirementId
          ? String(tc.requirementId)
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        ownerId: tc.createdBy ? String(tc.createdBy) : "",
        state: tc.state || "Active",
        priority: tc.priority || "medium",
        typeOfTestCase: tc.typeOfTestCase || "Functional",
        automationStatus: tc.automationStatus || "not_automated",
        testType: tc.testType || "Positive",
        executionType: tc.executionType || "manual",
      };
      this.resetFormPickers();
      this.editingFromDetail = true;
      this.showDetailModal = false;
      this.showCreateModal = true;
      this.loadFunctionOptions();
      this.loadScenarios();
      if (this.form.scenarioId) this.loadSubScenarios(this.form.scenarioId);
      this.loadUsers();
    },
    async loadUsers() {
      if (this.userOptions.length) return;
      try {
        const rows = await usersApi.getAll();
        this.userOptions = (Array.isArray(rows) ? rows : []).map((u) => ({
          value: String(u.userId),
          label: this.formatUserDisplayName(u),
        }));
      } catch (e) {
        console.error("loadUsers error:", e);
        this.userOptions = [];
      }
    },
    formatUserDisplayName(user) {
      const first = String(user?.firstName || "").trim();
      const nick = String(user?.nickname || "").trim();
      if (!first) return "—";
      return nick ? `${first} (${nick})` : first;
    },
    formatHistoryDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
    },
    enumLabel(value) {
      const raw = String(value || "").trim();
      if (!raw) return "—";
      return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    statusPillClass(status) {
      const v = (status || "").trim().toLowerCase();
      if (v === "active" || v === "automated") return "pill-active";
      if (v === "in review" || v === "waiting review" || v === "in_progress") return "pill-inprogress";
      if (v === "feedback") return "pill-feedback";
      return "pill-inactive";
    },
    priorityPillClass(priority) {
      const v = (priority || "").trim().toLowerCase();
      if (v === "high" || v === "critical") return "pill-high";
      if (v === "medium") return "pill-medium";
      if (v === "low") return "pill-low";
      return "pill-inactive";
    },
    testTypePillClass(testType) {
      const v = (testType || "").trim().toLowerCase();
      if (v === "positive") return "qa-tc-type-pill--positive";
      if (v === "negative") return "qa-tc-type-pill--negative";
      return "qa-tc-type-pill--default";
    },
    reviewTypeToneClass(testType) {
      const v = String(testType || "").trim().toLowerCase();
      if (v === "positive") return "is-positive";
      if (v === "negative") return "is-negative";
      return "";
    },
    reviewPriorityToneClass(priority) {
      const v = String(priority || "").trim().toLowerCase();
      if (v === "high" || v === "critical") return "is-high";
      if (v === "medium") return "is-medium";
      if (v === "low") return "is-low";
      return "";
    },
    uniqueCaseValues(getter) {
      return [...new Set(this.cases.map(getter).map((v) => String(v || "").trim()).filter(Boolean))].sort();
    },
    isUncategorizedLabel(value) {
      return String(value || "").trim().toLowerCase() === "uncategorized";
    },
    caseFunctionName(tc) {
      return String(tc?.functionName || tc?.function || "").trim();
    },
    caseScenarioName(tc) {
      return String(tc?.scenarioName || tc?.scenario || "").trim();
    },
    caseSubScenarioName(tc) {
      return String(tc?.subScenarioName || tc?.subScenario || "").trim();
    },
    normalizeCaseHierarchy(tc) {
      const functionName = this.caseFunctionName(tc);
      const scenarioName = this.caseScenarioName(tc);
      const subScenarioName = this.caseSubScenarioName(tc);
      return {
        ...tc,
        functionName,
        scenarioName,
        subScenarioName,
        function: tc?.function || functionName,
        scenario: tc?.scenario || scenarioName,
        subScenario: tc?.subScenario || subScenarioName,
      };
    },
    resetCaseFilters() {
      this.filtersPanelOpen = false;
      this.filters = {
        function: "",
        scenario: "",
        subScenario: "",
        testType: "",
        status: "",
        priority: "",
      };
    },
    toggleFiltersPanel() {
      this.filtersPanelOpen = !this.filtersPanelOpen;
    },
    onFunctionFilterChange(value) {
      this.filters.function = value ?? "";
      this.filters.scenario = "";
      this.filters.subScenario = "";
    },
    onScenarioFilterChange(value) {
      this.filters.scenario = value ?? "";
      this.filters.subScenario = "";
    },
    clearFilters() {
      this.searchText = "";
      this.resetCaseFilters();
    },
    closeActionMenus(e) {
      if (
        this.$refs.downloadWrap &&
        typeof this.$refs.downloadWrap.contains === "function" &&
        !this.$refs.downloadWrap.contains(e.target)
      ) {
        this.showDownloadMenu = false;
      }
      if (
        this.$refs.uploadWrap &&
        typeof this.$refs.uploadWrap.contains === "function" &&
        !this.$refs.uploadWrap.contains(e.target)
      ) {
        this.showUploadMenu = false;
      }
    },
    toggleDownloadMenu() {
      this.showDownloadMenu = !this.showDownloadMenu;
      if (this.showDownloadMenu) this.showUploadMenu = false;
    },
    toggleUploadMenu() {
      this.showUploadMenu = !this.showUploadMenu;
      if (this.showUploadMenu) this.showDownloadMenu = false;
    },
    triggerFileUpload() {
      this.showUploadMenu = false;
      const input = this.$refs.fileInput;
      if (input && typeof input.click === "function") input.click();
    },
    showToast(type, message) {
      this.uploadToast = { show: true, type, message };
      setTimeout(() => { this.uploadToast.show = false; }, 4000);
    },
    async downloadTemplate() {
      this.showUploadMenu = false;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/template/testcase`);
        const raw = await res.text();
        let data;
        try {
          data = JSON.parse(raw);
        } catch {
          throw new Error("Invalid API response — check backend / proxy configuration.");
        }
        if (!res.ok) throw new Error(data.error || "Failed to get download URL");
        if (!data.url) throw new Error("Missing download URL");
        const a = document.createElement("a");
        a.href = data.url;
        a.download = "QA_Template.xlsx";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (err) {
        this.showToast("error", `Download failed: ${err.message}`);
      }
    },
    async handleFileUpload(e) {
      const input = e.target;
      const file = input.files[0];
      if (!file) return;
      this.showUploadMenu = false;
      if (!this.selectedSubProjectId) {
        this.showToast("error", "Select a sub-project before importing.");
        input.value = "";
        return;
      }
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["csv", "xlsx", "xls"].includes(ext)) {
        this.showToast("error", "Only .csv, .xlsx, .xls files are supported.");
        input.value = "";
        return;
      }
      try {
        const parseCSV = (raw) => {
          const results = [];
          const lines = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
          let row = [], cur = "", inQuote = false;
          for (let i = 0; i < lines.length; i++) {
            const ch = lines[i];
            if (inQuote) {
              if (ch === '"' && lines[i + 1] === '"') { cur += '"'; i++; }
              else if (ch === '"') { inQuote = false; }
              else { cur += ch; }
            } else if (ch === '"') { inQuote = true; }
            else if (ch === ",") { row.push(cur.trim()); cur = ""; }
            else if (ch === "\n") { row.push(cur.trim()); results.push(row); row = []; cur = ""; }
            else { cur += ch; }
          }
          if (cur || row.length) { row.push(cur.trim()); results.push(row); }
          return results;
        };
        const parseExcelRows = async (uploadFile) => {
          const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");
          const buffer = await uploadFile.arrayBuffer();
          const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
          const sheetName = workbook.SheetNames[0];
          if (!sheetName) return [];
          const sheet = workbook.Sheets[sheetName];
          const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
          return rawRows
            .map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? "").trim()) : []))
            .filter((row) => row.some((cell) => cell !== ""));
        };

        let rows;
        if (ext === "csv") {
          rows = parseCSV((await file.text()).replace(/^\uFEFF/, ""));
        } else {
          rows = await parseExcelRows(file);
        }
        if (rows.length < 2) {
          this.showToast("error", "File is empty or has no data rows.");
          input.value = "";
          return;
        }

        const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const TITLE_ALIASES = ["testcasedetail", "testcasename", "testcasedetails"];
        const headerRowIdx = rows.findIndex((r) => r.some((h) => TITLE_ALIASES.includes(normalize(h))));
        if (headerRowIdx === -1) {
          this.showToast("error", "Cannot find column header row with 'Test Case Detail'.");
          input.value = "";
          return;
        }

        const headerMap = {};
        rows[headerRowIdx].forEach((h, i) => { headerMap[normalize(h)] = i; });
        const col = (aliases) => {
          for (const a of aliases) {
            const idx = headerMap[normalize(a)];
            if (idx !== undefined) return idx;
          }
          return -1;
        };
        const COL = {
          testcaseId: col(["Testcase ID", "Test Case ID", "TestcaseID", "TC ID"]),
          function: col(["Function"]),
          scenario: col(["Scenario"]),
          subScenario: col(["Sub-Scenario", "Sub Scenario", "SubScenario"]),
          requirementId: col(["Requirement ID", "Requestment ID", "RequirementID", "RequestmentID"]),
          module: col(["Module", "Platform"]),
          title: col(["Test case detail", "Test Case Name", "Test Case Detail"]),
          testType: col(["Type", "Test type", "Test Type", "TestType"]),
          precond: col(["Pre-condition", "Pre-Condition", "Preconditions", "Precondition"]),
          testData: col(["Test Data", "TestData"]),
          expected: col(["Expect result (Response & Database)", "Expect result", "Expect Result", "Expected Result", "Expected"]),
          stepDetail: col(["Test Step", "Test Steps", "Steps", "Step Detail"]),
          priority: col(["Priority"]),
          execType: col(["Execution Type"]),
          autoStatus: col(["Automation Status", "Status", "Test Status"]),
          typeOfTestCase: col(["Type of Test Case", "Type Of Test Case", "Case Category", "Category"]),
        };
        const resolveTitle = (get) => get(COL.title) || get(COL.subScenario) || get(COL.testcaseId);
        const isNewTestCaseRow = (get) => {
          if (get(COL.testcaseId) || get(COL.title)) return true;
          if (get(COL.subScenario) && (get(COL.scenario) || get(COL.stepDetail) || get(COL.expected))) return true;
          return false;
        };
        const stripStepNo = (s) => (s || "").replace(/^\d+\.\s*/, "").trim();
        const toAutoStatus = (v) => {
          const map = {
            automated: "automated",
            "in progress": "in_progress",
            in_progress: "in_progress",
            "not automated": "not_automated",
            not_automated: "not_automated",
          };
          return map[(v || "").trim().toLowerCase()] || "not_automated";
        };
        const toExecType = (v) => {
          const map = { manual: "manual", automated: "automated", auto: "automated" };
          return map[(v || "").trim().toLowerCase()] || null;
        };
        const toTestType = (v) => {
          const t = (v || "").trim().toLowerCase();
          if (t === "positive") return "Positive";
          if (t === "negative") return "Negative";
          return (v || "").trim() || null;
        };
        const normalizePriority = (v) => {
          const p = (v || "medium").trim().toLowerCase();
          if (p === "hight") return "high";
          return p || "medium";
        };

        const allRows = rows.slice(headerRowIdx + 1);
        const tcBlocks = [];
        let currentBlock = null;
        for (const row of allRows) {
          const get = (colIdx) => (colIdx >= 0 ? (row[colIdx] || "").trim() : "");
          const stepDetail = get(COL.stepDetail);
          const expected = get(COL.expected);
          if (isNewTestCaseRow(get)) {
            currentBlock = { row, steps: [] };
            tcBlocks.push(currentBlock);
            if (stepDetail || expected) {
              currentBlock.steps.push({
                stepDetail: stripStepNo(stepDetail),
                expectedResult: expected,
                platform: get(COL.module) || null,
              });
            }
          } else if (currentBlock && (stepDetail || expected)) {
            currentBlock.steps.push({
              stepDetail: stripStepNo(stepDetail),
              expectedResult: expected,
              platform: get(COL.module) || null,
            });
          }
        }

        this.importProgress = {
          show: true,
          total: tcBlocks.length,
          current: 0,
          success: 0,
          failed: 0,
          currentTitle: "",
          done: false,
          cancelled: false,
        };

        const subProjectId = this.selectedSubProjectId;
        let functions = await testFunctionsApi.getBySubProject(subProjectId);
        let scenarios = await scenariosApi.getBySubProject(subProjectId);
        if (!Array.isArray(functions)) functions = [];
        if (!Array.isArray(scenarios)) scenarios = [];
        const subScenariosByScenario = new Map();
        const SUB_SCENARIO_MAX_LEN = 300;
        let lastFunctionName = "";
        let lastScenarioName = "";

        for (const block of tcBlocks) {
          if (this.importProgress.cancelled) break;
          const get = (colIdx) => (colIdx >= 0 ? (block.row[colIdx] || "").trim() : "");
          const title = resolveTitle(get);
          const testcaseId = get(COL.testcaseId);
          this.importProgress.current++;
          this.importProgress.currentTitle = title || testcaseId || "...";
          try {
            if (!title) throw new Error("Missing test case title");
            const rawFunction = get(COL.function);
            if (rawFunction) lastFunctionName = rawFunction;
            const functionName = rawFunction || lastFunctionName || "Uncategorized";
            const rawScenario = get(COL.scenario);
            if (rawScenario) lastScenarioName = rawScenario;
            const scenarioName = rawScenario || lastScenarioName || "Uncategorized";
            let subScenarioName = get(COL.subScenario);
            if (subScenarioName.length > SUB_SCENARIO_MAX_LEN) {
              subScenarioName = subScenarioName.slice(0, SUB_SCENARIO_MAX_LEN);
            }

            let testFunction = functions.find(
              (f) => String(f.functionName || "").toLowerCase() === functionName.toLowerCase()
            );
            if (!testFunction) {
              testFunction = await testFunctionsApi.insert({ subProjectId, functionName });
              functions.push(testFunction);
            }
            const functionId = testFunction.functionId;

            let scenario = scenarios.find(
              (s) =>
                String(s.scenarioName || "").toLowerCase() === scenarioName.toLowerCase()
                && Number(s.functionId) === Number(functionId)
            );
            if (!scenario) {
              scenario = await scenariosApi.insert({ subProjectId, scenarioName, functionId });
              scenarios.push(scenario);
            }
            const scenarioId = scenario.scenarioId;

            let subSc = null;
            if (subScenarioName) {
              if (!subScenariosByScenario.has(scenarioId)) {
                subScenariosByScenario.set(scenarioId, await subScenariosApi.getByScenario(scenarioId));
              }
              const list = subScenariosByScenario.get(scenarioId) || [];
              subSc = list.find(
                (s) => String(s.subScenarioName || "").toLowerCase() === subScenarioName.toLowerCase()
              );
              if (!subSc) {
                subSc = await subScenariosApi.insert(scenarioId, subScenarioName);
                list.push(subSc);
                subScenariosByScenario.set(scenarioId, list);
              }
            }

            const newTc = await realTestCasesApi.insert({
              scenarioId,
              subScenarioId: subSc?.subScenarioId || null,
              title,
              typeOfTestCase: normalizeTypeOfTestCase(get(COL.typeOfTestCase), "Functional"),
              testType: toTestType(get(COL.testType)),
              executionType: toExecType(get(COL.execType)),
              automationStatus: toAutoStatus(get(COL.autoStatus)),
              priority: normalizePriority(get(COL.priority)),
              preconditions: get(COL.precond) || null,
              testData: get(COL.testData) || null,
              requirementId: get(COL.requirementId) || null,
              description: null,
              state: "Waiting Review",
              createdBy: this.currentUserId,
              folderId: this.folderIdForSave(),
            });
            if (block.steps.length > 0 && newTc?.testCaseId) {
              await stepsAndResultApi.save(newTc.testCaseId, block.steps, this.currentUserId);
            }
            this.importProgress.success++;
          } catch (err) {
            this.importProgress.failed++;
            const label = testcaseId || title || `#${this.importProgress.current}`;
            this.importProgress.currentTitle = `${label}: ${err.message}`;
          }
        }

        await this.load();
        this.importProgress.done = true;
        if (this.importProgress.cancelled) {
          this.importProgress.currentTitle = "Import cancelled.";
        } else if (this.importProgress.failed > 0) {
          this.importProgress.currentTitle =
            `Import complete: ${this.importProgress.success} succeeded, ${this.importProgress.failed} failed.`;
        } else {
          this.importProgress.currentTitle = "Import complete!";
        }
      } catch (err) {
        this.importProgress.show = true;
        this.importProgress.done = true;
        this.importProgress.failed++;
        this.importProgress.currentTitle = `Error: ${err.message}`;
      }
      input.value = "";
    },
    downloadParams() {
      if (!this.selectedSubProjectId) return null;
      const params = { subProjectId: this.selectedSubProjectId };
      if (this.selectedFolderId) params.folderId = this.selectedFolderId;
      if (this.selectingMode && this.selectedIds.length) {
        params.testCaseIds = this.selectedIds.join(",");
      }
      return params;
    },
    enterSelectMode() {
      this.selectingMode = true;
      this.selectedIds = [];
    },
    exitSelectMode() {
      this.selectingMode = false;
      this.selectedIds = [];
      this.showDeleteConfirm = false;
      this.closeBulkEdit();
    },
    toggleSelect(id) {
      if (!this.selectingMode) return;
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) this.selectedIds.push(id);
      else this.selectedIds.splice(idx, 1);
    },
    toggleSelectAll() {
      if (!this.selectingMode) return;
      const ids = this.filteredCases.map((tc) => tc.testCaseId);
      if (this.allVisibleSelected) {
        this.selectedIds = this.selectedIds.filter((id) => !ids.includes(id));
      } else {
        ids.forEach((id) => {
          if (!this.selectedIds.includes(id)) this.selectedIds.push(id);
        });
      }
    },
    onCaseRowClick(tc) {
      if (this.selectingMode) {
        this.toggleSelect(tc.testCaseId);
        return;
      }
      this.openDetailModal(tc);
    },
    async openBulkEdit() {
      if (!this.selectedIds.length) return;
      this.bulkForm = emptyBulkForm();
      this.bulkFormFieldErrors = { function: "", scenario: "", subScenario: "" };
      this.bulkEditError = "";
      this.showBulkEdit = true;
    },
    closeBulkEdit() {
      this.showBulkEdit = false;
      this.bulkEditSaving = false;
      this.bulkEditError = "";
      this.bulkForm = emptyBulkForm();
      this.bulkFormFieldErrors = { function: "", scenario: "", subScenario: "" };
    },
    onBulkFunctionChange(value) {
      const raw = String(value ?? "").trim();
      const exists = this.bulkFunctionSelectOptions.some((o) => String(o.value) === raw);
      this.bulkForm.functionIsNew = !!raw && !exists && !/^\d+$/.test(raw);
      this.bulkForm.scenarioId = "";
      this.bulkForm.scenarioIsNew = false;
      this.bulkForm.subScenarioId = "";
      this.bulkForm.subScenarioIsNew = false;
      this.bulkFormFieldErrors.function = "";
    },
    onBulkScenarioChange(value) {
      const raw = String(value ?? this.bulkForm.scenarioId ?? "").trim();
      const exists = this.bulkScenarioSelectOptions.some((o) => String(o.value) === raw);
      this.bulkForm.scenarioIsNew = !!raw && !exists && !/^\d+$/.test(raw);
      this.bulkForm.subScenarioId = "";
      this.bulkForm.subScenarioIsNew = false;
      this.bulkFormFieldErrors.scenario = "";
    },
    onBulkSubScenarioChange(value) {
      const raw = String(value ?? "").trim();
      const exists = this.bulkSubScenarioSelectOptions.some((o) => String(o.value) === raw);
      this.bulkForm.subScenarioIsNew = !!raw && !exists && !/^\d+$/.test(raw);
      this.bulkFormFieldErrors.subScenario = "";
    },
    validateBulkForm() {
      this.bulkFormFieldErrors = { function: "", scenario: "", subScenario: "" };
      const hasAny = !!(
        String(this.bulkForm.functionId || "").trim()
        || String(this.bulkForm.scenarioId || "").trim()
        || String(this.bulkForm.subScenarioId || "").trim()
      );
      if (!hasAny) this.bulkEditError = "Select at least one field to update.";
      return hasAny;
    },
    async saveBulkEdit() {
      this.bulkEditError = "";
      if (!this.validateBulkForm() || this.bulkEditSaving) return;
      this.bulkEditSaving = true;
      try {
        await this.loadFunctionOptions();
        await this.loadScenarios();
        const mapFunctionValue = (value) => {
          const raw = String(value || "").trim();
          if (!raw) return { id: "", isNew: false };
          if (/^\d+$/.test(raw)) return { id: raw, isNew: false };
          const found = (this.functionOptions || []).find(
            (o) => String(o.label || "").trim().toLowerCase() === raw.toLowerCase(),
          );
          if (found) return { id: String(found.value), isNew: false };
          return { id: raw, isNew: true };
        };
        const mapScenarioValue = (value) => {
          const raw = String(value || "").trim();
          if (!raw) return { id: "", isNew: false };
          if (/^\d+$/.test(raw)) return { id: raw, isNew: false };
          const found = (this.allScenarios || []).find(
            (s) => String(s.scenarioName || "").trim().toLowerCase() === raw.toLowerCase(),
          );
          if (found) return { id: String(found.scenarioId), isNew: false };
          return { id: raw, isNew: true };
        };
        const bulkFn = mapFunctionValue(this.bulkForm.functionId);
        const bulkSc = mapScenarioValue(this.bulkForm.scenarioId);
        const bulkSubRaw = String(this.bulkForm.subScenarioId || "").trim();
        const ids = [...this.selectedIds];
        for (const id of ids) {
          const tc = await realTestCasesApi.getById(id);
          const fallbackFn = mapFunctionValue(tc.functionId || this.caseFunctionName(tc));
          const fallbackSc = mapScenarioValue(tc.scenarioId || this.caseScenarioName(tc));
          const mergedForm = {
            functionId: bulkFn.id || fallbackFn.id,
            functionIsNew: bulkFn.id ? bulkFn.isNew : fallbackFn.isNew,
            scenarioId: bulkSc.id || fallbackSc.id,
            scenarioIsNew: bulkSc.id ? bulkSc.isNew : fallbackSc.isNew,
            subScenarioId: bulkSubRaw || String(tc.subScenarioId || this.caseSubScenarioName(tc) || "").trim(),
            subScenarioIsNew: !!bulkSubRaw && this.bulkForm.subScenarioIsNew,
          };
          const { functionId, scenarioId, subScenarioId } = await this.resolveFunctionScenarioSubScenario(mergedForm);
          await realTestCasesApi.update(id, {
            title: tc.title,
            description: tc.description,
            preconditions: tc.preconditions,
            testData: tc.testData,
            state: tc.state,
            priority: tc.priority,
            typeOfTestCase: tc.typeOfTestCase,
            automationStatus: tc.automationStatus,
            testType: tc.testType,
            executionType: tc.executionType,
            createdBy: tc.createdBy,
            requirementId: tc.requirementId,
            jiraLink: tc.jiraLink,
            tag: tc.tag,
            scenarioId: scenarioId || tc.scenarioId,
            functionId: functionId || tc.functionId,
            subScenarioId: subScenarioId || tc.subScenarioId || null,
            modifyBy: this.currentUserId,
          });
        }
        this.closeBulkEdit();
        this.exitSelectMode();
        await this.load();
        this.showToast("success", "Updated successfully.");
      } catch (e) {
        console.error("saveBulkEdit error:", e);
        this.bulkEditError = e.message || "Failed to update test cases";
      } finally {
        this.bulkEditSaving = false;
      }
    },
    async deleteSelected() {
      this.showDeleteConfirm = false;
      try {
        await Promise.all(this.selectedIds.map((id) => realTestCasesApi.delete(id, { modifyBy: this.currentUserId })));
        this.exitSelectMode();
        await this.load();
        this.showToast("success", "Deleted successfully.");
      } catch (e) {
        this.showToast("error", `Delete failed: ${e.message}`);
      }
    },
    onReviewScenarioFilterChange(value) {
      this.reviewFilters.scenario = value ?? "";
      this.reviewFilters.subScenario = "";
    },
    reviewSteps(tc) {
      return Array.isArray(tc?.steps) ? tc.steps : [];
    },
    reviewStepCount(tc) {
      return Math.max(this.reviewSteps(tc).length, 1);
    },
    reviewExtraSteps(tc) {
      return this.reviewSteps(tc).slice(1);
    },
    reviewModuleText(tc, index) {
      const steps = this.reviewSteps(tc);
      if (!steps.length) return "—";
      const step = steps[index];
      if (!step) return "—";
      return String(step.platform || "").trim() || "—";
    },
    reviewStepText(tc, index) {
      const steps = this.reviewSteps(tc);
      if (!steps.length) return "—";
      const step = steps[index];
      if (!step) return "—";
      const detail = String(step.stepDetail || step.action || "").trim();
      return `${index + 1}. ${detail || "—"}`;
    },
    reviewExpectedText(tc, index) {
      const steps = this.reviewSteps(tc);
      if (!steps.length) return "—";
      const step = steps[index];
      if (!step) return "—";
      return String(step.expectedResult || step.expected || "").trim() || "—";
    },
    unbindPdfStickyHeaders() {
      this._pdfStickyObserver?.disconnect();
      this._pdfStickyObserver = null;
    },
    syncPdfStickyHeaders() {
      this.unbindPdfStickyHeaders();
      if (!this.showReviewView || this.reviewViewMode !== "pdf") return;
      this.$nextTick(() => {
        const table = document.querySelector(".qa-tc-review-overlay .qa-tc-pdf-table");
        if (!table) return;
        const update = () => {
          const thead = table.querySelector("thead");
          const sc = table.querySelector(".qa-tc-pdf-section--sc td");
          const thH = thead ? Math.ceil(thead.getBoundingClientRect().height) : 40;
          const scH = sc ? Math.ceil(sc.getBoundingClientRect().height) : 28;
          table.style.setProperty("--qa-tc-pdf-th", `${thH}px`);
          table.style.setProperty("--qa-tc-pdf-sc", `${scH}px`);
        };
        update();
        if (typeof ResizeObserver === "undefined") return;
        this._pdfStickyObserver = new ResizeObserver(update);
        this._pdfStickyObserver.observe(table);
        const thead = table.querySelector("thead");
        if (thead) this._pdfStickyObserver.observe(thead);
      });
    },
    closeReviewView() {
      this.unbindPdfStickyHeaders();
      this.showReviewView = false;
      this.reviewViewMode = "standard";
      this.reviewCommentsOpen = true;
      this.reviewFilters = { search: "", scenario: "", subScenario: "", status: "", testType: "", priority: "" };
      this.reviewLoading = false;
      this.reviewError = "";
      this.reviewCases = [];
      this.reviewFocusId = null;
      this.reviewComments = [];
      this.reviewCommentText = "";
      this.reviewFeedbackId = null;
      this.reviewFeedbackText = "";
      this.showPendingFeedbackModal = false;
      this.pendingReviewFocusCase = null;
    },
    reviewCaseFunction(tc) {
      return tc?.functionName || tc?.function || "—";
    },
    reviewCaseScenario(tc) {
      return tc?.scenarioName || tc?.scenario || "—";
    },
    reviewCaseSubScenario(tc) {
      return tc?.subScenarioName || tc?.subScenario || "—";
    },
    applyReviewState(tc, next) {
      const idx = this.reviewCases.findIndex((item) => item.testCaseId === tc.testCaseId);
      if (idx !== -1) this.reviewCases.splice(idx, 1, { ...this.reviewCases[idx], ...next });
      const listIdx = this.cases.findIndex((item) => item.testCaseId === tc.testCaseId);
      if (listIdx !== -1) this.cases.splice(listIdx, 1, { ...this.cases[listIdx], state: next.state || this.cases[listIdx].state });
    },
    async loadReviewComments(testCaseId) {
      if (!testCaseId) {
        this.reviewComments = [];
        return;
      }
      this.reviewCommentsLoading = true;
      try {
        this.reviewComments = await realTestCasesApi.getActivity(testCaseId);
      } catch (e) {
        this.reviewComments = [];
      } finally {
        this.reviewCommentsLoading = false;
      }
    },
    focusReviewCase(tc, options = {}) {
      if (!tc) return;
      const switching = this.reviewFeedbackId && this.reviewFeedbackId !== tc.testCaseId;
      if (switching && !options.force) {
        this.pendingReviewFocusCase = tc;
        this.showPendingFeedbackModal = true;
        return;
      }
      this.reviewFocusId = tc.testCaseId;
      this.reviewCommentText = "";
      this.loadReviewComments(tc.testCaseId);
      this.$nextTick(() => {
        document.getElementById(`review-tc-${tc.testCaseId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    closePendingFeedbackModal() {
      this.showPendingFeedbackModal = false;
      this.pendingReviewFocusCase = null;
    },
    stayOnPendingFeedback() {
      this.closePendingFeedbackModal();
    },
    async savePendingFeedbackWithoutMessage() {
      const current = this.reviewFocusCase;
      const next = this.pendingReviewFocusCase;
      this.showPendingFeedbackModal = false;
      this.pendingReviewFocusCase = null;
      if (current) {
        await this.updateReviewState(current, "FeedBack", { stateRemark: "", allowEmptyFeedback: true });
      }
      this.cancelReviewFeedback();
      if (next) this.focusReviewCase(next, { force: true });
    },
    skipPendingFeedbackStatusChange() {
      const next = this.pendingReviewFocusCase;
      this.closePendingFeedbackModal();
      this.cancelReviewFeedback();
      if (next) this.focusReviewCase(next, { force: true });
    },
    reviewCommentBody(entry) {
      if (entry.entryType === "remark") return entry.remarkText || entry.newValue || "—";
      if (entry.entryType === "feedback") return entry.newValue || entry.remarkText || "—";
      if (entry.fieldName === "State") return `${entry.oldValue || "—"} → ${entry.newValue || "—"}`;
      return entry.remarkText || entry.newValue || "—";
    },
    onReviewStatusChange(value) {
      const tc = this.reviewFocusCase;
      if (!tc) return;
      const nextState = String(value || "").trim();
      if (!nextState) return;
      if (nextState === "FeedBack") {
        this.openReviewFeedback(tc);
        return;
      }
      this.cancelReviewFeedback();
      if (nextState === String(tc.state || "").trim()) return;
      this.updateReviewState(tc, nextState);
    },
    async updateReviewState(tc, nextState, extra = {}) {
      if (!tc?.testCaseId || !nextState) return;
      this.reviewStateSaving = tc.testCaseId;
      try {
        const updated = await realTestCasesApi.updateState(tc.testCaseId, {
          state: nextState,
          modifyBy: this.currentUserId,
          ...extra,
        });
        this.applyReviewState(tc, { state: updated.state || nextState });
        if (this.reviewFocusId === tc.testCaseId) this.loadReviewComments(tc.testCaseId);
      } catch (e) {
        window.alert(e.message || "Failed to update review state");
      } finally {
        this.reviewStateSaving = null;
      }
    },
    openReviewComments() {
      this.reviewFeedbackId = null;
      this.reviewFeedbackText = "";
      this.reviewCommentsOpen = true;
    },
    openReviewFeedback(tc) {
      this.focusReviewCase(tc);
      this.reviewFeedbackId = tc.testCaseId;
      this.reviewFeedbackText = "";
      this.reviewCommentsOpen = true;
    },
    cancelReviewFeedback() {
      this.reviewFeedbackId = null;
      this.reviewFeedbackText = "";
    },
    async submitReviewFeedback(tc) {
      if (!tc?.testCaseId) return;
      const text = String(this.reviewFeedbackText || "").trim();
      if (!text) {
        window.alert("Feedback message is required");
        return;
      }
      this.reviewStateSaving = tc.testCaseId;
      try {
        const updated = await realTestCasesApi.updateState(tc.testCaseId, {
          state: "FeedBack",
          stateRemark: text,
          modifyBy: this.currentUserId,
        });
        this.applyReviewState(tc, { state: updated.state || "FeedBack" });
        this.cancelReviewFeedback();
        this.focusReviewCase(tc);
      } catch (e) {
        window.alert(e.message || "Failed to save feedback");
      } finally {
        this.reviewStateSaving = null;
      }
    },
    async saveReviewComment() {
      const text = String(this.reviewCommentText || "").trim();
      if (!text || !this.reviewFocusId || this.reviewCommentSaving) return;
      this.reviewCommentSaving = true;
      try {
        this.reviewComments = await realTestCasesApi.addRemark(this.reviewFocusId, {
          remarkText: text,
          actorUserId: this.currentUserId,
        });
        this.reviewCommentText = "";
      } catch (e) {
        window.alert(e.message || "Failed to save comment");
      } finally {
        this.reviewCommentSaving = false;
      }
    },
    async viewPdfReport() {
      const source = this.selectingMode && this.selectedIds.length
        ? this.filteredCases.filter((tc) => this.selectedIds.includes(tc.testCaseId))
        : this.filteredCases;
      if (!source.length) {
        window.alert("No test cases to view.");
        return;
      }
      this.showDownloadMenu = false;
      this.showReviewView = true;
      this.reviewLoading = true;
      this.reviewError = "";
      this.reviewCases = [];
      this.reviewFocusId = null;
      this.reviewComments = [];
      try {
        const withSteps = await Promise.all(
          source.map(async (tc) => {
            let steps = [];
            try {
              const raw = await stepsAndResultApi.getByCase(tc.testCaseId);
              steps = Array.isArray(raw)
                ? raw.map((s) => ({
                    platform: s.platform || "",
                    stepDetail: s.stepDetail || s.action || "",
                    expectedResult: s.expectedResult || s.expected || "",
                  }))
                : [];
            } catch (_) {
              steps = [];
            }
            return { ...tc, steps };
          }),
        );
        this.reviewCases = withSteps;
        this.focusReviewCase(withSteps[0]);
      } catch (e) {
        this.reviewError = e.message || "Failed to load test cases";
      } finally {
        this.reviewLoading = false;
      }
    },
    async downloadBlob(kind) {
      const params = this.downloadParams();
      if (!params) {
        window.alert("Select a sub-project before downloading.");
        return;
      }
      this.showDownloadMenu = false;
      try {
        const { blob, fileName } = kind === "pdf"
          ? await reportsApi.downloadTestCasePdf("", params)
          : await reportsApi.downloadTestCaseExcel("", params);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const dateTag = new Date().toISOString().substring(0, 10);
        const fallback = `TestCase_export_${dateTag}.${kind === "pdf" ? "pdf" : "xlsx"}`;
        a.download = fileName || fallback;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        window.alert(e.message || "Download failed");
      }
    },
    downloadPdfReport() {
      this.downloadBlob("pdf");
    },
    downloadExcelReport() {
      this.downloadBlob("excel");
    },
  },
};
</script>

<style scoped>
.pm-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.pm-page-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pm-page-col {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.pm-page-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pm-page-tab-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qa-tc-inset {
  flex: 1;
  min-height: 0;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  padding: 1rem;
}

.qa-tc-back {
  flex-shrink: 0;
  margin-bottom: 0.35rem;
}

.qa-tc-total-bar {
  flex: 0 0 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.35rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #344767;
  margin: 0 0 0.55rem;
  padding-right: 1rem;
  line-height: 1;
  overflow: visible;
  position: relative;
  z-index: 25;
}

.qa-tc-total-count {
  white-space: nowrap;
}

.qa-tc-total-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  position: relative;
  z-index: 26;
  pointer-events: auto;
}

.qa-tc-total-action {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 26;
  pointer-events: auto;
  border-radius: 0.25rem;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1;
  padding: 0.12rem 0.45rem;
  cursor: pointer;
  white-space: nowrap;
}

.qa-tc-total-action:disabled {
  opacity: 0.45;
  cursor: default;
  pointer-events: none;
}

.qa-tc-total-action--edit {
  background: #82d616;
  border: 1px solid #82d616;
  color: #1b4332;
}

.qa-tc-total-action--delete {
  background: #ea0606;
  border: 1px solid #ea0606;
  color: #fff;
}

.qa-tc-table-empty {
  padding: 2.2rem 1rem;
  text-align: center;
  color: #8392ab;
  font-size: 0.8rem;
}

.qa-tc-back-btn {
  color: #8392ab;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
}

.qa-tc-back-btn:hover {
  color: #344767;
}

.qa-tc-toolbar {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
  position: relative;
  z-index: 20;
  overflow: visible;
}

.qa-tc-search {
  min-width: 220px;
  width: 260px;
}

.qa-tc-filter {
  min-width: 160px;
  width: 180px;
  position: relative;
  z-index: 21;
  overflow: visible;
}

.qa-tc-toolbar-actions {
  margin-left: auto;
  display: flex;
  align-items: flex-end;
}

.qa-tc-folder-list-row {
  cursor: pointer;
  align-items: flex-start;
}

.qa-tc-folder-list-row .qa-tc-name {
  line-height: 1.35;
}

.qa-tc-folder-list-row .col-count,
.qa-tc-folder-list-row .col-status,
.qa-tc-folder-list-row .col-action {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  min-height: 1.15rem;
  padding-top: 0.1rem;
  white-space: nowrap;
}

.qa-tc-folder-list-row .col-status {
  flex: 1.15;
  min-width: 128px;
}

.qa-tc-folder-list-row .col-action {
  flex-wrap: nowrap;
  gap: 0.5rem;
}

.qa-tc-folder-list-row .col-action .btn-link {
  text-decoration: none;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 1.15rem;
  margin-bottom: 0;
}

.qa-tc-folder-list-row .col-action .btn-link i {
  font-size: 0.85rem;
  line-height: 1;
}

.qa-tc-folder-list-icon {
  color: #5e72e4;
}

.folder-tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
  border-radius: 12px;
  background: transparent;
}

.folder-tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.folder-tab-item.active {
  color: #ffffff;
  background: #344767;
}

.folder-modal-box--edit {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.folder-modal-tabs {
  padding: 1rem 1rem 0.75rem;
  flex-shrink: 0;
}

.folder-modal-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-top: 1px solid #e9ecef;
}

.folder-modal-split {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.folder-modal-pane-form {
  flex: 1 1 50%;
  width: 50%;
  max-width: 50%;
  min-width: 0;
  padding: 1rem;
  overflow: visible;
  border-right: 1px solid #e9ecef;
}

.folder-modal-pane-form :deep(.base-select--open) {
  position: relative;
  z-index: 20;
}

.folder-modal-pane-comment {
  flex: 1 1 50%;
  width: 50%;
  max-width: 50%;
  min-width: 0;
  padding: 1rem;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.folder-modal-comment-compose {
  flex: 0 0 auto;
  padding-bottom: 0.75rem;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

.folder-modal-comment-input {
  width: 100%;
  height: 88px;
  resize: none;
}

.folder-modal-comment-history {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.folder-modal-history {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;
}

.folder-modal-history-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.folder-modal-footer {
  min-height: 58px;
  flex-shrink: 0;
}

.col-action {
  flex: 0.9;
  min-width: 72px;
}

.action-edit { color: #4361ee; }
.action-edit:hover { color: #2d4bc8; }
.action-delete { color: #e74c3c; }
.action-delete:hover { color: #c0392b; }
.action-edit:disabled,
.action-delete:disabled {
  color: #cfd5de;
  cursor: not-allowed;
  opacity: 1;
}

.qa-tc-empty {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.qa-tc-table {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}

.qa-tc-table .pm-table-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.qa-tc-table .pm-table-header.qa-tc-tree-header {
  padding: 0.65rem 1rem 0.65rem 2.5rem;
}

.qa-tc-row {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 0.9rem 1rem;
  border: 0;
  border-bottom: 1px solid #e9ecef;
  background: #fff;
  text-align: left;
  transition: background 0.15s;
}

.qa-tc-row:last-child {
  border-bottom: none;
}

.qa-tc-row {
  cursor: pointer;
}

.qa-tc-row:hover {
  background: #fafbfc;
}

.col-project,
.col-case,
.col-scenario {
  flex: 2.4;
  min-width: 0;
}

.col-count,
.col-status,
.col-priority {
  flex: 0.9;
  min-width: 90px;
}

.qa-tc-name {
  font-size: 0.84rem;
  font-weight: 700;
  color: #344767;
}

.qa-tc-sub {
  font-size: 0.75rem;
  color: #8392ab;
  margin-top: 2px;
}

.pm-cell-secondary {
  font-size: 0.78rem;
  color: #67748e;
}

.qa-tc-chevron {
  color: #8392ab;
  font-size: 0.72rem;
}

.count-pill {
  display: inline-block;
  min-width: 1.6rem;
  padding: 0.15rem 0.5rem;
  border-radius: 50px;
  background: #eef2ff;
  color: #3d4ed7;
  font-size: 0.72rem;
  font-weight: 700;
}

.status-pill {
  display: inline-block;
  padding: 0.2rem 0.65rem;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.pill-active { background: #d4f5e2; color: #28a745; border: 1px solid #b7efd0; }
.pill-inactive { background: #f0f0f0; color: #8392ab; border: 1px solid #e0e0e0; }
.pill-inprogress { background: #d1ecf1; color: #0c7a9a; border: 1px solid #bee5eb; }
.pill-feedback { background: #ffe8d9; color: #c2410c; border: 1px solid #fdba74; }
.pill-high { background: #fde8e8; color: #e74c3c; border: 1px solid #f9c0c0; }
.pill-medium { background: #fff3cd; color: #c87a00; border: 1px solid #ffe7a0; }
.pill-low { background: #d4f5e2; color: #28a745; border: 1px solid #b7efd0; }
.qa-tc-tree-header .col-status,
.qa-tc-tree-header .col-priority,
.qa-tc-case-row .col-status,
.qa-tc-case-row .col-priority {
  display: flex;
  align-items: center;
  justify-content: center;
}

.qa-tc-tree-header .col-id,
.qa-tc-case-row .col-id {
  flex: 0.75;
  min-width: 72px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
}

.qa-tc-tree-header .col-name,
.qa-tc-case-row .col-name {
  flex: 4;
  min-width: 0;
}

.qa-tc-case-row .col-name {
  margin-left: -1.75rem;
}

.qa-tc-id {
  font-size: 0.75rem;
  font-weight: 700;
  color: #67748e;
}

.qa-tc-case-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #344767;
}

.qa-tc-case-row:hover .qa-tc-case-title {
  color: #5e72e4;
}

.qa-tc-type-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 0.5rem;
  font-weight: 600;
  line-height: 1.1;
  padding: 0.06rem 0.24rem;
  border: 1px solid transparent;
}

.qa-tc-type-pill--positive {
  background: #e8f7ef;
  color: #1e7f46;
  border-color: #bde7cd;
}

.qa-tc-type-pill--negative {
  background: #fff4e8;
  color: #b45309;
  border-color: #fcd9b6;
}

.qa-tc-type-pill--default {
  background: #f1f3f5;
  color: #67748e;
  border-color: #e2e5e9;
}

.tc-toolbar-wrap {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  max-width: 100%;
  overflow: visible;
  margin-bottom: 0.85rem;
  position: relative;
  z-index: 20;
}

.tc-toolbar-main {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.5rem;
  min-width: 0;
}

.tc-toolbar-primary {
  display: grid;
  grid-template-columns: 200px 130px 130px 130px auto;
  gap: 0.5rem;
  align-items: end;
  flex: 0 0 auto;
  min-width: 0;
}

.tc-filter-field--status,
.tc-filter-field--priority,
.tc-filter-field--type {
  width: 130px;
  max-width: 130px;
}

.tc-toolbar-advanced {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(140px, 1.2fr) minmax(140px, 1.2fr) auto;
  gap: 0.5rem;
  align-items: end;
  padding: 0.65rem 0.75rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  min-width: 0;
  overflow: visible;
  position: relative;
  z-index: 21;
}

.tc-filter-field {
  min-width: 0;
}

.tc-filter-label--hidden {
  visibility: hidden;
}

.tc-search-input-group {
  width: 100%;
  min-width: 0;
}

.tc-filters-toggle,
.tc-clear-filters-btn {
  white-space: nowrap;
  height: 28px;
  padding: 0 0.62rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}

.tc-filters-toggle--active {
  border-color: #5e72e4;
  color: #5e72e4;
}

.tc-filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.1rem;
  height: 1.1rem;
  margin-left: 0.35rem;
  padding: 0 0.3rem;
  border-radius: 999px;
  background: #5e72e4;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
}

.tc-toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  min-width: 0;
  margin-left: auto;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 0.5rem;
}

.tc-action-field {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-end;
  min-width: 0;
}

.tc-action-label {
  display: block;
  margin-bottom: 0.25rem;
  height: 1.05rem;
  line-height: 1.05rem;
  white-space: nowrap;
  text-align: right;
}

.tc-action-field--add {
  align-items: center;
}

.tc-action-field--add .qa-tc-copy-link {
  display: block;
  width: 100%;
  text-align: center;
}

.qa-tc-copy-link {
  border: none;
  background: none;
  box-shadow: none;
  padding: 0;
  color: #fd7e14 !important;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
}

.qa-tc-copy-link:hover,
.qa-tc-copy-link:focus {
  color: #d9480f !important;
  text-decoration: underline;
}

.tc-copy-locked {
  pointer-events: none;
  opacity: 0.55;
}

.qa-tc-copy-link.disabled {
  pointer-events: none;
  opacity: 0.45;
}

.qa-tc-copy-step-modal {
  width: 960px;
  max-width: 96vw;
  height: 720px;
  max-height: 86vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.qa-tc-copy-modal-header,
.qa-tc-copy-step-filters,
.qa-tc-copy-modal-footer {
  flex-shrink: 0;
}

.qa-tc-copy-step-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.qa-tc-copy-step-table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.qa-tc-copy-step-row {
  cursor: pointer;
}

.qa-tc-copy-step-row.is-checked {
  background: #fff7ed;
}

.qa-tc-copy-step-cell {
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 260px;
}

.tc-step-handle {
  cursor: grab;
  user-select: none;
}

.tc-step-handle:active {
  cursor: grabbing;
}

.tc-step-row.is-drag-over {
  outline: 1px dashed #fd7e14;
  border-radius: 8px;
  background: #fff7ed;
}

.tc-step-row.is-dragging {
  opacity: 0.55;
}

.qa-tc-copy-steps {
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.qa-tc-copy-step {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  margin: 0;
  cursor: pointer;
  border-bottom: 1px solid #f1f3f5;
}

.qa-tc-copy-step:last-child {
  border-bottom: none;
}

.qa-tc-copy-step.is-checked {
  background: #fff7ed;
}

.qa-tc-copy-choice-modal {
  width: 960px;
  max-width: 96vw;
  height: 720px;
  max-height: 86vh;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.qa-tc-copy-choice-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.qa-tc-copy-choice-row {
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f3f5;
}

.qa-tc-copy-choice-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.qa-tc-copy-compare {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}

.qa-tc-copy-compare-card {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0;
  padding: 0.7rem 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  cursor: pointer;
  background: #fff;
}

.qa-tc-copy-compare-card.is-selected {
  border-color: #344767;
  background: #f8f9fa;
}

.qa-tc-copy-compare-value {
  display: block;
  font-size: 0.75rem;
  color: #344767;
  white-space: pre-wrap;
  word-break: break-word;
}

.tc-action-dropdown-wrap {
  position: relative;
  z-index: 30;
  min-width: 110px;
  width: 110px;
}

.tc-import-dropdown-wrap {
  min-width: 145px;
  width: 145px;
}

.tc-import-menu {
  min-width: 220px;
  width: 220px;
}

.tc-action-trigger {
  width: 100%;
  height: 28px;
  border: 1px solid #d6d9df;
  border-radius: 8px;
  background: #fff;
  color: #344767;
  font-size: 0.72rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.5rem;
}

.tc-action-trigger i {
  font-size: 0.62rem;
  color: #5c6475;
}

.upload-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 300;
  overflow: hidden;
  padding: 0.4rem 0;
}

.tc-download-menu {
  min-width: 160px;
  width: 160px;
}

.upload-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.55rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 500;
  color: #344767;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}

.upload-menu-item:hover {
  background: #f8f9fa;
}

.upload-menu-divider {
  border-top: 1px solid #e9ecef;
  margin: 0.3rem 0;
}

.upload-toast {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 9999;
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-size: 0.825rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.upload-toast.success { background: #f0fff4; border: 1px solid #c6f6d5; color: #276749; }
.upload-toast.error { background: #fff5f5; border: 1px solid #fed7d7; color: #c53030; }

.tc-toolbar-wrap .btn {
  white-space: nowrap;
  height: 28px;
  padding: 0 0.62rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tc-toolbar-wrap .input-group-sm > .form-control,
.tc-toolbar-wrap .input-group-sm > .input-group-text {
  height: 28px;
  min-height: 28px;
  font-size: 0.72rem;
}

@media (max-width: 992px) {
  .tc-toolbar-primary {
    grid-template-columns: minmax(160px, 1fr) 130px;
  }

  .tc-toolbar-advanced {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Function / Scenario / Sub-Scenario folder rows — match Test Case tab */
.qa-tc-folder-row {
  display: flex;
  align-items: center;
  min-width: 0;
  width: 100%;
  cursor: pointer;
}

.qa-tc-folder-chevron {
  width: 12px;
  font-size: 0.65rem;
  color: #8392ab;
  text-align: center;
}

.qa-tc-folder-icon--function {
  color: #5e72e4;
}

.qa-tc-folder-icon--scenario {
  color: #fb8c00;
}

.qa-tc-folder-row--function {
  padding: 0.6rem 1rem;
  background: #e8edf5;
  border-bottom: 1px solid #dde2eb;
  font-size: 0.75rem;
  font-weight: 700;
  color: #344767;
}

.qa-tc-folder-row--scenario {
  padding: 0.55rem 1rem 0.55rem 2rem;
  background: #f3f4f6;
  border-bottom: 1px solid #e9ecef;
  border-top: 1px solid #e2e5e9;
  font-size: 0.75rem;
  font-weight: 700;
  color: #344767;
}

.qa-tc-folder-row--sub {
  padding: 0.4rem 1rem 0.4rem 4rem;
  background: #fafbfc;
  border-bottom: 1px solid #f1f3f5;
  font-size: 0.75rem;
  font-weight: 500;
  color: #8392ab;
}

.qa-tc-folder-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qa-tc-folder-count {
  flex-shrink: 0;
  margin-left: 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: #67748e;
}

.qa-tc-case-row--nested {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 4.25rem;
  border-bottom: 1px solid #f1f3f5;
  background: #fff;
  cursor: pointer;
  text-align: left;
}

.qa-tc-case-row--nested:hover {
  background: #f5f8ff;
}

.qa-tc-case-row--selected {
  background: #eef3ff !important;
}

.qa-tc-tree-header .col-check,
.qa-tc-case-row .col-check {
  flex: 0 0 36px;
  min-width: 36px;
  display: flex;
  align-items: center;
}

.qa-tc-review-overlay {
  position: fixed;
  inset: 0;
  z-index: 3200;
  background: #fff;
}

.qa-tc-review-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.qa-tc-review-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.25rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

.qa-tc-review-body {
  flex: 1;
  min-height: 0;
  display: flex;
}

.qa-tc-review-doc {
  flex: 1;
  min-width: 0;
  overflow: auto;
  padding: 1.25rem;
}

.qa-tc-review-status {
  margin: 3rem auto;
  text-align: center;
  color: #67748e;
}

.qa-tc-review-content {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 0 1.5rem;
}

.qa-tc-review-mode {
  display: inline-flex;
  align-items: center;
  padding: 3px;
  border: 1px solid #dce1ea;
  border-radius: 8px;
  background: #f8f9fb;
}

.qa-tc-review-mode-btn {
  border: 0;
  background: transparent;
  color: #67748e;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.42rem 0.7rem;
  border-radius: 6px;
  cursor: pointer;
}

.qa-tc-review-mode-btn.active {
  background: #fff;
  color: #344767;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}

.qa-tc-review-content--pdf {
  width: 100%;
  max-width: none;
  margin: 0;
}

.qa-tc-pdf-wrap {
  overflow: auto;
  background: #fff;
  width: 100%;
}

.qa-tc-pdf-table {
  width: 100%;
  min-width: 960px;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.78rem;
  color: #344767;
}

.qa-tc-pdf-col-id { width: 35px; }
.qa-tc-pdf-col-name { width: 180px; }
.qa-tc-pdf-col-type { width: 40px; }
.qa-tc-pdf-col-priority { width: 40px; }
.qa-tc-pdf-col-pre,
.qa-tc-pdf-col-data { width: 120px; }
.qa-tc-pdf-col-module { width: 70px; }
.qa-tc-pdf-col-step,
.qa-tc-pdf-col-expected { width: 180px; }

.qa-tc-pdf-table th {
  background: #344767;
  color: #fff;
  font-weight: 700;
  font-size: 0.72rem;
  text-align: center;
  padding: 0.45rem 0.5rem;
  border: 0.5px solid #d2d2dc;
  white-space: nowrap;
}

.qa-tc-pdf-table td {
  border: 0.5px solid #d2d2dc;
  padding: 0.45rem 0.5rem;
  vertical-align: top;
  background: #fff;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.qa-tc-pdf-section td {
  font-weight: 700;
  text-align: left;
}

.qa-tc-pdf-section--sc td {
  background: #f5f5f5;
  color: #344767;
}

.qa-tc-pdf-section--sub td {
  background: #fff;
  color: #67748e;
  padding-left: 1.1rem;
}

.qa-tc-pdf-id {
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
}

.qa-tc-pdf-name {
  font-weight: 700;
}

.qa-tc-pdf-pre {
  white-space: pre-wrap;
}

.qa-tc-pdf-case {
  cursor: pointer;
}

.qa-tc-pdf-case--active td {
  background: #eef3ff;
}

.qa-tc-review-title-block {
  margin-bottom: 1rem;
}

.qa-tc-review-doc-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: #212529;
  letter-spacing: 0.02em;
}

.qa-tc-review-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  background: #fafafa;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
}

.qa-tc-review-info-grid span {
  display: block;
  font-size: 0.7rem;
  color: #8392ab;
}

.qa-tc-review-info-grid strong {
  display: block;
  font-size: 0.82rem;
  color: #344767;
  margin-top: 0.15rem;
}

.qa-tc-review-section {
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.4rem 0.65rem;
  margin: 0.65rem 0 0.35rem;
  border-radius: 6px;
}

.qa-tc-review-section--fn {
  background: #344767;
  color: #fff;
}

.qa-tc-review-section--sc {
  background: #eef2ff;
  color: #344767;
}

.qa-tc-review-section--sub {
  background: #f8f9fa;
  color: #67748e;
}

.qa-tc-review-case {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 0.85rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
}

.qa-tc-review-case--active {
  border-color: #5e72e4;
  box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.15);
}

.qa-tc-review-case-top {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
}

.qa-tc-review-case-id {
  font-size: 1rem;
  font-weight: 800;
  color: #344767;
  letter-spacing: 0.01em;
}

.qa-tc-review-case-name {
  font-size: 1.28rem;
  font-weight: 800;
  color: #212529;
  line-height: 1.3;
}

.qa-tc-review-case-actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
}

.qa-tc-review-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem 1.15rem;
  font-size: 0.78rem;
  color: #67748e;
  margin: 0.55rem 0 0.7rem;
}

.qa-tc-review-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.7rem;
}

.qa-tc-review-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  margin-bottom: 0.2rem;
}

.qa-tc-review-value {
  font-size: 0.8rem;
  color:rgb(18, 56, 94);;
  white-space: pre-wrap;
  margin: 0;
}

.qa-tc-review-steps {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.qa-tc-review-col-no { width: 42px; }
.qa-tc-review-col-module { width: 160px; }
.qa-tc-review-col-step { width: 40%; }
.qa-tc-review-col-expected { width: 40%; }

.qa-tc-review-steps th,
.qa-tc-review-steps td {
  border: 1px solid #e9ecef;
  padding: 0.4rem 0.5rem;
  vertical-align: top;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.qa-tc-review-steps th {
  background: #344767;
  color: #fff;
  font-weight: 700;
}

.qa-tc-review-feedback {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #e9ecef;
}

.qa-tc-review-header-title {
  min-width: 0;
}

.qa-tc-review-header-title h6 {
  font-size: 1.05rem;
  max-width: 52vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qa-tc-review-side {
  width: 340px;
  flex-shrink: 0;
  background: #fff;
  border-left: 1px solid #e9ecef;
  padding: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.qa-tc-review-side-title {
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  color:rgb(19, 21, 24);
  letter-spacing: 0.04em;
}

.qa-tc-review-comments {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.qa-tc-review-comment {
  font-size: 1rem;
  color:rgb(23, 43, 77);
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f3f5;
}

.qa-tc-review-comment strong {
  font-size: 0.65rem;
}

.qa-tc-review-comment .d-flex > span {
  font-size: 0.68rem;
  color: #8392ab;
}

.qa-tc-review-comment p {
  margin-top: 0.35rem;
  white-space: pre-wrap;
}

@media (max-width: 1100px) {
  .qa-tc-review-info-grid {
    grid-template-columns: 1fr 1fr;
  }
}

/* Create Test Case modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

/* Jira requirement picker */
.jira-tag {
  display: inline-flex;
  align-items: center;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  margin: 2px 2px 2px 0;
}

.jira-tag-remove {
  background: none;
  border: none;
  color: #0c7a9a;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0 0 0 4px;
  line-height: 1;
}

.qa-tc-req-add-btn {
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.45rem 0.75rem;
}

.jira-issue-list {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.jira-issue-item {
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

.jira-issue-item:last-child {
  border-bottom: none;
}

.jira-issue-item:hover {
  background: #f8f9fa;
}

.jira-issue-selected {
  background: #eef9f0;
}

/* Detail modal tabs — same as Test Plan > Test Case */
.tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 0.3rem;
  border-radius: 12px;
  background: transparent;
}

.tab-item {
  flex: 1 1 0;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-item.active {
  color: #ffffff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}

/* Detail modal history */
.activity-history-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.activity-history-item {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.55rem 0.65rem;
}

.activity-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
}

.badge-change {
  background: #fff3cd;
  color: #856404;
}

.badge-sync {
  background: #e8f4fd;
  color: #0c7a9a;
}

.badge-feedback {
  background: #fde8e8;
  color: #9a3b0c;
}

.badge-remark {
  background: #e8f4fd;
  color: #0c7a9a;
}

.activity-text {
  white-space: pre-wrap;
  word-break: break-word;
  color: #344767;
}

.activity-subtasks-history,
.activity-sync-history {
  color: #344767;
}

.activity-subtasks-table {
  font-size: 0.72rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.activity-subtasks-label {
  font-weight: 700;
  letter-spacing: 0.04em;
}

.activity-step-change {
  display: block;
  width: 100%;
  margin-bottom: 0.75rem;
}

.activity-step-change:last-child {
  margin-bottom: 0;
}

.activity-step-diff-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 0.55rem;
  width: 100%;
}

.activity-step-diff-grid > .activity-step-pane {
  flex: 1 1 0;
  min-width: 0;
  width: 50%;
}

.activity-step-pane {
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  border: 1px solid;
  min-width: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.activity-step-pane--before {
  background: #fff5f5;
  border-color: #e8a0a8;
}

.activity-step-pane--after {
  background: #f0fdf4;
  border-color: #86efac;
}

.activity-step-pane-label {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.2rem 0.55rem;
  border-radius: 4px;
  margin-bottom: 0.55rem;
}

.activity-step-pane--before .activity-step-pane-label {
  background: #f8d7da;
  color: #842029;
}

.activity-step-pane--after .activity-step-pane-label {
  background: #d1e7dd;
  color: #0f5132;
}
</style>

<style>
/* Edit Folder is teleported to body — unscoped so height actually applies */
.folder-modal-box--edit {
  height: 720px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.folder-modal-box--edit .folder-modal-body {
  flex: 1 1 auto;
  min-height: 0;
}

.folder-modal-box--edit .folder-modal-comment-input {
  height: 80px;
  resize: none;
}

/* View Test Case overlay is teleported to body — unscoped so layout actually applies */
.qa-tc-review-overlay.is-standard,
.qa-tc-review-overlay.is-pdf,
.qa-tc-review-overlay.is-standard .qa-tc-review-shell,
.qa-tc-review-overlay.is-pdf .qa-tc-review-shell,
.qa-tc-review-overlay.is-standard .qa-tc-review-body,
.qa-tc-review-overlay.is-pdf .qa-tc-review-body,
.qa-tc-review-overlay.is-standard .qa-tc-review-doc,
.qa-tc-review-overlay.is-pdf .qa-tc-review-doc {
  background: #fff !important;
}

.qa-tc-review-overlay.is-standard .qa-tc-review-doc,
.qa-tc-review-overlay.is-pdf .qa-tc-review-doc {
  width: 100% !important;
  max-width: none !important;
}

.qa-tc-review-overlay.is-pdf .qa-tc-review-doc {
  padding-top: 0 !important;
}

.qa-tc-review-overlay.is-pdf .qa-tc-review-title-block {
  padding-top: 1.25rem;
}

.qa-tc-review-overlay.is-standard .qa-tc-review-content,
.qa-tc-review-overlay.is-pdf .qa-tc-review-content {
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
}

.qa-tc-review-overlay .qa-tc-review-header-title {
  min-width: 0;
}

.qa-tc-review-overlay .qa-tc-review-filters {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: minmax(180px, 1.4fr) repeat(5, minmax(130px, 1fr));
  gap: 0.75rem;
  padding: 0.7rem 1.25rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

@media (max-width: 1100px) {
  .qa-tc-review-overlay .qa-tc-review-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .qa-tc-review-overlay .qa-tc-review-filter-field--search {
    grid-column: 1 / -1;
  }
}

.qa-tc-review-overlay .qa-tc-review-filter-field label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  margin-bottom: 0.25rem;
}

.qa-tc-review-overlay .qa-tc-review-header-title h6 {
  font-size: 1.05rem;
  max-width: 52vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qa-tc-review-overlay .qa-tc-review-case-id {
  font-size: 1rem;
  font-weight: 800;
  color: #344767;
}

.qa-tc-review-overlay .qa-tc-review-case-name {
  font-size: 1.28rem;
  font-weight: 800;
  color: #212529;
  line-height: 1.3;
}

.qa-tc-review-overlay .qa-tc-review-body {
  position: relative;
}

.qa-tc-review-overlay .qa-tc-review-type-row {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  font-size: 0.78rem;
  color: #67748e;
  margin-top: 0.2rem;
}

.qa-tc-review-overlay .qa-tc-review-type-item {
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
}

.qa-tc-review-overlay .qa-tc-review-type-val {
  display: inline-block;
  min-width: 8ch;
  font-weight: 800;
}

.qa-tc-review-overlay .qa-tc-review-type-val.is-positive {
  color: #198754;
}

.qa-tc-review-overlay .qa-tc-review-type-val.is-negative {
  color: #dc3545;
}

.qa-tc-review-overlay .qa-tc-review-priority-val {
  font-weight: 800;
}

.qa-tc-review-overlay .qa-tc-review-priority-val.is-high {
  color: #dc3545;
}

.qa-tc-review-overlay .qa-tc-review-priority-val.is-medium {
  color: #fd7e14;
}

.qa-tc-review-overlay .qa-tc-review-priority-val.is-low {
  color: #0ea5e9;
}

.qa-tc-review-overlay .qa-tc-review-hierarchy {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  margin-top: 1.15em;
  margin-bottom: 1.15em;
  font-size: 0.78rem;
  color:rgb(18, 56, 94);
}

.qa-tc-review-overlay .qa-tc-review-fields {
  margin-top: 0;
}

.qa-tc-review-overlay .qa-tc-review-steps {
  width: 100%;
  table-layout: fixed;
}

.qa-tc-review-overlay .qa-tc-review-col-no { width: 50px; }
.qa-tc-review-overlay .qa-tc-review-col-module { width: 80px; }
.qa-tc-review-overlay .qa-tc-review-col-step { width: 45%; }
.qa-tc-review-overlay .qa-tc-review-col-expected { width: 45%; }

.qa-tc-review-overlay .qa-tc-review-steps th,
.qa-tc-review-overlay .qa-tc-review-steps td {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.qa-tc-review-overlay .qa-tc-review-comment-body {
  margin-top: 0.35rem;
}

.qa-tc-review-overlay .qa-tc-review-comment-body,
.qa-tc-review-overlay .qa-tc-review-comment-body span,
.qa-tc-review-overlay .qa-tc-review-comment-body div {
  font-size: 0.85rem;
  color: #344767;
}

.qa-tc-review-overlay .qa-tc-review-k {
  color: #212529;
  font-weight: 800;
}

.qa-tc-review-overlay .qa-tc-review-label {
  color: #212529 !important;
  font-weight: 800;
}

.qa-tc-review-overlay .qa-tc-review-fb-btn {
  font-size: 0.62rem !important;
  padding: 0.12rem 0.4rem !important;
  line-height: 1.2 !important;
  min-height: 0;
  color: #7a5b00 !important;
  background: #f6c343 !important;
  border-color: #d4a017 !important;
}

.qa-tc-review-overlay .qa-tc-review-side,
.qa-tc-review-overlay .qa-tc-review-comment,
.qa-tc-review-overlay .qa-tc-review-comment p,
.qa-tc-review-overlay .qa-tc-review-comment strong {
  font-family: inherit;
}

.qa-tc-review-overlay .status-pill--sm {
  font-size: 0.62rem;
  padding: 0.12rem 0.4rem;
}

.qa-tc-review-overlay .bo-checkbox--xs {
  gap: 0.28rem;
}

.qa-tc-review-overlay .bo-checkbox--xs .bo-checkbox-box {
  width: 14px;
  height: 14px;
  border-width: 1.5px;
  border-radius: 0.25rem;
}

.qa-tc-review-overlay .bo-checkbox--xs .bo-checkbox-box::after {
  width: 3px;
  height: 7px;
  border-right-width: 1.5px;
  border-bottom-width: 1.5px;
}

.qa-tc-review-overlay .bo-checkbox--xs .bo-checkbox-label {
  font-size: 0.68rem;
}

.qa-tc-review-overlay .qa-tc-review-side {
  width: 340px;
  flex-shrink: 0;
  background: #fff;
  border-left: 1px solid #e9ecef;
  padding: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.qa-tc-review-overlay .qa-tc-review-side-status {
  flex-shrink: 0;
  padding-bottom: 0.85rem;
  margin-bottom: 0.85rem;
  border-bottom: 1px solid #e9ecef;
}

.qa-tc-review-overlay .qa-tc-review-side-status-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
}

.qa-tc-review-overlay .qa-tc-review-side-comments {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qa-tc-review-overlay .qa-tc-review-side-compose {
  flex-shrink: 0;
}

.qa-tc-review-overlay .qa-tc-review-side-history {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.qa-tc-review-overlay .qa-tc-review-side,
.qa-tc-review-overlay .qa-tc-review-comment,
.qa-tc-review-overlay .qa-tc-review-comment p,
.qa-tc-review-overlay .qa-tc-review-comment strong {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.qa-tc-review-overlay .qa-tc-review-comment p {
  white-space: pre-wrap;
}

.qa-tc-review-overlay .qa-tc-pdf-wrap,
.qa-tc-review-overlay .qa-tc-pdf-table {
  width: 100%;
  max-width: none;
}

.qa-tc-review-overlay .qa-tc-pdf-wrap {
  overflow: visible;
}

.qa-tc-review-overlay .qa-tc-pdf-table {
  table-layout: fixed;
  min-width: 960px;
  border-collapse: separate;
  border-spacing: 0;
}

.qa-tc-review-overlay .qa-tc-pdf-table th,
.qa-tc-review-overlay .qa-tc-pdf-table td {
  border-width: 0 1px 1px 0;
  border-style: solid;
  border-color: #d2d2dc;
}

.qa-tc-review-overlay .qa-tc-pdf-table thead th {
  border-top-width: 1px;
}

.qa-tc-review-overlay .qa-tc-pdf-table tr > *:first-child {
  border-left-width: 1px;
}

.qa-tc-review-overlay .qa-tc-pdf-table thead th {
  position: sticky;
  top: 0;
  z-index: 8;
  background: #344767;
  background-clip: padding-box;
}

.qa-tc-review-overlay .qa-tc-pdf-table .qa-tc-pdf-section--sc td {
  position: sticky;
  top: var(--qa-tc-pdf-th, 2.5rem);
  z-index: 7;
  background: #f5f5f5;
}

.qa-tc-review-overlay .qa-tc-pdf-table .qa-tc-pdf-section--sub td {
  position: sticky;
  top: calc(var(--qa-tc-pdf-th, 2.5rem) + var(--qa-tc-pdf-sc, 1.85rem));
  z-index: 6;
  background: #fff;
}

.qa-tc-review-overlay .qa-tc-pdf-col-id { width: 45px; }
.qa-tc-review-overlay .qa-tc-pdf-col-name { width: 150px; }
.qa-tc-review-overlay .qa-tc-pdf-col-type { width: 46px; }
.qa-tc-review-overlay .qa-tc-pdf-col-priority { width: 45px; }
.qa-tc-review-overlay .qa-tc-pdf-col-pre ,
.qa-tc-review-overlay .qa-tc-pdf-col-data { width: 110px; }
.qa-tc-review-overlay .qa-tc-pdf-col-module { width: 55px; }
.qa-tc-review-overlay .qa-tc-pdf-col-step,
.qa-tc-review-overlay .qa-tc-pdf-col-expected { width: 180px; }

.qa-tc-review-overlay .qa-tc-pdf-table th {
  text-align: center;
}

.qa-tc-review-overlay .qa-tc-pdf-table th:nth-child(3),
.qa-tc-review-overlay .qa-tc-pdf-table th:nth-child(4),
.qa-tc-review-overlay .qa-tc-pdf-table th:nth-child(5),
.qa-tc-review-overlay .qa-tc-pdf-table th:nth-child(6),
.qa-tc-review-overlay .qa-tc-pdf-table th:nth-child(7) {
  white-space: normal;
}

.qa-tc-review-overlay .qa-tc-pdf-table th:nth-child(3),
.qa-tc-review-overlay .qa-tc-pdf-table th:nth-child(4) {
  padding-left: 0.15rem;
  padding-right: 0.15rem;
  text-align: center;
  vertical-align: middle;
}

.qa-tc-review-overlay .qa-tc-pdf-table .qa-tc-review-type-val {
  min-width: 0;
}

.qa-tc-review-overlay .qa-tc-pdf-id {
  white-space: normal;
  text-align: center;
  padding-left: 0.2rem;
  padding-right: 0.2rem;
  overflow: hidden;
}

.qa-tc-review-overlay .qa-tc-pdf-id .status-pill {
  display: block;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  margin: 0.35rem 0 0;
  padding: 0.1rem 0.12rem;
  font-size: 0.52rem;
  font-weight: 700;
  line-height: 1.15;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Create / Edit / Detail modal layout — same as TestCaseTab (teleported — must be unscoped) */
.tc-create-modal {
  width: 1240px;
  max-width: 98vw;
  height: 90vh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tc-create-modal-header {
  flex-shrink: 0;
}

.tc-create-modal-body {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tc-create-modal-columns {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  align-items: stretch;
}

.tc-create-modal-left {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tc-create-modal-left-fixed {
  flex-shrink: 0;
  padding: 1rem 1rem 0.75rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

.tc-create-modal-left-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.75rem 1rem 1rem;
}

.tc-create-modal-right {
  width: 240px;
  flex-shrink: 0;
  border-left: 1px solid #e9ecef;
  overflow-y: auto;
  padding: 1rem;
  background: #fff;
}

.tc-create-modal-footer {
  flex-shrink: 0;
  background: #fff;
}

.qa-tc-req-overlay {
  z-index: 3100;
}

.qa-tc-req-modal {
  width: 720px;
  max-width: 96vw;
  height: 78vh;
  max-height: 780px;
  min-height: 560px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qa-tc-req-modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.qa-tc-req-paste {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
}

.qa-tc-req-paste .form-control {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.85rem;
  height: 38px;
}

.qa-tc-req-paste-add {
  flex: 0 0 88px;
  height: 38px;
  font-weight: 600;
}

.qa-tc-req-divider {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.qa-tc-req-divider::before,
.qa-tc-req-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e9ecef;
}

.qa-tc-req-issue-list {
  min-height: 280px;
  max-height: none;
  height: 280px;
}

.tc-detail-modal-footer {
  flex-shrink: 0;
  background: #fff;
}

.tc-step-module-col {
  width: 175px;
  flex-shrink: 0;
}

.tc-create-modal .bullet-textarea-wrap--resizable {
  align-self: stretch;
}

/* Detail modal layout — teleported, unscoped */
.tc-detail-modal {
  width: 1240px;
  max-width: 98vw;
  height: 90vh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tc-detail-modal-body {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 1rem 1rem 1.25rem;
}

.tc-detail-modal-panel {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tc-detail-modal .tc-create-modal-columns {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: stretch;
}

.tc-detail-modal .tc-create-modal-left {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tc-detail-modal .tc-create-modal-left-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.75rem 0 1rem;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.tc-detail-modal .tc-create-modal-right {
  width: 240px;
  flex-shrink: 0;
  min-height: 0;
  border-left: 1px solid #e9ecef;
  overflow-y: auto;
  padding: 0 0 0 1rem;
  background: #fff;
}

.tc-detail-modal .tc-detail-modal-left-fixed {
  flex-shrink: 0;
  padding-bottom: 0.75rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0;
}

.tc-detail-modal .tc-detail-history-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
  -webkit-overflow-scrolling: touch;
}

.tc-detail-modal .tc-detail-remark {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tc-detail-modal .tc-detail-remark-composer {
  flex-shrink: 0;
  margin-bottom: 0.85rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #e9ecef;
}

.tc-detail-modal .tc-detail-title {
  margin-bottom: 1rem;
}

.tc-detail-modal .tc-detail-steps-header,
.tc-detail-modal .tc-detail-steps-row {
  display: grid;
  grid-template-columns: 72px 120px minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.5rem;
  align-items: stretch;
}

.tc-detail-modal .tc-detail-steps-header {
  margin-bottom: 0.5rem;
}

.tc-detail-modal .tc-detail-steps-row + .tc-detail-steps-row {
  margin-top: 0.5rem;
}

.tc-detail-modal .tc-detail-steps-label {
  display: flex;
  align-items: flex-start;
  padding-top: 6px;
}

.tc-detail-modal .tc-detail-step-cell {
  display: flex;
  min-width: 0;
  min-height: 4.5rem;
}

.tc-detail-modal .tc-detail-step-box {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 100%;
  font-size: 0.75rem;
  background: #fff;
  color: #344767;
  white-space: pre-wrap;
  word-break: break-word;
}

.tc-detail-modal .tc-detail-step-box .bullet-text-plain,
.tc-detail-modal .tc-detail-step-box .bullet-text-content {
  color: #344767;
}

/* Detail view — dark labels (bold) and values */
.tc-detail-modal .tc-detail-field-label,
.tc-detail-modal .tc-detail-sidebar-title,
.tc-detail-modal .tc-detail-section-title {
  color: #344767;
  font-weight: 700;
}

.tc-detail-modal .tc-detail-field-label {
  font-size: 0.75rem;
  font-weight: 700 !important;
}

.tc-detail-modal .tc-detail-sidebar-title {
  font-size: 0.75rem;
}

.tc-detail-modal .tc-detail-section-title {
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
}

.tc-detail-modal .tc-detail-field-value,
.tc-detail-modal .tc-detail-hierarchy {
  color: #344767;
  font-weight: 400;
}

.tc-detail-modal .tc-create-modal-columns .font-weight-bold,
.tc-detail-modal .tc-create-modal-columns .tc-detail-section-title {
  color: #344767;
  font-weight: 700;
}

.tc-detail-modal .tc-create-modal-columns .text-secondary,
.tc-detail-modal .tc-create-modal-columns .tc-detail-hierarchy,
.tc-detail-modal .tc-create-modal-columns .bullet-text-plain,
.tc-detail-modal .tc-create-modal-columns .bullet-text-content {
  color: #344767;
  font-weight: 400;
}

.tc-detail-modal .tc-create-modal-columns .tc-detail-steps-header,
.tc-detail-modal .tc-create-modal-columns .tc-detail-steps-label {
  color: #344767;
  font-weight: 700;
}

.tc-detail-modal .tc-detail-title h6 {
  color: #344767;
}

/* Detail modal history — teleported, unscoped */
.tc-detail-modal .activity-step-diff-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 0.55rem;
  width: 100%;
}

.tc-detail-modal .activity-step-diff-grid > .activity-step-pane {
  flex: 1 1 0;
  min-width: 0;
  width: 50%;
  box-sizing: border-box;
}
</style>
